"use client"

import React, { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Tabs,
  Tab,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  useMediaQuery,
} from "@mui/material"
import { useTheme, alpha } from "@mui/material/styles"

import { School, Work, FilterList, CloudDownload, ZoomIn, ZoomOut, Refresh } from "@mui/icons-material"
import Layout from "../common/Layout"
import apiClient from "../../context/apiClient"
import { GoogleMap, Circle, InfoWindow, useLoadScript } from "@react-google-maps/api"

const containerStyle = {
  width: "100%",
  height: "100%",
}

const indiaCenter = { lat: 22.9734, lng: 78.6569 }

const stateCoordinates = {
  AndhraPradesh: { lat: 15.9129, lng: 79.74 },
  ArunachalPradesh: { lat: 28.218, lng: 94.7278 },
  Assam: { lat: 26.2006, lng: 92.9376 },
  Bihar: { lat: 25.0961, lng: 85.3131 },
  Chhattisgarh: { lat: 21.2787, lng: 81.8661 },
  Goa: { lat: 15.2993, lng: 74.124 },
  Gujarat: { lat: 22.2587, lng: 71.1924 },
  Haryana: { lat: 29.0588, lng: 76.0856 },
  HimachalPradesh: { lat: 31.1048, lng: 77.1734 },
  Jharkhand: { lat: 23.6102, lng: 85.2799 },
  Karnataka: { lat: 15.3173, lng: 75.7139 },
  Kerala: { lat: 10.8505, lng: 76.2711 },
  MadhyaPradesh: { lat: 22.9734, lng: 78.6569 },
  Maharashtra: { lat: 19.7515, lng: 75.7139 },
  Manipur: { lat: 24.6637, lng: 93.9063 },
  Meghalaya: { lat: 25.467, lng: 91.3662 },
  Mizoram: { lat: 23.1645, lng: 92.9376 },
  Nagaland: { lat: 26.1584, lng: 94.5624 },
  Odisha: { lat: 20.9517, lng: 85.0985 },
  Punjab: { lat: 31.1471, lng: 75.3412 },
  Rajasthan: { lat: 27.0238, lng: 74.2179 },
  Rajsthan: { lat: 27.0238, lng: 74.2179 },
  Sikkim: { lat: 27.533, lng: 88.5122 },
  TamilNadu: { lat: 11.1271, lng: 78.6569 },
  Telangana: { lat: 18.1124, lng: 79.0193 },
  Tripura: { lat: 23.9408, lng: 91.9882 },
  UttarPradesh: { lat: 26.8467, lng: 80.9462 },
  Uttarakhand: { lat: 30.0668, lng: 79.0193 },
  WestBengal: { lat: 22.9868, lng: 87.855 },
  AndamanAndNicobarIslands: { lat: 11.7401, lng: 92.6586 },
  Chandigarh: { lat: 30.7333, lng: 76.7794 },
  DadraAndNagarHaveliAndDamanAndDiu: { lat: 20.4283, lng: 72.8397 },
  Delhi: { lat: 28.7041, lng: 77.1025 },
  JammuAndKashmir: { lat: 33.7782, lng: 76.5762 },
  Ladakh: { lat: 34.1526, lng: 77.577 },
  Lakshadweep: { lat: 10.5667, lng: 72.6417 },
  Puducherry: { lat: 11.9416, lng: 79.8083 },
}

const districtCoordinates = {
  nashik: { lat: 19.9975, lng: 73.7898 },
  "Kolhapur-416101": { lat: 16.705, lng: 74.2433 },
  dhule: { lat: 20.9042, lng: 74.7749 },
  pune: { lat: 18.5204, lng: 73.8567 },
  "Solapur-413304": { lat: 17.6599, lng: 75.9064 },
  "Pune-411017": { lat: 18.5204, lng: 73.8567 },
  mumbai: { lat: 19.076, lng: 72.8777 },
  thane: { lat: 19.2183, lng: 72.9781 },
  nagpur: { lat: 21.1458, lng: 79.0882 },
  aurangabad: { lat: 19.8762, lng: 75.3433 },
}

function TalentHeatmap() {
  const [tabValue, setTabValue] = useState(0)
  const [roleFilter, setRoleFilter] = useState("all")
  const [certificationFilter, setCertificationFilter] = useState("all")
  const [zoomLevel, setZoomLevel] = useState(5)
  const [loading, setLoading] = useState(true)
  const [institutionData, setInstitutionData] = useState([])
  const [roleData, setRoleData] = useState([])
  const [certificationData, setCertificationData] = useState([])
  const [readinessData, setReadinessData] = useState(null)
  const [filterOptions, setFilterOptions] = useState({ roles: [], certifications: [] })
  const [heatmapData, setHeatmapData] = useState([])
  const [topStatesData, setTopStatesData] = useState([])
  const [activeState, setActiveState] = useState(null)
  const [districtData, setDistrictData] = useState([])
  const [activeDistrict, setActiveDistrict] = useState(null)
  const [showDistrictLevel, setShowDistrictLevel] = useState(false)

  // MUI hooks for responsiveness
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")) // Check for mobile screens (width < 600px)

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyCJFtqpKTMxYbh3E3bnzQeDANkknX-O-gQ",
    libraries: ["visualization"],
  })

  useEffect(() => {
    apiClient
      .get("/talent-heatmap-filter-options/")
      .then((response) => setFilterOptions(response.data))
      .catch((error) => console.error("Error fetching filter options:", error))
  }, [])

  useEffect(() => {
    setLoading(true)
    if (tabValue === 0) {
      const params = new URLSearchParams()
      if (roleFilter !== "all") params.append("role", roleFilter)
      if (certificationFilter !== "all") params.append("certification", certificationFilter)
      const queryString = params.toString()
      const url = `/talent-heatmap/${queryString ? `?${queryString}` : ""}`

      apiClient
        .get(url)
        .then((response) => {
          const states = response.data.state_distribution || []
          const districts = response.data.district_distribution || []
          const candidates = response.data.experienced_candidates || []

          const matchCandidateToState = (candidate, stateName) => {
            return candidate.current_state && candidate.current_state.toLowerCase() === stateName.toLowerCase()
          }

          const matchCandidateToDistrict = (candidate, districtName) => {
            return (
              candidate.current_district &&
              candidate.current_district.toLowerCase().includes(districtName.toLowerCase())
            )
          }

          const mergedStateData = states.map((stateObj) => {
            const stateCandidates = candidates.filter((candidate) => matchCandidateToState(candidate, stateObj.state))
            return {
              ...stateObj,
              candidates: stateCandidates,
            }
          })

          const mergedDistrictData = districts.map((districtObj) => {
            const districtCandidates = candidates.filter((candidate) =>
              matchCandidateToDistrict(candidate, districtObj.district),
            )
            return {
              ...districtObj,
              candidates: districtCandidates,
            }
          })

          setHeatmapData(mergedStateData)
          setDistrictData(mergedDistrictData)
          setTopStatesData(states.sort((a, b) => b.count - a.count))
          setLoading(false)
        })
        .catch((error) => {
          console.error("Error fetching state-wise heatmap data:", error)
          setHeatmapData([])
          setDistrictData([])
          setTopStatesData([])
          setLoading(false)
        })
    } else if (tabValue === 1) {
      apiClient
        .get("/talent-heatmap-institute-wise/")
        .then((response) => {
          setInstitutionData(response.data)
          setLoading(false)
        })
        .catch((error) => {
          console.error("Error fetching institution data:", error)
          setLoading(false)
        })
    } else if (tabValue === 2) {
      apiClient
        .get("/talent_heatmap/RoleAndCertifications/")
        .then((response) => {
          setRoleData(response.data.talent_distribution_by_role)
          setCertificationData(response.data.talent_distribution_by_certification)
          setReadinessData(response.data.international_readiness_index)
          setLoading(false)
        })
        .catch((error) => {
          console.error("Error fetching role and certification data:", error)
          setLoading(false)
        })
    }
  }, [tabValue, roleFilter, certificationFilter])

  useEffect(() => {
    if (zoomLevel >= 7) {
      setShowDistrictLevel(true)
    } else {
      setShowDistrictLevel(false)
      setActiveDistrict(null)
    }
  }, [zoomLevel])

  const handleTabChange = (e, newValue) => setTabValue(newValue)
  const handleRoleFilterChange = (e) => setRoleFilter(e.target.value)
  const handleCertificationFilterChange = (e) => setCertificationFilter(e.target.value)
  const handleZoomIn = () => setZoomLevel(Math.min(zoomLevel + 1, 12))
  const handleZoomOut = () => setZoomLevel(Math.max(zoomLevel - 1, 3))
  const handleResetZoom = () => {
    setZoomLevel(5)
    setShowDistrictLevel(false)
    setActiveDistrict(null)
  }

  const maxRoleCount = roleData.length > 0 ? Math.max(...roleData.map((r) => r.count)) : 1
  const maxCertCount = certificationData.length > 0 ? Math.max(...certificationData.map((c) => c.count)) : 1

  return (
    <Layout title="Talent Heatmap">
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Paper sx={{ p: 2, mb: 3, backgroundColor: theme.palette.mint?.main || theme.palette.background.paper, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, color: "primary.main" }}>
              Talent Availability Heatmap
            </Typography>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" startIcon={<FilterList />} size="small">
                Advanced Filters
              </Button>
              <Button variant="outlined" startIcon={<CloudDownload />} size="small">
                Export Data
              </Button>
            </Box>
          </Box>
        </Paper>
        <Paper 
          sx={{ 
            p: 2, 
            mb: 3, 
            backgroundColor: theme.palette.mint?.main || theme.palette.background.paper, 
            borderRadius: "20px",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons="auto"
            centered={!isMobile}
          >
            <Tab label="State-wise Distribution" />
            <Tab label="Institution-wise Distribution" />
            <Tab label="Role & Certification" />
          </Tabs>
        </Paper>

        {tabValue === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card sx={{ backgroundColor: theme.palette.mint?.main || theme.palette.background.paper, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>

                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: "15px",
                      flexWrap: "wrap",
                      width: "100%",
                      px: 2,
                      py: 1.5,
                      gap: 1,
                    }}
                  >
                    <Typography variant="h6" color="primary.main">

                      India AI Talent Heatmap {showDistrictLevel ? "(District View)" : "(State View)"}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="Zoom In">
                        <IconButton size="small" onClick={handleZoomIn}>
                          <ZoomIn />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Zoom Out">
                        <IconButton size="small" onClick={handleZoomOut}>
                          <ZoomOut />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset View">
                        <IconButton size="small" onClick={handleResetZoom}>
                          <Refresh />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      height: { xs: 350, sm: 450, md: 500 },
                      borderRadius: 1,
                      overflow: "hidden",
                      border: "1px solid #ddd",
                    }}
                  >
                    {loading ? (
                      <Skeleton variant="rectangular" width="100%" height="100%" />
                    ) : isLoaded ? (
                      <GoogleMap mapContainerStyle={containerStyle} center={indiaCenter} zoom={zoomLevel}>
                        {!showDistrictLevel &&
                          heatmapData.map((stateData, index) => {
                            const coords = stateCoordinates[stateData.state.replace(/\s/g, "")]
                            if (!coords) return null
                            return (
                              <Circle
                                key={`state-${index}`}
                                center={coords}
                                radius={Math.sqrt(stateData.count) * 35000}
                                onMouseOver={() => setActiveState(stateData)}
                                onMouseOut={() => setActiveState(null)}
                                options={{
                                  strokeColor: theme.palette.primary.main,
                                  strokeOpacity: 0.8,
                                  strokeWeight: 2,
                                  fillColor: "#D4AF37",
                                  fillOpacity: 0.4,
                                  clickable: true,
                                }}
                              />
                            )
                          })}

                        {showDistrictLevel &&
                          districtData.map((districtData, index) => {
                            const coords = districtCoordinates[districtData.district.toLowerCase()]
                            if (!coords) return null
                            return (
                              <Circle
                                key={`district-${index}`}
                                center={coords}
                                radius={Math.sqrt(districtData.count) * 15000}
                                onMouseOver={() => setActiveDistrict(districtData)}
                                onMouseOut={() => setActiveDistrict(null)}
                                options={{
                                  strokeColor: "#FF6B35",
                                  strokeOpacity: 0.9,
                                  strokeWeight: 2,
                                  fillColor: "#FF6B35",
                                  fillOpacity: 0.5,
                                  clickable: true,
                                }}
                              />
                            )
                          })}

                        {activeState &&
                          !showDistrictLevel &&
                          stateCoordinates[activeState.state.replace(/\s/g, "")] && (
                            <InfoWindow
                              position={stateCoordinates[activeState.state.replace(/\s/g, "")]}
                              onCloseClick={() => setActiveState(null)}
                              options={{
                                pixelOffset: new window.google.maps.Size(0, -30),
                                disableAutoPan: true,
                                backgroundColor: theme.palette.mint.main,
                              }}
                            >
                              <Paper
                                elevation={0}
                                sx={{
                                  width: 280,
                                  p: 0,
                                  borderRadius: "16px",
                                  overflow: "hidden",
                                  border: "1px solid rgba(255, 255, 255, 0.2)",
                                  background:
                                    "linear-gradient(135deg, #1A1A1A 0%, #0D0D0D 100%)",
                                  backdropFilter: "blur(12px) saturate(180%)",
                                  boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
                                }}
                              >
                                <Box sx={{ p: "12px 16px", background: "rgba(0,0,0,0.15)" }}>
                                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#FFFFFF" }}>
                                      {activeState.state}
                                    </Typography>
                                    <Chip
                                      icon={<Work sx={{ color: "#E0E0E0 !important" }} />}
                                      label={`${activeState.count}`}
                                      size="small"
                                      sx={{
                                        fontWeight: 600,
                                        color: "#E0E0E0",
                                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                                      }}
                                    />
                                  </Box>
                                </Box>

                                <List
                                  dense
                                  sx={{
                                    maxHeight: 150,
                                    overflow: "auto",
                                    p: "8px 8px 8px 16px",
                                    "&::-webkit-scrollbar": { width: "4px" },
                                    "&::-webkit-scrollbar-track": { background: "transparent" },
                                    "&::-webkit-scrollbar-thumb": {
                                      background: "#888",
                                      borderRadius: "2px",
                                    },
                                    "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
                                  }}
                                >
                                  {activeState.candidates && activeState.candidates.length > 0 ? (
                                    activeState.candidates.map((candidate) => (
                                      <ListItem
                                        key={candidate.id}
                                        disablePadding
                                        secondaryAction={
                                          <IconButton edge="end" size="small" sx={{ color: "#b0b0b0" }}>
                                            <School />
                                          </IconButton>
                                        }
                                        sx={{
                                          py: 0.75,
                                          borderRadius: "8px",
                                          transition: "background-color 0.2s ease-in-out",
                                          "&:hover": {
                                            background: "rgba(255, 255, 255, 0.08)",
                                          },
                                        }}
                                      >
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                          <Avatar src={candidate.profile_photo_url} sx={{ width: 30, height: 30 }}>
                                            {candidate.name.charAt(0)}
                                          </Avatar>
                                        </ListItemIcon>
                                        <ListItemText
                                          primary={
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#FFFFFF" }}>
                                              {candidate.name}
                                            </Typography>
                                          }
                                          secondary={
                                            <Typography variant="caption" sx={{ color: "#B0B0B0" }}>
                                              {candidate.current_city}, {candidate.current_district}
                                            </Typography>
                                          }
                                        />
                                      </ListItem>
                                    ))
                                  ) : (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        height: 80,
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                        No candidates found.
                                      </Typography>
                                    </Box>
                                  )}
                                </List>
                              </Paper>
                            </InfoWindow>
                          )}

                        {activeDistrict &&
                          showDistrictLevel &&
                          districtCoordinates[activeDistrict.district.toLowerCase()] && (
                            <InfoWindow
                              position={districtCoordinates[activeDistrict.district.toLowerCase()]}
                              onCloseClick={() => setActiveDistrict(null)}
                              options={{
                                pixelOffset: new window.google.maps.Size(0, -30),
                                disableAutoPan: true,
                                backgroundColor: theme.palette.mint.main,
                              }}
                            >
                              <Paper
                                elevation={0}
                                sx={{
                                  width: 300,
                                  p: 0,
                                  borderRadius: "16px",
                                  overflow: "hidden",
                                  border: "1px solid rgba(255, 255, 255, 0.2)",
                                  background:
                                    "linear-gradient(135deg, rgba(255, 107, 53, 0.9), rgba(255, 140, 105, 0.9))",
                                  backdropFilter: "blur(12px) saturate(180%)",
                                  boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
                                }}
                              >
                                <Box sx={{ p: "12px 16px", background: "rgba(0,0,0,0.15)" }}>
                                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: "mint.contrastText" }}>
                                      {activeDistrict.district}
                                    </Typography>
                                    <Chip
                                      icon={<Work sx={{ color: "inherit !important" }} />}
                                      label={`${activeDistrict.count}`}
                                      size="small"
                                      sx={{
                                        fontWeight: 600,
                                        color: "mint.contrastText",
                                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                                      }}
                                    />
                                  </Box>
                                </Box>

                                <List
                                  dense
                                  sx={{
                                    maxHeight: 180,
                                    overflow: "auto",
                                    p: "8px 8px 8px 16px",
                                    "&::-webkit-scrollbar": { width: "4px" },
                                    "&::-webkit-scrollbar-track": { background: "transparent" },
                                    "&::-webkit-scrollbar-thumb": {
                                      background: "#888",
                                      borderRadius: "2px",
                                    },
                                    "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
                                  }}
                                >
                                  {activeDistrict.candidates && activeDistrict.candidates.length > 0 ? (
                                    activeDistrict.candidates.map((candidate) => (
                                      <ListItem
                                        key={candidate.id}
                                        disablePadding
                                        sx={{
                                          py: 0.75,
                                          borderRadius: "8px",
                                          transition: "background-color 0.2s ease-in-out",
                                          "&:hover": {
                                            background: "rgba(255, 255, 255, 0.08)",
                                          },
                                        }}
                                      >
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                          <Avatar src={candidate.profile_photo_url} sx={{ width: 32, height: 32 }}>
                                            {candidate.name.charAt(0)}
                                          </Avatar>
                                        </ListItemIcon>
                                        <ListItemText
                                          primary={
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: "mint.contrastText" }}>
                                              {candidate.name}
                                            </Typography>
                                          }
                                          secondary={
                                            <Box>
                                              <Typography variant="caption" sx={{ color: "mint.contrastText", opacity: 0.8, display: "block" }}>
                                                {candidate.current_company || "Seeking Opportunities"}
                                              </Typography>
                                              <Typography variant="caption" sx={{ color: "mint.contrastText", opacity: 0.6, display: "block" }}>
                                                {(() => {
                                                  try {
                                                    const interests = JSON.parse(
                                                      candidate.domain_interest.replace(/'/g, '"'),
                                                    )
                                                    return Array.isArray(interests)
                                                      ? interests.slice(0, 2).join(", ")
                                                      : "Specialist"
                                                  } catch (e) {
                                                    return "Specialist"
                                                  }
                                                })()}
                                              </Typography>
                                            </Box>
                                          }
                                        />
                                      </ListItem>
                                    ))
                                  ) : (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        height: 80,
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ color: "mint.contrastText", opacity: 0.8 }}>
                                        No candidates found.
                                      </Typography>
                                    </Box>
                                  )}
                                </List>
                              </Paper>
                            </InfoWindow>
                          )}
                      </GoogleMap>
                    ) : (
                      <Typography sx={{ p: 2 }}>Loading Map...</Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ mb: 2, backgroundColor: theme.palette.mint?.main || theme.palette.background.paper, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>

                <CardContent>
                  <Typography variant="h6" color="primary.main" gutterBottom>
                    Filters
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="role-filter-label">Filter by Role</InputLabel>
                    <Select
                      labelId="role-filter-label"
                      value={roleFilter}
                      onChange={handleRoleFilterChange}
                      size="small"
                      label="Filter by Role"
                    >
                      <MenuItem value="all">All Roles</MenuItem>
                      {filterOptions.roles.map((role, i) => (
                        <MenuItem key={i} value={role}>
                          {role}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel id="cert-filter-label">Filter by Certification</InputLabel>
                    <Select
                      labelId="cert-filter-label"
                      value={certificationFilter}
                      onChange={handleCertificationFilterChange}
                      size="small"
                      label="Filter by Certification"
                    >
                      <MenuItem value="all">All Certifications</MenuItem>
                      {filterOptions.certifications.map((cert, i) => (
                        <MenuItem key={i} value={cert}>
                          {cert}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>

              <Card 
                sx={{ 
                  backgroundColor: theme.palette.mint?.main || theme.palette.background.paper,
                  borderRadius: "20px",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  boxShadow: theme.shadows[2]
                }}
              >
                <CardContent>
                  <Typography variant="h6" color="primary.main" gutterBottom>
                    {showDistrictLevel ? "Top Districts by Talent" : "Top States by Talent"}
                  </Typography>
                  {loading ? (
                    <List dense>
                      {Array.from(new Array(5)).map((_, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <Skeleton variant="circular" width={16} height={16} />
                          </ListItemIcon>
                          <ListItemText
                            primary={<Skeleton variant="text" width="60%" />}
                            secondary={<Skeleton variant="text" width="40%" />}
                          />
                          <Skeleton variant="rectangular" width={40} height={20} />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <List dense>
                      {(showDistrictLevel ? districtData : topStatesData).slice(0, 10).map((item, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                bgcolor: showDistrictLevel ? "secondary.main" : "primary.main",
                                borderRadius: "50%",
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={showDistrictLevel ? item.district : item.state}
                            secondary={`${item.count} professionals`}
                          />
                          <Chip label={`#${index + 1}`} size="small" color="primary" variant="outlined" />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {tabValue === 1 && (
          <Card
            sx={{
              backgroundColor: theme.palette.mint?.main || theme.palette.background.paper,
              borderRadius: "20px",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              maxHeight: "75vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent>
              <Typography variant="h6" color="primary.main" gutterBottom>
                Top Institutions by AI Talent Output
              </Typography>
            </CardContent>

            {isMobile ? (
              // Mobile-friendly List View
              <List sx={{ overflowY: "auto" }}>
                {loading
                  ? Array.from(new Array(10)).map((_, i) => (
                      <ListItem key={i}>
                        <ListItemText
                          primary={<Skeleton variant="text" width="70%" />}
                          secondary={<Skeleton variant="text" width="40%" />}
                        />
                      </ListItem>
                    ))
                  : institutionData.map((inst, i) => (
                      <ListItem
                        key={i}
                        secondaryAction={<Chip label={`${inst.graduates} graduates`} color="secondary" size="small" />}
                      >
                        <ListItemIcon>
                          <Typography sx={{ mr: 1 }}>#{i + 1}</Typography>
                          <Avatar sx={{ bgcolor: "primary.main", width: 28, height: 28 }}>
                            <School fontSize="small" />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText primary={inst.institution} />
                      </ListItem>
                    ))}
              </List>
            ) : (
              // Desktop Table View
              <TableContainer component={Paper} sx={{ flexGrow: 1 }}>
                <Table stickyHeader size="small" aria-label="Top institutions table">
                  <TableHead>
                    <TableRow>
                      <TableCell width={80}>Rank</TableCell>
                      <TableCell>Institution</TableCell>
                      <TableCell align="right" width={180}>
                        Graduates
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading
                      ? Array.from(new Array(15)).map((_, i) => (
                          <TableRow hover key={i}>
                            <TableCell>
                              <Skeleton variant="text" />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Skeleton variant="circular" width={28} height={28} />
                                <Skeleton variant="text" width="80%" />
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton variant="rectangular" width={100} height={20} />
                            </TableCell>
                          </TableRow>
                        ))
                      : institutionData.map((inst, i) => (
                          <TableRow hover key={i}>
                            <TableCell>#{i + 1}</TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Avatar sx={{ bgcolor: "primary.main", width: 28, height: 28 }}>
                                  <School fontSize="small" />
                                </Avatar>
                                <Typography variant="body2">{inst.institution}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={`${inst.graduates} graduates`} color="secondary" size="small" />
                            </TableCell>
                          </TableRow>
                        ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        )}

        {tabValue === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Card 
                sx={{ 
                  backgroundColor: theme.palette.mint?.main || theme.palette.background.paper, 
                  height: '100%',
                  borderRadius: "20px",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <CardContent>
                  <Typography variant="h6" color="primary.main" fontWeight="bold" gutterBottom>
                    Talent Distribution by Role
                  </Typography>
                  {loading ? (
                    <List>
                      {Array.from(new Array(5)).map((_, i) => (
                        <ListItem key={i}>
                          <ListItemIcon>
                            <Skeleton variant="circular" width={24} height={24} />
                          </ListItemIcon>
                          <ListItemText
                            primary={<Skeleton variant="text" width="70%" />}
                            secondary={<Skeleton variant="text" width="50%" />}
                          />
                          <Skeleton variant="rectangular" width={100} height={8} />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <List>
                      {roleData.map((role, i) => (
                        <React.Fragment key={i}>
                          <ListItem>
                            <ListItemIcon>
                              <Work sx={{ color: "primary.main" }} />
                            </ListItemIcon>
                            <ListItemText primary={role.role} secondary={`${role.count} professionals`} />
                            <Box
                              sx={{ width: 100, height: 8, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 1, overflow: "hidden" }}
                            >
                              <Box
                                sx={{
                                  width: `${(role.count / maxRoleCount) * 100}%`,
                                  height: "100%",
                                  bgcolor: "primary.main",
                                }}
                              />
                            </Box>
                          </ListItem>
                          {i < roleData.length - 1 && <Divider variant="inset" component="li" />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  backgroundColor: theme.palette.mint?.main || theme.palette.background.paper, 
                  height: '100%',
                  borderRadius: "20px",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <CardContent>
                  <Typography variant="h6" color="primary.main" fontWeight="bold" gutterBottom>
                    Talent Distribution by Certification
                  </Typography>
                  {loading ? (
                    <List>
                      {Array.from(new Array(5)).map((_, i) => (
                        <ListItem key={i}>
                          <ListItemIcon>
                            <Skeleton variant="circular" width={24} height={24} />
                          </ListItemIcon>
                          <ListItemText
                            primary={<Skeleton variant="text" width="70%" />}
                            secondary={<Skeleton variant="text" width="50%" />}
                          />
                          <Skeleton variant="rectangular" width={100} height={8} />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <List>
                      {certificationData.map((cert, i) => (
                        <React.Fragment key={i}>
                          <ListItem>
                            <ListItemIcon>
                              <School sx={{ color: "primary.main" }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={cert.certification}
                              secondary={`${cert.count} certified professionals`}
                            />
                            <Box
                              sx={{ width: 100, height: 8, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 1, overflow: "hidden" }}
                            >
                              <Box
                                sx={{
                                  width: `${(cert.count / maxCertCount) * 100}%`,
                                  height: "100%",
                                  bgcolor: "secondary.main",
                                }}
                              />
                            </Box>
                          </ListItem>
                          {i < certificationData.length - 1 && <Divider variant="inset" component="li" />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card 
                sx={{ 
                  backgroundColor: theme.palette.mint?.main || theme.palette.background.paper, 
                  height: '100%',
                  borderRadius: "20px",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <CardContent>
                  <Typography variant="h6" color="primary.main" fontWeight="bold" gutterBottom>
                    International Readiness Index
                  </Typography>
                  {loading ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Skeleton variant="rectangular" height={118} />
                      </Grid>
                      <Grid item xs={12}>
                        <Skeleton variant="rectangular" height={118} />
                      </Grid>
                      <Grid item xs={12}>
                        <Skeleton variant="rectangular" height={118} />
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2, textAlign: "center" }}>
                          <Typography variant="h3" color="success.main">
                            {readinessData?.english_proficiency_percentage ?? "0"}%
                          </Typography>
                          <Typography>English Proficiency</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2, textAlign: "center" }}>
                          <Typography variant="h3" color="primary.main">
                            {readinessData?.passport_ready_percentage ?? "0"}%
                          </Typography>
                          <Typography>Passport Ready</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2, textAlign: "center" }}>
                          <Typography variant="h3" color="warning.main">
                            {readinessData?.relocation_willing_percentage ?? "0"}%
                          </Typography>
                          <Typography>Relocation Willing</Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>
    </Layout>
  )
}

export default TalentHeatmap

// "use client"

// import React, { useState, useEffect } from "react"
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Paper,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Button,
//   Tabs,
//   Tab,
//   Chip,
//   List,
//   ListItem,
//   ListItemText,
//   ListItemIcon,
//   Divider,
//   Avatar,
//   IconButton,
//   Tooltip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Skeleton,
//   useMediaQuery,
//   useTheme,
// } from "@mui/material"
// import { School, Work, FilterList, CloudDownload, ZoomIn, ZoomOut, Refresh } from "@mui/icons-material"
// import Layout from "../common/Layout"
// import apiClient from "../../context/apiClient"
// import { GoogleMap, Circle, InfoWindow, useLoadScript } from "@react-google-maps/api"

// // Responsive container style for the map
// const containerStyle = {
//   width: "100%",
//   height: "100%", // Make height 100% to fill its parent container
// }

// const indiaCenter = { lat: 22.9734, lng: 78.6569 }

// const stateCoordinates = {
//   AndhraPradesh: { lat: 15.9129, lng: 79.74 },
//   ArunachalPradesh: { lat: 28.218, lng: 94.7278 },
//   Assam: { lat: 26.2006, lng: 92.9376 },
//   Bihar: { lat: 25.0961, lng: 85.3131 },
//   Chhattisgarh: { lat: 21.2787, lng: 81.8661 },
//   Goa: { lat: 15.2993, lng: 74.124 },
//   Gujarat: { lat: 22.2587, lng: 71.1924 },
//   Haryana: { lat: 29.0588, lng: 76.0856 },
//   HimachalPradesh: { lat: 31.1048, lng: 77.1734 },
//   Jharkhand: { lat: 23.6102, lng: 85.2799 },
//   Karnataka: { lat: 15.3173, lng: 75.7139 },
//   Kerala: { lat: 10.8505, lng: 76.2711 },
//   MadhyaPradesh: { lat: 22.9734, lng: 78.6569 },
//   Maharashtra: { lat: 19.7515, lng: 75.7139 },
//   Manipur: { lat: 24.6637, lng: 93.9063 },
//   Meghalaya: { lat: 25.467, lng: 91.3662 },
//   Mizoram: { lat: 23.1645, lng: 92.9376 },
//   Nagaland: { lat: 26.1584, lng: 94.5624 },
//   Odisha: { lat: 20.9517, lng: 85.0985 },
//   Punjab: { lat: 31.1471, lng: 75.3412 },
//   Rajasthan: { lat: 27.0238, lng: 74.2179 },
//   Rajsthan: { lat: 27.0238, lng: 74.2179 },
//   Sikkim: { lat: 27.533, lng: 88.5122 },
//   TamilNadu: { lat: 11.1271, lng: 78.6569 },
//   Telangana: { lat: 18.1124, lng: 79.0193 },
//   Tripura: { lat: 23.9408, lng: 91.9882 },
//   UttarPradesh: { lat: 26.8467, lng: 80.9462 },
//   Uttarakhand: { lat: 30.0668, lng: 79.0193 },
//   WestBengal: { lat: 22.9868, lng: 87.855 },
//   AndamanAndNicobarIslands: { lat: 11.7401, lng: 92.6586 },
//   Chandigarh: { lat: 30.7333, lng: 76.7794 },
//   DadraAndNagarHaveliAndDamanAndDiu: { lat: 20.4283, lng: 72.8397 },
//   Delhi: { lat: 28.7041, lng: 77.1025 },
//   JammuAndKashmir: { lat: 33.7782, lng: 76.5762 },
//   Ladakh: { lat: 34.1526, lng: 77.577 },
//   Lakshadweep: { lat: 10.5667, lng: 72.6417 },
//   Puducherry: { lat: 11.9416, lng: 79.8083 },
// }

// function TalentHeatmap() {
//   const [tabValue, setTabValue] = useState(0)
//   const [roleFilter, setRoleFilter] = useState("all")
//   const [certificationFilter, setCertificationFilter] = useState("all")
//   const [zoomLevel, setZoomLevel] = useState(5)
//   const [loading, setLoading] = useState(true)
//   const [institutionData, setInstitutionData] = useState([])
//   const [roleData, setRoleData] = useState([])
//   const [certificationData, setCertificationData] = useState([])
//   const [readinessData, setReadinessData] = useState(null)
//   const [filterOptions, setFilterOptions] = useState({ roles: [], certifications: [] })
//   const [heatmapData, setHeatmapData] = useState([])
//   const [topStatesData, setTopStatesData] = useState([])
//   const [activeState, setActiveState] = useState(null)

//   // MUI hooks for responsiveness
//   const theme = useTheme()
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm")) // Check for mobile screens (width < 600px)

//   const { isLoaded } = useLoadScript({
// googleMapsApiKey: "AIzaSyCJFtqpKTMxYbh3E3bnzQeDANkknX-O-gQ",    libraries: ["visualization"],
//   })

//   useEffect(() => {
//     apiClient
//       .get("/talent-heatmap-filter-options/")
//       .then((response) => setFilterOptions(response.data))
//       .catch((error) => console.error("Error fetching filter options:", error))
//   }, [])

//   useEffect(() => {
//     setLoading(true)
//     if (tabValue === 0) {
//       const params = new URLSearchParams()
//       if (roleFilter !== "all") params.append("role", roleFilter)
//       if (certificationFilter !== "all") params.append("certification", certificationFilter)
//       const queryString = params.toString()
//       const url = `/talent-heatmap/${queryString ? `?${queryString}` : ""}`

//       apiClient
//         .get(url)
//         .then((response) => {
//           const states = response.data.state_distribution || []
//           const candidates = response.data.experienced_candidates || []

//           const matchCandidateToState = (candidate, stateName) => {
//             if (!candidate.work_preferences) return false
//             const locations = candidate.work_preferences.map((p) => p.location.toLowerCase())
//             const lowerStateName = stateName.toLowerCase()

//             if (
//               lowerStateName === "maharashtra" &&
//               locations.some((loc) => ["mumbai", "pune", "nashik", "kolhapur", "dhule", "solapur"].some((city) => loc.includes(city)))
//             ) {
//               return true
//             }
//             if (lowerStateName === "rajsthan" && locations.some((loc) => loc.includes("jaipur"))) {
//               return true
//             }
//             if (lowerStateName === "bihar" && locations.some((loc) => loc.includes("patna"))) {
//               return true
//             }
//             return false
//           }

//           const mergedData = states.map((stateObj) => {
//             const stateCandidates = candidates.filter((candidate) => matchCandidateToState(candidate, stateObj.state))
//             return {
//               ...stateObj,
//               candidates: stateCandidates,
//             }
//           })

//           setHeatmapData(mergedData)
//           setTopStatesData(states.sort((a, b) => b.count - a.count))
//           setLoading(false)
//         })
//         .catch((error) => {
//           console.error("Error fetching state-wise heatmap data:", error)
//           setHeatmapData([])
//           setTopStatesData([])
//           setLoading(false)
//         })
//     } else if (tabValue === 1) {
//       apiClient
//         .get("/talent-heatmap-institute-wise/")
//         .then((response) => {
//           setInstitutionData(response.data)
//           setLoading(false)
//         })
//         .catch((error) => {
//           console.error("Error fetching institution data:", error)
//           setLoading(false)
//         })
//     } else if (tabValue === 2) {
//       apiClient
//         .get("/talent_heatmap/RoleAndCertifications/")
//         .then((response) => {
//           setRoleData(response.data.talent_distribution_by_role)
//           setCertificationData(response.data.talent_distribution_by_certification)
//           setReadinessData(response.data.international_readiness_index)
//           setLoading(false)
//         })
//         .catch((error) => {
//           console.error("Error fetching role and certification data:", error)
//           setLoading(false)
//         })
//     }
//   }, [tabValue, roleFilter, certificationFilter])

//   const handleTabChange = (e, newValue) => setTabValue(newValue)
//   const handleRoleFilterChange = (e) => setRoleFilter(e.target.value)
//   const handleCertificationFilterChange = (e) => setCertificationFilter(e.target.value)
//   const handleZoomIn = () => setZoomLevel(Math.min(zoomLevel + 1, 12))
//   const handleZoomOut = () => setZoomLevel(Math.max(zoomLevel - 1, 3))
//   const handleResetZoom = () => setZoomLevel(5)

//   const maxRoleCount = roleData.length > 0 ? Math.max(...roleData.map((r) => r.count)) : 1
//   const maxCertCount = certificationData.length > 0 ? Math.max(...certificationData.map((c) => c.count)) : 1

//   return (
//     <Layout>
//       <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
//         <Paper sx={{ p: 2, mb: 3, backgroundColor: "background.paper", border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
//           <Box
//             sx={{
//               display: "flex",
//               justifyContent: "space-between",
//               alignItems: { sm: "center" },
//               flexDirection: { xs: "column", sm: "row" },
//               gap: 2,
//             }}
//           >
//             <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, color: "#5A36CC" }}>
//               Talent Availability Heatmap
//             </Typography>
//             <Box sx={{ display: "flex", gap: 1 }}>
//               <Button variant="outlined" startIcon={<FilterList />} size="small">
//                 Advanced Filters
//               </Button>
//               <Button variant="outlined" startIcon={<CloudDownload />} size="small">
//                 Export Data
//               </Button>
//             </Box>
//           </Box>
//         </Paper>
//         <Paper sx={{ mb: 3, backgroundColor: "#F1FFFA" }}>
//           <Tabs
//             value={tabValue}
//             onChange={handleTabChange}
//             indicatorColor="primary"
//             textColor="primary"
//             variant={isMobile ? "scrollable" : "standard"}
//             scrollButtons="auto"
//             centered={!isMobile}
//           >
//             <Tab label="State-wise Distribution" />
//             <Tab label="Institution-wise Distribution" />
//             <Tab label="Role & Certification" />
//           </Tabs>
//         </Paper>

//         {tabValue === 0 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12} md={8}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Box
//                     sx={{
//                       display: "flex",
//                       justifyContent: "space-between",
//                       alignItems: "center",
//                       mb: 2,
//                       backgroundColor: "#F1FFFA",
//                       borderRadius: "10px",
//                       flexWrap: "wrap",
//                       width: "870px",
//                       gap: 1,
//                     }}
//                   >
//                     <Typography variant="h6" color="#4F46BA">
//                       India AI Talent Heatmap
//                     </Typography>
//                     <Box sx={{ display: "flex", gap: 1 }}>
//                       <Tooltip title="Zoom In">
//                         <IconButton size="small" onClick={handleZoomIn}>
//                           <ZoomIn />
//                         </IconButton>
//                       </Tooltip>
//                       <Tooltip title="Zoom Out">
//                         <IconButton size="small" onClick={handleZoomOut}>
//                           <ZoomOut />
//                         </IconButton>
//                       </Tooltip>
//                       <Tooltip title="Reset View">
//                         <IconButton size="small" onClick={handleResetZoom}>
//                           <Refresh />
//                         </IconButton>
//                       </Tooltip>
//                     </Box>
//                   </Box>

//                   <Box
//                     sx={{
//                       height: { xs: 350, sm: 450, md: 500 },
//                       borderRadius: 1,
//                       overflow: "hidden",
//                       border: "1px solid #ddd",
//                     }}
//                   >
//                     {loading ? (
//                       <Skeleton variant="rectangular" width="100%" height="100%" />
//                     ) : isLoaded ? (
//                       <GoogleMap mapContainerStyle={containerStyle} center={indiaCenter} zoom={zoomLevel}>
//                         {heatmapData.map((stateData, index) => {
//                           const coords = stateCoordinates[stateData.state.replace(/\s/g, "")]
//                           if (!coords) return null
//                           return (
//                             <Circle
//                               key={index}
//                               center={coords}
//                               radius={Math.sqrt(stateData.count) * 35000}
//                               onMouseOver={() => setActiveState(stateData)}
//                               onMouseOut={() => setActiveState(null)}
//                               options={{
//                                 strokeColor: "#4F46BA",
//                                 strokeOpacity: 0.8,
//                                 strokeWeight: 2,
//                                 fillColor: "#cc3636ff",
//                                 fillOpacity: 0.4,
//                                 clickable: true,
//                               }}
//                             />
//                           )
//                         })}
//                         {activeState && stateCoordinates[activeState.state.replace(/\s/g, "")] && (
//                           <InfoWindow
//                             position={stateCoordinates[activeState.state.replace(/\s/g, "")]}
//                             onCloseClick={() => setActiveState(null)}
//                             options={{
//                               pixelOffset: new window.google.maps.Size(0, -30),
//                               disableAutoPan: true,
//                               backgroundColor: "#F1FFFA",
//                             }}
//                           >
//                             <Paper
//                               elevation={0}
//                               sx={{
//                                 width: 280,
//                                 p: 0,
//                                 borderRadius: "16px",
//                                 overflow: "hidden",
//                                 border: "1px solid rgba(255, 255, 255, 0.2)",
//                                 background: "linear-gradient(135deg, rgba(93, 109, 248, 0.9), rgba(110, 115, 170, 0.9))",
//                                 backdropFilter: "blur(12px) saturate(180%)",
//                                 boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
//                               }}
//                             >
//                               <Box sx={{ p: "12px 16px", background: "rgba(0,0,0,0.15)" }}>
//                                 <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
//                                   <Typography variant="h6" sx={{ fontWeight: 700, color: "#FFFFFF" }}>
//                                     {activeState.state}
//                                   </Typography>
//                                   <Chip
//                                     icon={<Work sx={{ color: "#E0E0E0 !important" }} />}
//                                     label={`${activeState.count}`}
//                                     size="small"
//                                     sx={{
//                                       fontWeight: 600,
//                                       color: "#E0E0E0",
//                                       backgroundColor: "rgba(255, 255, 255, 0.1)",
//                                     }}
//                                   />
//                                 </Box>
//                               </Box>

//                               <List
//                                 dense
//                                 sx={{
//                                   maxHeight: 150,
//                                   overflow: "auto",
//                                   p: "8px 8px 8px 16px",
//                                   "&::-webkit-scrollbar": { width: "4px" },
//                                   "&::-webkit-scrollbar-track": { background: "transparent" },
//                                   "&::-webkit-scrollbar-thumb": {
//                                     background: "#888",
//                                     borderRadius: "2px",
//                                   },
//                                   "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
//                                 }}
//                               >
//                                 {activeState.candidates && activeState.candidates.length > 0 ? (
//                                   activeState.candidates.map((candidate) => (
//                                     <ListItem
//                                       key={candidate.id}
//                                       disablePadding
//                                       secondaryAction={
//                                         <IconButton edge="end" size="small" sx={{ color: "#b0b0b0" }}>
//                                           <School />
//                                         </IconButton>
//                                       }
//                                       sx={{
//                                         py: 0.75,
//                                         borderRadius: "8px",
//                                         transition: "background-color 0.2s ease-in-out",
//                                         "&:hover": {
//                                           background: "rgba(255, 255, 255, 0.08)",
//                                         },
//                                       }}
//                                     >
//                                       <ListItemIcon sx={{ minWidth: 40 }}>
//                                         <Avatar src={candidate.profile_photo_url} sx={{ width: 30, height: 30 }}>
//                                           {candidate.name.charAt(0)}
//                                         </Avatar>
//                                       </ListItemIcon>
//                                       <ListItemText
//                                         primary={
//                                           <Typography variant="body2" sx={{ fontWeight: 600, color: "#FFFFFF" }}>
//                                             {candidate.name}
//                                           </Typography>
//                                         }
//                                         secondary={
//                                           <Typography variant="caption" sx={{ color: "#B0B0B0" }}>
//                                             {(() => {
//                                               try {
//                                                 const interests = JSON.parse(candidate.domain_interest.replace(/'/g, '"'))
//                                                 return Array.isArray(interests) ? interests[0] : "Specialist"
//                                               } catch (e) {
//                                                 return "Specialist"
//                                               }
//                                             })()}
//                                           </Typography>
//                                         }
//                                       />
//                                     </ListItem>
//                                   ))
//                                 ) : (
//                                   <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 80 }}>
//                                     <Typography variant="body2" sx={{ color: "#B0B0B0" }}>
//                                       No candidates found.
//                                     </Typography>
//                                   </Box>
//                                 )}
//                               </List>
//                             </Paper>
//                           </InfoWindow>
//                         )}
//                       </GoogleMap>
//                     ) : (
//                       <Typography sx={{ p: 2 }}>Loading Map...</Typography>
//                     )}
//                   </Box>
//                 </CardContent>
//               </Card>
//             </Grid>
//             <Grid item xs={12} md={4}>
//               <Card sx={{ mb: 2, backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" color="#4F46BA" gutterBottom>
//                     Filters
//                   </Typography>
//                   <FormControl fullWidth sx={{ mb: 2 }}>
//                     <InputLabel id="role-filter-label">Filter by Role</InputLabel>
//                     <Select
//                       labelId="role-filter-label"
//                       value={roleFilter}
//                       onChange={handleRoleFilterChange}
//                       size="small"
//                       label="Filter by Role" // <-- FIX APPLIED HERE
//                     >
//                       <MenuItem value="all">All Roles</MenuItem>
//                       {filterOptions.roles.map((role, i) => (
//                         <MenuItem key={i} value={role}>
//                           {role}
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                   <FormControl fullWidth>
//                     <InputLabel id="cert-filter-label">Filter by Certification</InputLabel>
//                     <Select
//                       labelId="cert-filter-label"
//                       value={certificationFilter}
//                       onChange={handleCertificationFilterChange}
//                       size="small"
//                       label="Filter by Certification" // <-- FIX APPLIED HERE
//                     >
//                       <MenuItem value="all">All Certifications</MenuItem>
//                       {filterOptions.certifications.map((cert, i) => (
//                         <MenuItem key={i} value={cert}>
//                           {cert}
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                 </CardContent>
//               </Card>

//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" color="#4F46BA" gutterBottom>
//                     Top States by Talent
//                   </Typography>
//                   {loading ? (
//                     <List dense>
//                       {Array.from(new Array(5)).map((_, index) => (
//                         <ListItem key={index}>
//                           <ListItemIcon>
//                             <Skeleton variant="circular" width={16} height={16} />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={<Skeleton variant="text" width="60%" />}
//                             secondary={<Skeleton variant="text" width="40%" />}
//                           />
//                           <Skeleton variant="rectangular" width={40} height={20} />
//                         </ListItem>
//                       ))}
//                     </List>
//                   ) : (
//                     <List dense>
//                       {topStatesData.slice(0, 10).map((s, index) => (
//                         <ListItem key={index}>
//                           <ListItemIcon>
//                             <Box sx={{ width: 16, height: 16, bgcolor: "primary.main", borderRadius: "50%" }} />
//                           </ListItemIcon>
//                           <ListItemText primary={s.state} secondary={`${s.count} professionals`} />
//                           <Chip label={`#${index + 1}`} size="small" color="primary" variant="outlined" />
//                         </ListItem>
//                       ))}
//                     </List>
//                   )}
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}

//         {tabValue === 1 && (
//           <Card
//             sx={{
//               backgroundColor: "#F1FFFA",
//               maxHeight: "75vh",
//               display: "flex",
//               flexDirection: "column",
//             }}
//           >
//             <CardContent>
//               <Typography variant="h6" color="#4F46BA" gutterBottom>
//                 Top Institutions by AI Talent Output
//               </Typography>
//             </CardContent>

//             {isMobile ? (
//               // Mobile-friendly List View
//               <List sx={{ overflowY: "auto" }}>
//                 {loading
//                   ? Array.from(new Array(10)).map((_, i) => (
//                       <ListItem key={i}>
//                         <ListItemText
//                           primary={<Skeleton variant="text" width="70%" />}
//                           secondary={<Skeleton variant="text" width="40%" />}
//                         />
//                       </ListItem>
//                     ))
//                   : institutionData.map((inst, i) => (
//                       <ListItem
//                         key={i}
//                         secondaryAction={<Chip label={`${inst.graduates} graduates`} color="secondary" size="small" />}
//                       >
//                         <ListItemIcon>
//                           <Typography sx={{ mr: 1 }}>#{i + 1}</Typography>
//                           <Avatar sx={{ bgcolor: "primary.main", width: 28, height: 28 }}>
//                             <School fontSize="small" />
//                           </Avatar>
//                         </ListItemIcon>
//                         <ListItemText primary={inst.institution} />
//                       </ListItem>
//                     ))}
//               </List>
//             ) : (
//               // Desktop Table View
//               <TableContainer component={Paper} sx={{ flexGrow: 1 }}>
//                 <Table stickyHeader size="small" aria-label="Top institutions table">
//                   <TableHead>
//                     <TableRow>
//                       <TableCell width={80}>Rank</TableCell>
//                       <TableCell>Institution</TableCell>
//                       <TableCell align="right" width={180}>
//                         Graduates
//                       </TableCell>
//                     </TableRow>
//                   </TableHead>
//                   <TableBody>
//                     {loading
//                       ? Array.from(new Array(15)).map((_, i) => (
//                           <TableRow hover key={i}>
//                             <TableCell>
//                               <Skeleton variant="text" />
//                             </TableCell>
//                             <TableCell>
//                               <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                                 <Skeleton variant="circular" width={28} height={28} />
//                                 <Skeleton variant="text" width="80%" />
//                               </Box>
//                             </TableCell>
//                             <TableCell align="right">
//                               <Skeleton variant="rectangular" width={100} height={20} />
//                             </TableCell>
//                           </TableRow>
//                         ))
//                       : institutionData.map((inst, i) => (
//                           <TableRow hover key={i}>
//                             <TableCell>#{i + 1}</TableCell>
//                             <TableCell>
//                               <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                                 <Avatar sx={{ bgcolor: "primary.main", width: 28, height: 28 }}>
//                                   <School fontSize="small" />
//                                 </Avatar>
//                                 <Typography variant="body2">{inst.institution}</Typography>
//                               </Box>
//                             </TableCell>
//                             <TableCell align="right">
//                               <Chip label={`${inst.graduates} graduates`} color="secondary" size="small" />
//                             </TableCell>
//                           </TableRow>
//                         ))}
//                   </TableBody>
//                 </Table>
//               </TableContainer>
//             )}
//           </Card>
//         )}

//         {tabValue === 2 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12} md={5}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" color="#4F46BA" gutterBottom>
//                     Talent Distribution by Role
//                   </Typography>
//                   {loading ? (
//                     <List>
//                       {Array.from(new Array(5)).map((_, i) => (
//                         <ListItem key={i}>
//                           <ListItemIcon>
//                             <Skeleton variant="circular" width={24} height={24} />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={<Skeleton variant="text" width="70%" />}
//                             secondary={<Skeleton variant="text" width="50%" />}
//                           />
//                           <Skeleton variant="rectangular" width={100} height={8} />
//                         </ListItem>
//                       ))}
//                     </List>
//                   ) : (
//                     <List>
//                       {roleData.map((role, i) => (
//                         <React.Fragment key={i}>
//                           <ListItem>
//                             <ListItemIcon>
//                               <Work />
//                             </ListItemIcon>
//                             <ListItemText primary={role.role} secondary={`${role.count} professionals`} />
//                             <Box sx={{ width: 100, height: 8, bgcolor: "grey.300", borderRadius: 1, overflow: "hidden" }}>
//                               <Box
//                                 sx={{
//                                   width: `${(role.count / maxRoleCount) * 100}%`,
//                                   height: "100%",
//                                   bgcolor: "primary.main",
//                                 }}
//                               />
//                             </Box>
//                           </ListItem>
//                           {i < roleData.length - 1 && <Divider variant="inset" component="li" />}
//                         </React.Fragment>
//                       ))}
//                     </List>
//                   )}
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={4}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" color="#4F46BA" gutterBottom>
//                     Talent Distribution by Certification
//                   </Typography>
//                   {loading ? (
//                     <List>
//                       {Array.from(new Array(5)).map((_, i) => (
//                         <ListItem key={i}>
//                           <ListItemIcon>
//                             <Skeleton variant="circular" width={24} height={24} />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={<Skeleton variant="text" width="70%" />}
//                             secondary={<Skeleton variant="text" width="50%" />}
//                           />
//                           <Skeleton variant="rectangular" width={100} height={8} />
//                         </ListItem>
//                       ))}
//                     </List>
//                   ) : (
//                     <List>
//                       {certificationData.map((cert, i) => (
//                         <React.Fragment key={i}>
//                           <ListItem>
//                             <ListItemIcon>
//                               <School />
//                             </ListItemIcon>
//                             <ListItemText primary={cert.certification} secondary={`${cert.count} certified professionals`} />
//                             <Box sx={{ width: 100, height: 8, bgcolor: "grey.300", borderRadius: 1, overflow: "hidden" }}>
//                               <Box
//                                 sx={{
//                                   width: `${(cert.count / maxCertCount) * 100}%`,
//                                   height: "100%",
//                                   bgcolor: "secondary.main",
//                                 }}
//                               />
//                             </Box>
//                           </ListItem>
//                           {i < certificationData.length - 1 && <Divider variant="inset" component="li" />}
//                         </React.Fragment>
//                       ))}
//                     </List>
//                   )}
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={3}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" color="#4F46BA" gutterBottom>
//                     International Readiness Index
//                   </Typography>
//                   {loading ? (
//                     <Grid container spacing={2}>
//                       <Grid item xs={12}>
//                         <Skeleton variant="rectangular" height={118} />
//                       </Grid>
//                       <Grid item xs={12}>
//                         <Skeleton variant="rectangular" height={118} />
//                       </Grid>
//                       <Grid item xs={12}>
//                         <Skeleton variant="rectangular" height={118} />
//                       </Grid>
//                     </Grid>
//                   ) : (
//                     <Grid container spacing={2}>
//                       <Grid item xs={12}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h3" color="success.main">
//                             {readinessData?.english_proficiency_percentage ?? "0"}%
//                           </Typography>
//                           <Typography>English Proficiency</Typography>
//                         </Paper>
//                       </Grid>
//                       <Grid item xs={12}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h3" color="primary.main">
//                             {readinessData?.passport_ready_percentage ?? "0"}%
//                           </Typography>
//                           <Typography>Passport Ready</Typography>
//                         </Paper>
//                       </Grid>
//                       <Grid item xs={12}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h3" color="warning.main">
//                             {readinessData?.relocation_willing_percentage ?? "0"}%
//                           </Typography>
//                           <Typography>Relocation Willing</Typography>
//                         </Paper>
//                       </Grid>
//                     </Grid>
//                   )}
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}
//       </Box>
//     </Layout>
//   )
// }

// export default TalentHeatmap

// "use client"

// import React, { useState, useEffect } from "react"
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Paper,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Button,
//   Tabs,
//   Tab,
//   Chip,
//   List,
//   ListItem,
//   ListItemText,
//   ListItemIcon,
//   Divider,
//   Avatar,
//   IconButton,
//   Tooltip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Skeleton,
// } from "@mui/material"
// import { School, Work, FilterList, CloudDownload, ZoomIn, ZoomOut, Refresh } from "@mui/icons-material"
// import Layout from "../common/Layout"
// import apiClient from "../../context/apiClient"
// import { GoogleMap, Circle, InfoWindow, useLoadScript } from "@react-google-maps/api"

// const containerStyle = {
//   width: "100%",
//   height: "500px",
// }

// const indiaCenter = { lat: 22.9734, lng: 78.6569 }

// const stateCoordinates = {
//   AndhraPradesh: { lat: 15.9129, lng: 79.74 },
//   ArunachalPradesh: { lat: 28.218, lng: 94.7278 },
//   Assam: { lat: 26.2006, lng: 92.9376 },
//   Bihar: { lat: 25.0961, lng: 85.3131 },
//   Chhattisgarh: { lat: 21.2787, lng: 81.8661 },
//   Goa: { lat: 15.2993, lng: 74.124 },
//   Gujarat: { lat: 22.2587, lng: 71.1924 },
//   Haryana: { lat: 29.0588, lng: 76.0856 },
//   HimachalPradesh: { lat: 31.1048, lng: 77.1734 },
//   Jharkhand: { lat: 23.6102, lng: 85.2799 },
//   Karnataka: { lat: 15.3173, lng: 75.7139 },
//   Kerala: { lat: 10.8505, lng: 76.2711 },
//   MadhyaPradesh: { lat: 22.9734, lng: 78.6569 },
//   Maharashtra: { lat: 19.7515, lng: 75.7139 },
//   Manipur: { lat: 24.6637, lng: 93.9063 },
//   Meghalaya: { lat: 25.467, lng: 91.3662 },
//   Mizoram: { lat: 23.1645, lng: 92.9376 },
//   Nagaland: { lat: 26.1584, lng: 94.5624 },
//   Odisha: { lat: 20.9517, lng: 85.0985 },
//   Punjab: { lat: 31.1471, lng: 75.3412 },
//   Rajasthan: { lat: 27.0238, lng: 74.2179 },
//   Rajsthan: { lat: 27.0238, lng: 74.2179 },
//   Sikkim: { lat: 27.533, lng: 88.5122 },
//   TamilNadu: { lat: 11.1271, lng: 78.6569 },
//   Telangana: { lat: 18.1124, lng: 79.0193 },
//   Tripura: { lat: 23.9408, lng: 91.9882 },
//   UttarPradesh: { lat: 26.8467, lng: 80.9462 },
//   Uttarakhand: { lat: 30.0668, lng: 79.0193 },
//   WestBengal: { lat: 22.9868, lng: 87.855 },
//   AndamanAndNicobarIslands: { lat: 11.7401, lng: 92.6586 },
//   Chandigarh: { lat: 30.7333, lng: 76.7794 },
//   DadraAndNagarHaveliAndDamanAndDiu: { lat: 20.4283, lng: 72.8397 },
//   Delhi: { lat: 28.7041, lng: 77.1025 },
//   JammuAndKashmir: { lat: 33.7782, lng: 76.5762 },
//   Ladakh: { lat: 34.1526, lng: 77.577 },
//   Lakshadweep: { lat: 10.5667, lng: 72.6417 },
//   Puducherry: { lat: 11.9416, lng: 79.8083 },
// }

// function TalentHeatmap() {
//   const [tabValue, setTabValue] = useState(0)
//   const [roleFilter, setRoleFilter] = useState("all")
//   const [certificationFilter, setCertificationFilter] = useState("all")
//   const [zoomLevel, setZoomLevel] = useState(5)
//   const [loading, setLoading] = useState(true)
//   const [institutionData, setInstitutionData] = useState([])
//   const [roleData, setRoleData] = useState([])
//   const [certificationData, setCertificationData] = useState([])
//   const [readinessData, setReadinessData] = useState(null)
//   const [filterOptions, setFilterOptions] = useState({ roles: [], certifications: [] })
//   const [heatmapData, setHeatmapData] = useState([])
//   const [topStatesData, setTopStatesData] = useState([])
//   const [activeState, setActiveState] = useState(null)

//   const { isLoaded } = useLoadScript({
// googleMapsApiKey: "AIzaSyCJFtqpKTMxYbh3E3bnzQeDANkknX-O-gQ",    libraries: ["visualization"],
//   })

//   useEffect(() => {
//     apiClient
//       .get("/talent-heatmap-filter-options/")
//       .then((response) => setFilterOptions(response.data))
//       .catch((error) => console.error("Error fetching filter options:", error))
//   }, [])

//   useEffect(() => {
//     setLoading(true)
//     if (tabValue === 0) {
//       const params = new URLSearchParams()
//       if (roleFilter !== "all") params.append("role", roleFilter)
//       if (certificationFilter !== "all") params.append("certification", certificationFilter)
//       const queryString = params.toString()
//       const url = `/talent-heatmap/${queryString ? `?${queryString}` : ""}`

//       apiClient
//         .get(url)
//         .then((response) => {
//           const states = response.data.state_distribution || []
//           const candidates = response.data.experienced_candidates || []

//           const matchCandidateToState = (candidate, stateName) => {
//             if (!candidate.work_preferences) return false
//             const locations = candidate.work_preferences.map((p) => p.location.toLowerCase())
//             const lowerStateName = stateName.toLowerCase()

//             if (
//               lowerStateName === "maharashtra" &&
//               locations.some((loc) => ["mumbai", "pune", "nashik", "kolhapur", "dhule", "solapur"].some((city) => loc.includes(city)))
//             ) {
//               return true
//             }
//             if (lowerStateName === "rajsthan" && locations.some((loc) => loc.includes("jaipur"))) {
//               return true
//             }
//             if (lowerStateName === "bihar" && locations.some((loc) => loc.includes("patna"))) {
//               return true
//             }
//             return false
//           }

//           const mergedData = states.map((stateObj) => {
//             const stateCandidates = candidates.filter((candidate) => matchCandidateToState(candidate, stateObj.state))
//             return {
//               ...stateObj,
//               candidates: stateCandidates,
//             }
//           })

//           setHeatmapData(mergedData)
//           setTopStatesData(states.sort((a, b) => b.count - a.count))
//           setLoading(false)
//         })
//         .catch((error) => {
//           console.error("Error fetching state-wise heatmap data:", error)
//           setHeatmapData([])
//           setTopStatesData([])
//           setLoading(false)
//         })
//     } else if (tabValue === 1) {
//       apiClient
//         .get("/talent-heatmap-institute-wise/")
//         .then((response) => {
//           setInstitutionData(response.data)
//           setLoading(false)
//         })
//         .catch((error) => {
//           console.error("Error fetching institution data:", error)
//           setLoading(false)
//         })
//     } else if (tabValue === 2) {
//       apiClient
//         .get("/talent_heatmap/RoleAndCertifications/")
//         .then((response) => {
//           setRoleData(response.data.talent_distribution_by_role)
//           setCertificationData(response.data.talent_distribution_by_certification)
//           setReadinessData(response.data.international_readiness_index)
//           setLoading(false)
//         })
//         .catch((error) => {
//           console.error("Error fetching role and certification data:", error)
//           setLoading(false)
//         })
//     }
//   }, [tabValue, roleFilter, certificationFilter])

//   const handleTabChange = (e, newValue) => setTabValue(newValue)
//   const handleRoleFilterChange = (e) => setRoleFilter(e.target.value)
//   const handleCertificationFilterChange = (e) => setCertificationFilter(e.target.value)
//   const handleZoomIn = () => setZoomLevel(Math.min(zoomLevel + 1, 12))
//   const handleZoomOut = () => setZoomLevel(Math.max(zoomLevel - 1, 3))
//   const handleResetZoom = () => setZoomLevel(5)

//   const maxRoleCount = roleData.length > 0 ? Math.max(...roleData.map((r) => r.count)) : 1
//   const maxCertCount = certificationData.length > 0 ? Math.max(...certificationData.map((c) => c.count)) : 1

//   return (
//     <Layout>
//       <Box sx={{ p: 3 }}>
//         <Paper sx={{ p: 2, mb: 3, backgroundColor: "background.paper", border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
//           <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
//             <Typography variant="h4" sx={{ fontWeight: 700, color: "#5A36CC" }}>
//               Talent Availability Heatmap
//             </Typography>
//             <Box sx={{ display: "flex", gap: 2 }}>
//               <Button variant="outlined" startIcon={<FilterList />} size="small">
//                 Advanced Filters
//               </Button>
//               <Button variant="outlined" startIcon={<CloudDownload />} size="small">
//                 Export Data
//               </Button>
//             </Box>
//           </Box>
//         </Paper>
//         <Paper sx={{ mb: 3, backgroundColor: "#F1FFFA" }}>
//           <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" centered>
//             <Tab label="State-wise Distribution" />
//             <Tab label="Institution-wise Distribution" />
//             <Tab label="Role & Certification" />
//           </Tabs>
//         </Paper>

//         {tabValue === 0 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12} md={8}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Box
//                     sx={{
//                       display: "flex",
//                       justifyContent: "space-between",
//                       alignItems: "center",
//                       mb: 2,
//                       width: "870px",
//                       backgroundColor: "#F1FFFA",
//                       borderRadius: "10px",
//                     }}
//                   >
//                     <Typography variant="h6" color="#4F46BA">
//                       India AI Talent Heatmap
//                     </Typography>
//                     <Box sx={{ display: "flex", gap: 1 }}>
//                       <Tooltip title="Zoom In">
//                         <IconButton size="small" onClick={handleZoomIn}>
//                           <ZoomIn />
//                         </IconButton>
//                       </Tooltip>
//                       <Tooltip title="Zoom Out">
//                         <IconButton size="small" onClick={handleZoomOut}>
//                           <ZoomOut />
//                         </IconButton>
//                       </Tooltip>
//                       <Tooltip title="Reset View">
//                         <IconButton size="small" onClick={handleResetZoom}>
//                           <Refresh />
//                         </IconButton>
//                       </Tooltip>
//                     </Box>
//                   </Box>

//                   <Box sx={{ height: 500, borderRadius: 1, overflow: "hidden", border: "1px solid #ddd" }}>
//                     {loading ? (
//                       <Skeleton variant="rectangular" width="100%" height={500} />
//                     ) : isLoaded ? (
//                       <GoogleMap mapContainerStyle={containerStyle} center={indiaCenter} zoom={zoomLevel}>
//                         {heatmapData.map((stateData, index) => {
//                           const coords = stateCoordinates[stateData.state.replace(/\s/g, "")]
//                           if (!coords) return null
//                           return (
//                             <Circle
//                               key={index}
//                               center={coords}
//                               radius={Math.sqrt(stateData.count) * 35000}
//                               onMouseOver={() => setActiveState(stateData)}
//                               onMouseOut={() => setActiveState(null)}
//                               options={{
//                                 strokeColor: "#4F46BA",
//                                 strokeOpacity: 0.8,
//                                 strokeWeight: 2,
//                                 fillColor: "#cc3636ff",
//                                 fillOpacity: 0.4,
//                                 clickable: true,
//                               }}
//                             />
//                           )
//                         })}
//                         {activeState && stateCoordinates[activeState.state.replace(/\s/g, "")] && (
//                           <InfoWindow
//                             position={stateCoordinates[activeState.state.replace(/\s/g, "")]}
//                             onCloseClick={() => setActiveState(null)}
//                             options={{
//                               pixelOffset: new window.google.maps.Size(0, -30),
//                               disableAutoPan: true,
//                               backgroundColor: "#F1FFFA"
//                             }}
//                           >
//                             <Paper
//                               elevation={0}
//                               sx={{
//                                 width: 280,
//                                 p: 0,
//                                 borderRadius: "16px",
//                                 overflow: "hidden",
//                                 border: "1px solid rgba(255, 255, 255, 0.2)",
//                                 background: "linear-gradient(135deg, rgba(93, 109, 248, 0.9), rgba(110, 115, 170, 0.9))",
//                                 backdropFilter: "blur(12px) saturate(180%)",
//                                 boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
//                               }}
//                             >
//                               <Box sx={{ p: "12px 16px", background: "rgba(0,0,0,0.15)" }}>
//                                 <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
//                                   <Typography variant="h6" sx={{ fontWeight: 700, color: "#FFFFFF" }}>
//                                     {activeState.state}
//                                   </Typography>
//                                   <Chip
//                                     icon={<Work sx={{ color: "#E0E0E0 !important" }} />}
//                                     label={`${activeState.count}`}
//                                     size="small"
//                                     sx={{
//                                       fontWeight: 600,
//                                       color: "#E0E0E0",
//                                       backgroundColor: "rgba(255, 255, 255, 0.1)",
//                                     }}
//                                   />
//                                 </Box>
//                               </Box>

//                               <List
//                                 dense
//                                 sx={{
//                                   maxHeight: 150,
//                                   overflow: "auto",
//                                   p: "8px 8px 8px 16px",
//                                   "&::-webkit-scrollbar": { width: "4px" },
//                                   "&::-webkit-scrollbar-track": { background: "transparent" },
//                                   "&::-webkit-scrollbar-thumb": {
//                                     background: "#888",
//                                     borderRadius: "2px",
//                                   },
//                                   "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
//                                 }}
//                               >
//                                 {activeState.candidates && activeState.candidates.length > 0 ? (
//                                   activeState.candidates.map((candidate) => (
//                                     <ListItem
//                                       key={candidate.id}
//                                       disablePadding
//                                       secondaryAction={
//                                         <IconButton edge="end" size="small" sx={{ color: "#b0b0b0" }}>
//                                           <School />
//                                         </IconButton>
//                                       }
//                                       sx={{
//                                         py: 0.75,
//                                         borderRadius: "8px",
//                                         transition: "background-color 0.2s ease-in-out",
//                                         "&:hover": {
//                                           background: "rgba(255, 255, 255, 0.08)",
//                                         },
//                                       }}
//                                     >
//                                       <ListItemIcon sx={{ minWidth: 40 }}>
//                                         <Avatar src={candidate.profile_photo_url} sx={{ width: 30, height: 30 }}>
//                                           {candidate.name.charAt(0)}
//                                         </Avatar>
//                                       </ListItemIcon>
//                                       <ListItemText
//                                         primary={
//                                           <Typography variant="body2" sx={{ fontWeight: 600, color: "#FFFFFF" }}>
//                                             {candidate.name}
//                                           </Typography>
//                                         }
//                                         secondary={
//                                           <Typography variant="caption" sx={{ color: "#B0B0B0" }}>
//                                             {(() => {
//                                               try {
//                                                 const interests = JSON.parse(candidate.domain_interest.replace(/'/g, '"'));
//                                                 return Array.isArray(interests) ? interests[0] : "Specialist";
//                                               } catch (e) {
//                                                 return "Specialist";
//                                               }
//                                             })()}
//                                           </Typography>
//                                         }
//                                       />
//                                     </ListItem>
//                                   ))
//                                 ) : (
//                                   <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 80 }}>
//                                     <Typography variant="body2" sx={{ color: "#B0B0B0" }}>
//                                       No candidates found.
//                                     </Typography>
//                                   </Box>
//                                 )}
//                               </List>
//                             </Paper>
//                           </InfoWindow>
//                         )}
//                       </GoogleMap>
//                     ) : (
//                       <Typography sx={{ p: 2 }}>Loading Map...</Typography>
//                     )}
//                   </Box>
//                 </CardContent>
//               </Card>
//             </Grid>
//             <Grid item xs={12} md={4}>
//               <Card sx={{ mb: 2, backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" color="#4F46BA" gutterBottom>
//                     Filters
//                   </Typography>
//                   <FormControl fullWidth sx={{ mb: 2 }}>
//                     <InputLabel id="role-filter-label">Filter by Role</InputLabel>
//                     <Select
//                       labelId="role-filter-label"
//                       value={roleFilter}
//                       onChange={handleRoleFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Roles</MenuItem>
//                       {filterOptions.roles.map((role, i) => (
//                         <MenuItem key={i} value={role}>
//                           {role}
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                   <FormControl fullWidth>
//                     <InputLabel id="cert-filter-label">Filter by Certification</InputLabel>
//                     <Select
//                       labelId="cert-filter-label"
//                       value={certificationFilter}
//                       onChange={handleCertificationFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Certifications</MenuItem>
//                       {filterOptions.certifications.map((cert, i) => (
//                         <MenuItem key={i} value={cert}>
//                           {cert}
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                 </CardContent>
//               </Card>

//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" color="#4F46BA" gutterBottom>
//                     Top States by Talent
//                   </Typography>
//                   {loading ? (
//                     <List dense>
//                       {Array.from(new Array(5)).map((_, index) => (
//                         <ListItem key={index}>
//                           <ListItemIcon>
//                             <Skeleton variant="circular" width={16} height={16} />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={<Skeleton variant="text" width="60%" />}
//                             secondary={<Skeleton variant="text" width="40%" />}
//                           />
//                           <Skeleton variant="rectangular" width={40} height={20} />
//                         </ListItem>
//                       ))}
//                     </List>
//                   ) : (
//                     <List dense>
//                       {topStatesData.slice(0, 10).map((s, index) => (
//                         <ListItem key={index}>
//                           <ListItemIcon>
//                             <Box sx={{ width: 16, height: 16, bgcolor: "primary.main", borderRadius: "50%" }} />
//                           </ListItemIcon>
//                           <ListItemText primary={s.state} secondary={`${s.count} professionals`} />
//                           <Chip label={`#${index + 1}`} size="small" color="primary" variant="outlined" />
//                         </ListItem>
//                       ))}
//                     </List>
//                   )}
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}

//         {tabValue === 1 && (
//           <Card
//             sx={{
//               backgroundColor: "#F1FFFA",
//               display: "flex",
//               flexDirection: "column",
//               height: "calc(100vh - 250px)",
//             }}
//           >
//             <CardContent>
//               <Typography variant="h6" color="#4F46BA" gutterBottom>
//                 Top Institutions by AI Talent Output
//               </Typography>
//             </CardContent>
//             <TableContainer component={Paper} sx={{ flexGrow: 1 }}>
//               <Table stickyHeader size="small" aria-label="Top institutions table">
//                 <TableHead>
//                   <TableRow>
//                     <TableCell width={80}>Rank</TableCell>
//                     <TableCell>Institution</TableCell>
//                     <TableCell align="right" width={180}>
//                       Graduates
//                     </TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {loading
//                     ? Array.from(new Array(15)).map((_, i) => (
//                         <TableRow hover key={i}>
//                           <TableCell>
//                             <Skeleton variant="text" />
//                           </TableCell>
//                           <TableCell>
//                             <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                               <Skeleton variant="circular" width={28} height={28} />
//                               <Skeleton variant="text" width="80%" />
//                             </Box>
//                           </TableCell>
//                           <TableCell align="right">
//                             <Skeleton variant="rectangular" width={100} height={20} />
//                           </TableCell>
//                         </TableRow>
//                       ))
//                     : institutionData.map((inst, i) => (
//                         <TableRow hover key={i}>
//                           <TableCell>#{i + 1}</TableCell>
//                           <TableCell>
//                             <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                               <Avatar sx={{ bgcolor: "primary.main", width: 28, height: 28 }}>
//                                 <School fontSize="small" />
//                               </Avatar>
//                               <Typography variant="body2">{inst.institution}</Typography>
//                             </Box>
//                           </TableCell>
//                           <TableCell align="right">
//                             <Chip label={`${inst.graduates} graduates`} color="secondary" size="small" />
//                           </TableCell>
//                         </TableRow>
//                       ))}
//                 </TableBody>
//               </Table>
//             </TableContainer>
//           </Card>
//         )}

//         {tabValue === 2 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12} md={5}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" color="#4F46BA" gutterBottom>
//                     Talent Distribution by Role
//                   </Typography>
//                   {loading ? (
//                     <List>
//                       {Array.from(new Array(5)).map((_, i) => (
//                         <ListItem key={i}>
//                           <ListItemIcon>
//                             <Skeleton variant="circular" width={24} height={24} />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={<Skeleton variant="text" width="70%" />}
//                             secondary={<Skeleton variant="text" width="50%" />}
//                           />
//                           <Skeleton variant="rectangular" width={100} height={8} />
//                         </ListItem>
//                       ))}
//                     </List>
//                   ) : (
//                     <List>
//                       {roleData.map((role, i) => (
//                         <React.Fragment key={i}>
//                           <ListItem>
//                             <ListItemIcon>
//                               <Work />
//                             </ListItemIcon>
//                             <ListItemText primary={role.role} secondary={`${role.count} professionals`} />
//                             <Box sx={{ width: 100, height: 8, bgcolor: "grey.300", borderRadius: 1, overflow: "hidden" }}>
//                               <Box
//                                 sx={{
//                                   width: `${(role.count / maxRoleCount) * 100}%`,
//                                   height: "100%",
//                                   bgcolor: "primary.main",
//                                 }}
//                               />
//                             </Box>
//                           </ListItem>
//                           {i < roleData.length - 1 && <Divider variant="inset" component="li" />}
//                         </React.Fragment>
//                       ))}
//                     </List>
//                   )}
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={4}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" color="#4F46BA" gutterBottom>
//                     Talent Distribution by Certification
//                   </Typography>
//                   {loading ? (
//                     <List>
//                       {Array.from(new Array(5)).map((_, i) => (
//                         <ListItem key={i}>
//                           <ListItemIcon>
//                             <Skeleton variant="circular" width={24} height={24} />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={<Skeleton variant="text" width="70%" />}
//                             secondary={<Skeleton variant="text" width="50%" />}
//                           />
//                           <Skeleton variant="rectangular" width={100} height={8} />
//                         </ListItem>
//                       ))}
//                     </List>
//                   ) : (
//                     <List>
//                       {certificationData.map((cert, i) => (
//                         <React.Fragment key={i}>
//                           <ListItem>
//                             <ListItemIcon>
//                               <School />
//                             </ListItemIcon>
//                             <ListItemText primary={cert.certification} secondary={`${cert.count} certified professionals`} />
//                             <Box sx={{ width: 100, height: 8, bgcolor: "grey.300", borderRadius: 1, overflow: "hidden" }}>
//                               <Box
//                                 sx={{
//                                   width: `${(cert.count / maxCertCount) * 100}%`,
//                                   height: "100%",
//                                   bgcolor: "secondary.main",
//                                 }}
//                               />
//                             </Box>
//                           </ListItem>
//                           {i < certificationData.length - 1 && <Divider variant="inset" component="li" />}
//                         </React.Fragment>
//                       ))}
//                     </List>
//                   )}
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={3}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" color="#4F46BA" gutterBottom>
//                     International Readiness Index
//                   </Typography>
//                   {loading ? (
//                     <Grid container spacing={2}>
//                       <Grid item xs={12}>
//                         <Skeleton variant="rectangular" height={118} />
//                       </Grid>
//                       <Grid item xs={12}>
//                         <Skeleton variant="rectangular" height={118} />
//                       </Grid>
//                       <Grid item xs={12}>
//                         <Skeleton variant="rectangular" height={118} />
//                       </Grid>
//                     </Grid>
//                   ) : (
//                     <Grid container spacing={2}>
//                       <Grid item xs={12}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h3" color="success.main">
//                             {readinessData?.english_proficiency_percentage ?? "0"}%
//                           </Typography>
//                           <Typography>English Proficiency</Typography>
//                         </Paper>
//                       </Grid>
//                       <Grid item xs={12}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h3" color="primary.main">
//                             {readinessData?.passport_ready_percentage ?? "0"}%
//                           </Typography>
//                           <Typography>Passport Ready</Typography>
//                         </Paper>
//                       </Grid>
//                       <Grid item xs={12}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h3" color="warning.main">
//                             {readinessData?.relocation_willing_percentage ?? "0"}%
//                           </Typography>
//                           <Typography>Relocation Willing</Typography>
//                         </Paper>
//                       </Grid>
//                     </Grid>
//                   )}
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}
//       </Box>
//     </Layout>
//   )
// }

// export default TalentHeatmap

// "use client"

// import React, { useState, useEffect } from "react"
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Paper,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Button,
//   Tabs,
//   Tab,
//   Chip,
//   List,
//   ListItem,
//   ListItemText,
//   ListItemIcon,
//   Divider,
//   Avatar,
//   IconButton,
//   Tooltip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Skeleton,
// } from "@mui/material"
// import { School, Work, FilterList, CloudDownload, ZoomIn, ZoomOut, Refresh } from "@mui/icons-material"
// import Layout from "../common/Layout"
// import apiClient from "../../context/apiClient"
// import { GoogleMap, Circle, InfoWindow, useLoadScript } from "@react-google-maps/api"

// const containerStyle = {
//   width: "100%",
//   height: "500px",
// }

// const indiaCenter = { lat: 22.9734, lng: 78.6569 }

// const stateCoordinates = {
//   AndhraPradesh: { lat: 15.9129, lng: 79.74 },
//   ArunachalPradesh: { lat: 28.218, lng: 94.7278 },
//   Assam: { lat: 26.2006, lng: 92.9376 },
//   Bihar: { lat: 25.0961, lng: 85.3131 },
//   Chhattisgarh: { lat: 21.2787, lng: 81.8661 },
//   Goa: { lat: 15.2993, lng: 74.124 },
//   Gujarat: { lat: 22.2587, lng: 71.1924 },
//   Haryana: { lat: 29.0588, lng: 76.0856 },
//   HimachalPradesh: { lat: 31.1048, lng: 77.1734 },
//   Jharkhand: { lat: 23.6102, lng: 85.2799 },
//   Karnataka: { lat: 15.3173, lng: 75.7139 },
//   Kerala: { lat: 10.8505, lng: 76.2711 },
//   MadhyaPradesh: { lat: 22.9734, lng: 78.6569 },
//   Maharashtra: { lat: 19.7515, lng: 75.7139 },
//   Manipur: { lat: 24.6637, lng: 93.9063 },
//   Meghalaya: { lat: 25.467, lng: 91.3662 },
//   Mizoram: { lat: 23.1645, lng: 92.9376 },
//   Nagaland: { lat: 26.1584, lng: 94.5624 },
//   Odisha: { lat: 20.9517, lng: 85.0985 },
//   Punjab: { lat: 31.1471, lng: 75.3412 },
//   Rajasthan: { lat: 27.0238, lng: 74.2179 },
//   Rajsthan: { lat: 27.0238, lng: 74.2179 },
//   Sikkim: { lat: 27.533, lng: 88.5122 },
//   TamilNadu: { lat: 11.1271, lng: 78.6569 },
//   Telangana: { lat: 18.1124, lng: 79.0193 },
//   Tripura: { lat: 23.9408, lng: 91.9882 },
//   UttarPradesh: { lat: 26.8467, lng: 80.9462 },
//   Uttarakhand: { lat: 30.0668, lng: 79.0193 },
//   WestBengal: { lat: 22.9868, lng: 87.855 },
//   AndamanAndNicobarIslands: { lat: 11.7401, lng: 92.6586 },
//   Chandigarh: { lat: 30.7333, lng: 76.7794 },
//   DadraAndNagarHaveliAndDamanAndDiu: { lat: 20.4283, lng: 72.8397 },
//   Delhi: { lat: 28.7041, lng: 77.1025 },
//   JammuAndKashmir: { lat: 33.7782, lng: 76.5762 },
//   Ladakh: { lat: 34.1526, lng: 77.577 },
//   Lakshadweep: { lat: 10.5667, lng: 72.6417 },
//   Puducherry: { lat: 11.9416, lng: 79.8083 },
// }

// function TalentHeatmap() {
//   const [tabValue, setTabValue] = useState(0)
//   const [roleFilter, setRoleFilter] = useState("all")
//   const [certificationFilter, setCertificationFilter] = useState("all")
//   const [zoomLevel, setZoomLevel] = useState(5)
//   const [loading, setLoading] = useState(true)
//   const [institutionData, setInstitutionData] = useState([])
//   const [roleData, setRoleData] = useState([])
//   const [certificationData, setCertificationData] = useState([])
//   const [readinessData, setReadinessData] = useState(null)
//   const [filterOptions, setFilterOptions] = useState({ roles: [], certifications: [] })
//   const [heatmapData, setHeatmapData] = useState([])
//   const [topStatesData, setTopStatesData] = useState([])
//   const [activeState, setActiveState] = useState(null)

//   const { isLoaded } = useLoadScript({
//   googleMapsApiKey: "AIzaSyCJFtqpKTMxYbh3E3bnzQeDANkknX-O-gQ",
//     libraries: ["visualization"],
//   })

//   useEffect(() => {
//     apiClient
//       .get("/talent-heatmap-filter-options/")
//       .then((response) => setFilterOptions(response.data))
//       .catch((error) => console.error("Error fetching filter options:", error))
//   }, [])

//   useEffect(() => {
//     setLoading(true)
//     if (tabValue === 0) {
//       const params = new URLSearchParams()
//       if (roleFilter !== "all") params.append("role", roleFilter)
//       if (certificationFilter !== "all") params.append("certification", certificationFilter)
//       const queryString = params.toString()
//       const url = `/talent-heatmap/${queryString ? `?${queryString}` : ""}`

//       apiClient
//         .get(url)
//         .then((response) => {
//           const states = response.data.state_distribution || []
//           const candidates = response.data.experienced_candidates || []

//           const matchCandidateToState = (candidate, stateName) => {
//             if (!candidate.work_preferences) return false
//             const locations = candidate.work_preferences.map((p) => p.location.toLowerCase())
//             const lowerStateName = stateName.toLowerCase()

//             if (
//               lowerStateName === "maharashtra" &&
//               locations.some((loc) => ["mumbai", "pune", "nashik", "kolhapur", "dhule", "solapur"].some((city) => loc.includes(city)))
//             ) {
//               return true
//             }
//             if (lowerStateName === "rajsthan" && locations.some((loc) => loc.includes("jaipur"))) {
//               return true
//             }
//             if (lowerStateName === "bihar" && locations.some((loc) => loc.includes("patna"))) {
//               return true
//             }
//             return false
//           }

//           const mergedData = states.map((stateObj) => {
//             const stateCandidates = candidates.filter((candidate) => matchCandidateToState(candidate, stateObj.state))
//             return {
//               ...stateObj,
//               candidates: stateCandidates,
//             }
//           })

//           setHeatmapData(mergedData)
//           setTopStatesData(states.sort((a, b) => b.count - a.count))
//           setLoading(false)
//         })
//         .catch((error) => {
//           console.error("Error fetching state-wise heatmap data:", error)
//           setHeatmapData([])
//           setTopStatesData([])
//           setLoading(false)
//         })
//     } else if (tabValue === 1) {
//       apiClient
//         .get("/talent-heatmap-institute-wise/")
//         .then((response) => {
//           setInstitutionData(response.data)
//           setLoading(false)
//         })
//         .catch((error) => {
//           console.error("Error fetching institution data:", error)
//           setLoading(false)
//         })
//     } else if (tabValue === 2) {
//       apiClient
//         .get("/talent_heatmap/RoleAndCertifications/")
//         .then((response) => {
//           setRoleData(response.data.talent_distribution_by_role)
//           setCertificationData(response.data.talent_distribution_by_certification)
//           setReadinessData(response.data.international_readiness_index)
//           setLoading(false)
//         })
//         .catch((error) => {
//           console.error("Error fetching role and certification data:", error)
//           setLoading(false)
//         })
//     }
//   }, [tabValue, roleFilter, certificationFilter])

//   const handleTabChange = (e, newValue) => setTabValue(newValue)
//   const handleRoleFilterChange = (e) => setRoleFilter(e.target.value)
//   const handleCertificationFilterChange = (e) => setCertificationFilter(e.target.value)
//   const handleZoomIn = () => setZoomLevel(Math.min(zoomLevel + 1, 12))
//   const handleZoomOut = () => setZoomLevel(Math.max(zoomLevel - 1, 3))
//   const handleResetZoom = () => setZoomLevel(5)

//   const maxRoleCount = roleData.length > 0 ? Math.max(...roleData.map((r) => r.count)) : 1
//   const maxCertCount = certificationData.length > 0 ? Math.max(...certificationData.map((c) => c.count)) : 1

//   return (
//     <Layout>
//       <Box sx={{ p: 3 }}>
//         <Paper sx={{ p: 2, mb: 3, backgroundColor: "background.paper", border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
//           <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
//             <Typography variant="h4" sx={{ fontWeight: 700, color: "#5A36CC" }}>
//               Talent Availability Heatmap
//             </Typography>
//             <Box sx={{ display: "flex", gap: 2 }}>
//               <Button variant="outlined" startIcon={<FilterList />} size="small">
//                 Advanced Filters
//               </Button>
//               <Button variant="outlined" startIcon={<CloudDownload />} size="small">
//                 Export Data
//               </Button>
//             </Box>
//           </Box>
//         </Paper>
//         <Paper sx={{ mb: 3, backgroundColor: "#F1FFFA" }}>
//           <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" centered>
//             <Tab label="State-wise Distribution" />
//             <Tab label="Institution-wise Distribution" />
//             <Tab label="Role & Certification" />
//           </Tabs>
//         </Paper>

//         {tabValue === 0 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12} md={8}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Box
//                     sx={{
//                       display: "flex",
//                       justifyContent: "space-between",
//                       alignItems: "center",
//                       mb: 2,
//                       width: "870px",
//                       backgroundColor: "#F1FFFA",
//                       borderRadius: "10px",
//                     }}
//                   >
//                     <Typography variant="h6" color="#4F46BA">
//                       India AI Talent Heatmap
//                     </Typography>
//                     <Box sx={{ display: "flex", gap: 1 }}>
//                       <Tooltip title="Zoom In">
//                         <IconButton size="small" onClick={handleZoomIn}>
//                           <ZoomIn />
//                         </IconButton>
//                       </Tooltip>
//                       <Tooltip title="Zoom Out">
//                         <IconButton size="small" onClick={handleZoomOut}>
//                           <ZoomOut />
//                         </IconButton>
//                       </Tooltip>
//                       <Tooltip title="Reset View">
//                         <IconButton size="small" onClick={handleResetZoom}>
//                           <Refresh />
//                         </IconButton>
//                       </Tooltip>
//                     </Box>
//                   </Box>

//                   <Box sx={{ height: 500, borderRadius: 1, overflow: "hidden", border: "1px solid #ddd" }}>
//                     {loading ? (
//                       <Skeleton variant="rectangular" width="100%" height={500} />
//                     ) : isLoaded ? (
//                       <GoogleMap mapContainerStyle={containerStyle} center={indiaCenter} zoom={zoomLevel}>
//                         {heatmapData.map((stateData, index) => {
//                           const coords = stateCoordinates[stateData.state.replace(/\s/g, "")]
//                           if (!coords) return null
//                           return (
//                             <Circle
//                               key={index}
//                               center={coords}
//                               radius={Math.sqrt(stateData.count) * 35000}
//                               onMouseOver={() => setActiveState(stateData)}
//                               onMouseOut={() => setActiveState(null)}
//                               options={{
//                                 strokeColor: "#24cd3aff",
//                                 strokeOpacity: 0.8,
//                                 strokeWeight: 2,
//                                 fillColor: "#f0546eff",
//                                 fillOpacity: 0.4,
//                                 clickable: true,
//                               }}
//                             />
//                           )
//                         })}
//                         {activeState && stateCoordinates[activeState.state.replace(/\s/g, "")] && (
//                           <InfoWindow
//                             position={stateCoordinates[activeState.state.replace(/\s/g, "")]}
//                             onCloseClick={() => setActiveState(null)}
//                             options={{
//                               pixelOffset: new window.google.maps.Size(0, -30),
//                               disableAutoPan: true,
//                             }}
//                           >
//                             <Paper
//                               elevation={4}
//                               sx={{
//                                 width: 260,
//                                 borderRadius: "12px",
//                                 overflow: "hidden",
//                                 border: "1px solid rgba(255, 255, 255, 0.2)",
//                                 background: "rgba(255, 255, 255, 0.75)",
//                                 backdropFilter: "blur(10px)",
//                                 boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
//                               }}
//                             >
//                               <Box sx={{ p: 2 }}>
//                                 <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
//                                   <Typography variant="h6" sx={{ fontWeight: 700, color: "#333" }}>
//                                     {activeState.state}
//                                   </Typography>
//                                   <Chip
//                                     icon={<Work />}
//                                     label={`${activeState.count}`}
//                                     color="primary"
//                                     size="small"
//                                     sx={{ fontWeight: 500 }}
//                                   />
//                                 </Box>
//                                 <Divider sx={{ mb: 1 }} />
//                                 <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1 }}>
//                                   Available Talent
//                                 </Typography>
//                                 <List dense sx={{ maxHeight: 140, overflow: "auto", p: 0 }}>
//                                   {activeState.candidates && activeState.candidates.length > 0 ? (
//                                     activeState.candidates.map((candidate, i) => (
//                                       <ListItem
//                                         key={i}
//                                         disablePadding
//                                         secondaryAction={
//                                           <Tooltip title="View Profile">
//                                             <IconButton edge="end" size="small">
//                                               <School />
//                                             </IconButton>
//                                           </Tooltip>
//                                         }
//                                         sx={{
//                                           py: 0.5,
//                                           "&:not(:last-child)": {
//                                             borderBottom: "1px solid #eee",
//                                           },
//                                         }}
//                                       >
//                                         <ListItemIcon sx={{ minWidth: 40 }}>
//                                           <Avatar src={candidate.profile_photo_url} sx={{ width: 28, height: 28 }}>
//                                             {candidate.name.charAt(0)}
//                                           </Avatar>
//                                         </ListItemIcon>
//                                         <ListItemText
//                                           primary={
//                                             <Typography variant="body2" sx={{ fontWeight: 500 }}>
//                                               {candidate.name}
//                                             </Typography>
//                                           }
//                                           secondary={
//                                             <Typography variant="caption" sx={{ color: "text.secondary" }}>
//                                               {Array.isArray(candidate.domain_interest)
//                                                 ? candidate.domain_interest[0]
//                                                 : "Specialist"}
//                                             </Typography>
//                                           }
//                                         />
//                                       </ListItem>
//                                     ))
//                                   ) : (
//                                     <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 80 }}>
//                                       <Typography variant="body2" sx={{ color: "text.secondary" }}>
//                                         No candidate details found.
//                                       </Typography>
//                                     </Box>
//                                   )}
//                                 </List>
//                               </Box>
//                             </Paper>
//                           </InfoWindow>
//                         )}
//                       </GoogleMap>
//                     ) : (
//                       <Typography sx={{ p: 2 }}>Loading Map...</Typography>
//                     )}
//                   </Box>
//                 </CardContent>
//               </Card>
//             </Grid>
//             <Grid item xs={12} md={4}>
//               <Card sx={{ mb: 2, backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" color="#4F46BA" gutterBottom>
//                     Filters
//                   </Typography>
//                   <FormControl fullWidth sx={{ mb: 2 }}>
//                     <InputLabel id="role-filter-label">Filter by Role</InputLabel>
//                     <Select
//                       labelId="role-filter-label"
//                       value={roleFilter}
//                       onChange={handleRoleFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Roles</MenuItem>
//                       {filterOptions.roles.map((role, i) => (
//                         <MenuItem key={i} value={role}>
//                           {role}
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                   <FormControl fullWidth>
//                     <InputLabel id="cert-filter-label">Filter by Certification</InputLabel>
//                     <Select
//                       labelId="cert-filter-label"
//                       value={certificationFilter}
//                       onChange={handleCertificationFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Certifications</MenuItem>
//                       {filterOptions.certifications.map((cert, i) => (
//                         <MenuItem key={i} value={cert}>
//                           {cert}
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                 </CardContent>
//               </Card>

//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" color="#4F46BA" gutterBottom>
//                     Top States by Talent
//                   </Typography>
//                   {loading ? (
//                     <List dense>
//                       {Array.from(new Array(5)).map((_, index) => (
//                         <ListItem key={index}>
//                           <ListItemIcon>
//                             <Skeleton variant="circular" width={16} height={16} />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={<Skeleton variant="text" width="60%" />}
//                             secondary={<Skeleton variant="text" width="40%" />}
//                           />
//                           <Skeleton variant="rectangular" width={40} height={20} />
//                         </ListItem>
//                       ))}
//                     </List>
//                   ) : (
//                     <List dense>
//                       {topStatesData.slice(0, 10).map((s, index) => (
//                         <ListItem key={index}>
//                           <ListItemIcon>
//                             <Box sx={{ width: 16, height: 16, bgcolor: "primary.main", borderRadius: "50%" }} />
//                           </ListItemIcon>
//                           <ListItemText primary={s.state} secondary={`${s.count} professionals`} />
//                           <Chip label={`#${index + 1}`} size="small" color="primary" variant="outlined" />
//                         </ListItem>
//                       ))}
//                     </List>
//                   )}
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}

//         {tabValue === 1 && (
//           <Card
//             sx={{
//               backgroundColor: "#F1FFFA",
//               display: "flex",
//               flexDirection: "column",
//               height: "calc(100vh - 250px)",
//             }}
//           >
//             <CardContent>
//               <Typography variant="h6" color="#4F46BA" gutterBottom>
//                 Top Institutions by AI Talent Output
//               </Typography>
//             </CardContent>
//             <TableContainer component={Paper} sx={{ flexGrow: 1 }}>
//               <Table stickyHeader size="small" aria-label="Top institutions table">
//                 <TableHead>
//                   <TableRow>
//                     <TableCell width={80}>Rank</TableCell>
//                     <TableCell>Institution</TableCell>
//                     <TableCell align="right" width={180}>
//                       Graduates
//                     </TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {loading
//                     ? Array.from(new Array(15)).map((_, i) => (
//                         <TableRow hover key={i}>
//                           <TableCell>
//                             <Skeleton variant="text" />
//                           </TableCell>
//                           <TableCell>
//                             <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                               <Skeleton variant="circular" width={28} height={28} />
//                               <Skeleton variant="text" width="80%" />
//                             </Box>
//                           </TableCell>
//                           <TableCell align="right">
//                             <Skeleton variant="rectangular" width={100} height={20} />
//                           </TableCell>
//                         </TableRow>
//                       ))
//                     : institutionData.map((inst, i) => (
//                         <TableRow hover key={i}>
//                           <TableCell>#{i + 1}</TableCell>
//                           <TableCell>
//                             <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                               <Avatar sx={{ bgcolor: "primary.main", width: 28, height: 28 }}>
//                                 <School fontSize="small" />
//                               </Avatar>
//                               <Typography variant="body2">{inst.institution}</Typography>
//                             </Box>
//                           </TableCell>
//                           <TableCell align="right">
//                             <Chip label={`${inst.graduates} graduates`} color="secondary" size="small" />
//                           </TableCell>
//                         </TableRow>
//                       ))}
//                 </TableBody>
//               </Table>
//             </TableContainer>
//           </Card>
//         )}

//         {tabValue === 2 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12} md={5}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" color="#4F46BA" gutterBottom>
//                     Talent Distribution by Role
//                   </Typography>
//                   {loading ? (
//                     <List>
//                       {Array.from(new Array(5)).map((_, i) => (
//                         <ListItem key={i}>
//                           <ListItemIcon>
//                             <Skeleton variant="circular" width={24} height={24} />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={<Skeleton variant="text" width="70%" />}
//                             secondary={<Skeleton variant="text" width="50%" />}
//                           />
//                           <Skeleton variant="rectangular" width={100} height={8} />
//                         </ListItem>
//                       ))}
//                     </List>
//                   ) : (
//                     <List>
//                       {roleData.map((role, i) => (
//                         <React.Fragment key={i}>
//                           <ListItem>
//                             <ListItemIcon>
//                               <Work />
//                             </ListItemIcon>
//                             <ListItemText primary={role.role} secondary={`${role.count} professionals`} />
//                             <Box sx={{ width: 100, height: 8, bgcolor: "grey.300", borderRadius: 1, overflow: "hidden" }}>
//                               <Box
//                                 sx={{
//                                   width: `${(role.count / maxRoleCount) * 100}%`,
//                                   height: "100%",
//                                   bgcolor: "primary.main",
//                                 }}
//                               />
//                             </Box>
//                           </ListItem>
//                           {i < roleData.length - 1 && <Divider variant="inset" component="li" />}
//                         </React.Fragment>
//                       ))}
//                     </List>
//                   )}
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={4}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" color="#4F46BA" gutterBottom>
//                     Talent Distribution by Certification
//                   </Typography>
//                   {loading ? (
//                     <List>
//                       {Array.from(new Array(5)).map((_, i) => (
//                         <ListItem key={i}>
//                           <ListItemIcon>
//                             <Skeleton variant="circular" width={24} height={24} />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={<Skeleton variant="text" width="70%" />}
//                             secondary={<Skeleton variant="text" width="50%" />}
//                           />
//                           <Skeleton variant="rectangular" width={100} height={8} />
//                         </ListItem>
//                       ))}
//                     </List>
//                   ) : (
//                     <List>
//                       {certificationData.map((cert, i) => (
//                         <React.Fragment key={i}>
//                           <ListItem>
//                             <ListItemIcon>
//                               <School />
//                             </ListItemIcon>
//                             <ListItemText primary={cert.certification} secondary={`${cert.count} certified professionals`} />
//                             <Box sx={{ width: 100, height: 8, bgcolor: "grey.300", borderRadius: 1, overflow: "hidden" }}>
//                               <Box
//                                 sx={{
//                                   width: `${(cert.count / maxCertCount) * 100}%`,
//                                   height: "100%",
//                                   bgcolor: "secondary.main",
//                                 }}
//                               />
//                             </Box>
//                           </ListItem>
//                           {i < certificationData.length - 1 && <Divider variant="inset" component="li" />}
//                         </React.Fragment>
//                       ))}
//                     </List>
//                   )}
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={3}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" color="#4F46BA" gutterBottom>
//                     International Readiness Index
//                   </Typography>
//                   {loading ? (
//                     <Grid container spacing={2}>
//                       <Grid item xs={12}>
//                         <Skeleton variant="rectangular" height={118} />
//                       </Grid>
//                       <Grid item xs={12}>
//                         <Skeleton variant="rectangular" height={118} />
//                       </Grid>
//                       <Grid item xs={12}>
//                         <Skeleton variant="rectangular" height={118} />
//                       </Grid>
//                     </Grid>
//                   ) : (
//                     <Grid container spacing={2}>
//                       <Grid item xs={12}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h3" color="success.main">
//                             {readinessData?.english_proficiency_percentage ?? "0"}%
//                           </Typography>
//                           <Typography>English Proficiency</Typography>
//                         </Paper>
//                       </Grid>
//                       <Grid item xs={12}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h3" color="primary.main">
//                             {readinessData?.passport_ready_percentage ?? "0"}%
//                           </Typography>
//                           <Typography>Passport Ready</Typography>
//                         </Paper>
//                       </Grid>
//                       <Grid item xs={12}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h3" color="warning.main">
//                             {readinessData?.relocation_willing_percentage ?? "0"}%
//                           </Typography>
//                           <Typography>Relocation Willing</Typography>
//                         </Paper>
//                       </Grid>
//                     </Grid>
//                   )}
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}
//       </Box>
//     </Layout>
//   )
// }

// export default TalentHeatmap
// "use client"

  // import React, { useState, useEffect } from "react"
  // import {
  //   Box,
  //   Typography,
  //   Grid,
  //   Card,
  //   CardContent,
  //   Paper,
  //   FormControl,
  //   InputLabel,
  //   Select,
  //   MenuItem,
  //   Button,
  //   Tabs,
  //   Tab,
  //   Chip,
  //   List,
  //   ListItem,
  //   ListItemText,
  //   ListItemIcon,
  //   Divider,
  //   Avatar,
  //   IconButton,
  //   Tooltip,
  //   Table,
  //   TableBody,
  //   TableCell,
  //   TableContainer,
  //   TableHead,
  //   TableRow,
  //   Skeleton,
  // } from "@mui/material"
  // import { School, Work, FilterList, CloudDownload, ZoomIn, ZoomOut, Refresh } from "@mui/icons-material"
  // import Layout from "../common/Layout"
  // import apiClient from "../../context/apiClient"

  // import { GoogleMap, HeatmapLayer, useLoadScript } from "@react-google-maps/api"

  // const containerStyle = {
  //   width: "100%",
  //   height: "500px",
  // }

  // const indiaCenter = { lat: 22.9734, lng: 78.6569 }

  // const stateCoordinates = {
  //   AndhraPradesh: { lat: 15.9129, lng: 79.74 },
  //   ArunachalPradesh: { lat: 28.218, lng: 94.7278 },
  //   Assam: { lat: 26.2006, lng: 92.9376 },
  //   Bihar: { lat: 25.0961, lng: 85.3131 },
  //   Chhattisgarh: { lat: 21.2787, lng: 81.8661 },
  //   Goa: { lat: 15.2993, lng: 74.124 },
  //   Gujarat: { lat: 22.2587, lng: 71.1924 },
  //   Haryana: { lat: 29.0588, lng: 76.0856 },
  //   HimachalPradesh: { lat: 31.1048, lng: 77.1734 },
  //   Jharkhand: { lat: 23.6102, lng: 85.2799 },
  //   Karnataka: { lat: 15.3173, lng: 75.7139 },
  //   Kerala: { lat: 10.8505, lng: 76.2711 },
  //   MadhyaPradesh: { lat: 22.9734, lng: 78.6569 },
  //   Maharashtra: { lat: 19.7515, lng: 75.7139 },
  //   Manipur: { lat: 24.6637, lng: 93.9063 },
  //   Meghalaya: { lat: 25.467, lng: 91.3662 },
  //   Mizoram: { lat: 23.1645, lng: 92.9376 },
  //   Nagaland: { lat: 26.1584, lng: 94.5624 },
  //   Odisha: { lat: 20.9517, lng: 85.0985 },
  //   Punjab: { lat: 31.1471, lng: 75.3412 },
  //   Rajasthan: { lat: 27.0238, lng: 74.2179 },
  //   Rajsthan: { lat: 27.0238, lng: 74.2179 },
  //   Sikkim: { lat: 27.533, lng: 88.5122 },
  //   TamilNadu: { lat: 11.1271, lng: 78.6569 },
  //   Telangana: { lat: 18.1124, lng: 79.0193 },
  //   Tripura: { lat: 23.9408, lng: 91.9882 },
  //   UttarPradesh: { lat: 26.8467, lng: 80.9462 },
  //   Uttarakhand: { lat: 30.0668, lng: 79.0193 },
  //   WestBengal: { lat: 22.9868, lng: 87.855 },
  //   AndamanAndNicobarIslands: { lat: 11.7401, lng: 92.6586 },
  //   Chandigarh: { lat: 30.7333, lng: 76.7794 },
  //   DadraAndNagarHaveliAndDamanAndDiu: { lat: 20.4283, lng: 72.8397 },
  //   Delhi: { lat: 28.7041, lng: 77.1025 },
  //   JammuAndKashmir: { lat: 33.7782, lng: 76.5762 },
  //   Ladakh: { lat: 34.1526, lng: 77.577 },
  //   Lakshadweep: { lat: 10.5667, lng: 72.6417 },
  //   Puducherry: { lat: 11.9416, lng: 79.8083 },
  // }

  // function TalentHeatmap() {
  //   const [tabValue, setTabValue] = useState(0)
  //   const [roleFilter, setRoleFilter] = useState("all")
  //   const [certificationFilter, setCertificationFilter] = useState("all")
  //   const [zoomLevel, setZoomLevel] = useState(5)
  //   const [loading, setLoading] = useState(true)

  //   const [institutionData, setInstitutionData] = useState([])
  //   const [roleData, setRoleData] = useState([])
  //   const [certificationData, setCertificationData] = useState([])
  //   const [readinessData, setReadinessData] = useState(null)

  //   const [filterOptions, setFilterOptions] = useState({ roles: [], certifications: [] })
  //   const [stateHeatmapData, setStateHeatmapData] = useState([])
  //   const [topStatesData, setTopStatesData] = useState([])
  //   const [geoPoints, setGeoPoints] = useState([])

  //   const { isLoaded } = useLoadScript({
  //     googleMapsApiKey: "AIzaSyCJFtqpKTMxYbh3E3bnzQeDANkknX-O-gQ",
  //     libraries: ["visualization"],
  //   })

  //   useEffect(() => {
  //     apiClient
  //       .get("/talent-heatmap-filter-options/")
  //       .then((response) => setFilterOptions(response.data))
  //       .catch((error) => console.error("Error fetching filter options:", error))
  //   }, [])

  //   useEffect(() => {
  //     setLoading(true)
  //     if (tabValue === 0) {
  //       const params = new URLSearchParams()
  //       if (roleFilter !== "all") params.append("role", roleFilter)
  //       if (certificationFilter !== "all") params.append("certification", certificationFilter)
  //       const queryString = params.toString()
  //       const url = `/talent-heatmap/${queryString ? `?${queryString}` : ""}`

  //       apiClient
  //         .get(url)
  //         .then((response) => {
  //           setStateHeatmapData(response.data.heatmap_data)
  //           setTopStatesData(response.data.top_states)
  //           if (window.google) {
  //             const points = response.data.heatmap_data.flatMap((item) => {
  //               const coords = stateCoordinates[item.state]
  //               if (!coords) return []
  //               return Array(item.count).fill(new window.google.maps.LatLng(coords.lat, coords.lng))
  //             })
  //             setGeoPoints(points)
  //           }
  //           setLoading(false)
  //         })
  //         .catch((error) => {
  //           console.error("Error fetching state-wise heatmap data:", error)
  //           setStateHeatmapData([])
  //           setTopStatesData([])
  //           setGeoPoints([])
  //           setLoading(false)
  //         })
  //     } else if (tabValue === 1) {
  //       apiClient
  //         .get("/talent-heatmap-institute-wise/")
  //         .then((response) => {
  //           setInstitutionData(response.data)
  //           setLoading(false)
  //         })
  //         .catch((error) => {
  //           console.error("Error fetching institution data:", error)
  //           setLoading(false)
  //         })
  //     } else if (tabValue === 2) {
  //       apiClient
  //         .get("/talent_heatmap/RoleAndCertifications/")
  //         .then((response) => {
  //           setRoleData(response.data.talent_distribution_by_role)
  //           setCertificationData(response.data.talent_distribution_by_certification)
  //           setReadinessData(response.data.international_readiness_index)
  //           setLoading(false)
  //         })
  //         .catch((error) => {
  //           console.error("Error fetching role and certification data:", error)
  //           setLoading(false)
  //         })
  //     }
  //   }, [tabValue, roleFilter, certificationFilter])

  //   const handleTabChange = (e, newValue) => setTabValue(newValue)
  //   const handleRoleFilterChange = (e) => setRoleFilter(e.target.value)
  //   const handleCertificationFilterChange = (e) => setCertificationFilter(e.target.value)

  //   const handleZoomIn = () => setZoomLevel(Math.min(zoomLevel + 1, 12))
  //   const handleZoomOut = () => setZoomLevel(Math.max(zoomLevel - 1, 3))
  //   const handleResetZoom = () => setZoomLevel(5)

  //   const maxRoleCount = roleData.length > 0 ? Math.max(...roleData.map((r) => r.count)) : 1
  //   const maxCertCount = certificationData.length > 0 ? Math.max(...certificationData.map((c) => c.count)) : 1

  //   return (
  //     <Layout>
  //       <Box sx={{ p: 3 }}>
  //         <Paper sx={{ p: 2, mb: 3, backgroundColor: "background.paper", border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
  //           <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
  //             <Typography variant="h4" sx={{ fontWeight: 700, color: "#5A36CC" }} >
  //               Talent Availability Heatmap
  //             </Typography>
  //             <Box sx={{ display: "flex", gap: 2 }}>
  //               <Button variant="outlined" startIcon={<FilterList />} size="small">
  //                 Advanced Filters
  //               </Button>
  //               <Button variant="outlined" startIcon={<CloudDownload />} size="small">
  //                 Export Data
  //               </Button>
  //             </Box>
  //           </Box>
  //         </Paper>
  //         <Paper sx={{ mb: 3, backgroundColor: "#F1FFFA" }}>
  //           <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" centered>
  //             <Tab label="State-wise Distribution" />
  //             <Tab label="Institution-wise Distribution" />
  //             <Tab label="Role & Certification" />
  //           </Tabs>
  //         </Paper>

  //         {tabValue === 0 && (
  //           <Grid container spacing={3}>
  //             <Grid item xs={12} md={8}>
  //               <Card sx={{ backgroundColor: "#F1FFFA" }}>
  //                 <CardContent>
  //                   <Box
  //                     sx={{
  //                       display: "flex",
  //                       justifyContent: "space-between",
  //                       alignItems: "center",
  //                       mb: 2,
  //                       width: "870px",
  //                       height: "100%",
  //                       backgroundColor: "#F1FFFA",
  //                       borderRadius: "10px",
  //                     }}
  //                   >
  //                     <Typography variant="h6" color="#4F46BA">India AI Talent Heatmap</Typography>
  //                     <Box sx={{ display: "flex", gap: 1 }}>
  //                       <Tooltip title="Zoom In">
  //                         <IconButton size="small" onClick={handleZoomIn}>
  //                           <ZoomIn />
  //                         </IconButton>
  //                       </Tooltip>
  //                       <Tooltip title="Zoom Out">
  //                         <IconButton size="small" onClick={handleZoomOut}>
  //                           <ZoomOut />
  //                         </IconButton>
  //                       </Tooltip>
  //                       <Tooltip title="Reset View">
  //                         <IconButton size="small" onClick={handleResetZoom}>
  //                           <Refresh />
  //                         </IconButton>
  //                       </Tooltip>
  //                     </Box>
  //                   </Box>

  //                   <Box sx={{ height: 500, borderRadius: 1, overflow: "hidden", border: "1px solid #ddd" }}>
  //                     {loading ? (
  //                       <Skeleton variant="rectangular" width="100%" height={500} />
  //                     ) : isLoaded ? (
  //                       <GoogleMap mapContainerStyle={containerStyle} center={indiaCenter} zoom={zoomLevel}>
  //                         {geoPoints.length > 0 && (
  //                           <HeatmapLayer data={geoPoints} options={{ radius: 40, opacity: 0.6 }} />
  //                         )}
  //                       </GoogleMap>
  //                     ) : (
  //                       <Typography sx={{ p: 2 }}>Loading Map...</Typography>
  //                     )}
  //                   </Box>
  //                 </CardContent>
  //               </Card>
  //             </Grid>

  //             <Grid item xs={12} md={4}>
  //               <Card sx={{ mb: 2, backgroundColor: "#F1FFFA" }}>
  //                 <CardContent>
  //                   <Typography variant="h6" color="#4F46BA" gutterBottom>
  //                     Filters
  //                   </Typography>
  //                   <FormControl fullWidth sx={{ mb: 2 }}>
  //                     <InputLabel id="role-filter-label">Filter by Role</InputLabel>
  //                     <Select
  //                       labelId="role-filter-label"
  //                       value={roleFilter}
  //                       onChange={handleRoleFilterChange}
  //                       size="small"
  //                     >
  //                       <MenuItem value="all">All Roles</MenuItem>
  //                       {filterOptions.roles.map((role, i) => (
  //                         <MenuItem key={i} value={role}>
  //                           {role}
  //                         </MenuItem>
  //                       ))}
  //                     </Select>
  //                   </FormControl>
  //                   <FormControl fullWidth>
  //                     <InputLabel id="cert-filter-label">Filter by Certification</InputLabel>
  //                     <Select
  //                       labelId="cert-filter-label"
  //                       value={certificationFilter}
  //                       onChange={handleCertificationFilterChange}
  //                       size="small"
  //                     >
  //                       <MenuItem value="all">All Certifications</MenuItem>
  //                       {filterOptions.certifications.map((cert, i) => (
  //                         <MenuItem key={i} value={cert}>
  //                           {cert}
  //                         </MenuItem>
  //                       ))}
  //                     </Select>
  //                   </FormControl>
  //                 </CardContent>
  //               </Card>

  //               <Card sx={{ backgroundColor: "#F1FFFA" }}>
  //                 <CardContent>
  //                   <Typography variant="h6" color="#4F46BA" gutterBottom>
  //                     Top States by Talent
  //                   </Typography>
  //                   {loading ? (
  //                     <List dense>
  //                       {Array.from(new Array(10)).map((_, index) => (
  //                         <ListItem key={index}>
  //                           <ListItemIcon>
  //                             <Skeleton variant="circular" width={16} height={16} />
  //                           </ListItemIcon>
  //                           <ListItemText
  //                             primary={<Skeleton variant="text" width="60%" />}
  //                             secondary={<Skeleton variant="text" width="40%" />}
  //                           />
  //                           <Skeleton variant="rectangular" width={40} height={20} />
  //                         </ListItem>
  //                       ))}
  //                     </List>
  //                   ) : (
  //                     <List dense>
  //                       {topStatesData.slice(0, 10).map((s, index) => (
  //                         <ListItem key={index}>
  //                           <ListItemIcon>
  //                             <Box sx={{ width: 16, height: 16, bgcolor: "primary.main", borderRadius: "50%" }} />
  //                           </ListItemIcon>
  //                           <ListItemText primary={s.state} secondary={`${s.count} professionals`} />
  //                           <Chip label={`#${index + 1}`} size="small" color="primary" variant="outlined" />
  //                         </ListItem>
  //                       ))}
  //                     </List>
  //                   )}
  //                 </CardContent>
  //               </Card>
  //             </Grid>
  //           </Grid>
  //         )}

  //         {tabValue === 1 && (
  //           <Card
  //             sx={{
  //               backgroundColor: "#F1FFFA",
  //               display: "flex",
  //               flexDirection: "column",
  //               height: "calc(100vh - 250px)",
  //             }}
  //           >
  //             <CardContent>
  //               <Typography variant="h6" color="#4F46BA" gutterBottom>
  //                 Top Institutions by AI Talent Output
  //               </Typography>
  //             </CardContent>
  //             <TableContainer component={Paper} sx={{ flexGrow: 1 }}>
  //               <Table stickyHeader size="small" aria-label="Top institutions table">
  //                 <TableHead>
  //                   <TableRow>
  //                     <TableCell width={80}>Rank</TableCell>
  //                     <TableCell>Institution</TableCell>
  //                     <TableCell align="right" width={180}>
  //                       Graduates
  //                     </TableCell>
  //                   </TableRow>
  //                 </TableHead>
  //                 <TableBody>
  //                   {loading
  //                     ? Array.from(new Array(15)).map((_, i) => (
  //                         <TableRow hover key={i}>
  //                           <TableCell>
  //                             <Skeleton variant="text" />
  //                           </TableCell>
  //                           <TableCell>
  //                             <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
  //                               <Skeleton variant="circular" width={28} height={28} />
  //                               <Skeleton variant="text" width="80%" />
  //                             </Box>
  //                           </TableCell>
  //                           <TableCell align="right">
  //                             <Skeleton variant="rectangular" width={100} height={20} />
  //                           </TableCell>
  //                         </TableRow>
  //                       ))
  //                     : institutionData.map((inst, i) => (
  //                         <TableRow hover key={i}>
  //                           <TableCell>#{i + 1}</TableCell>
  //                           <TableCell>
  //                             <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
  //                               <Avatar sx={{ bgcolor: "primary.main", width: 28, height: 28 }}>
  //                                 <School fontSize="small" />
  //                               </Avatar>
  //                               <Typography variant="body2">{inst.institution}</Typography>
  //                             </Box>
  //                           </TableCell>
  //                           <TableCell align="right">
  //                             <Chip label={`${inst.graduates} graduates`} color="secondary" size="small" />
  //                           </TableCell>
  //                         </TableRow>
  //                       ))}
  //                 </TableBody>
  //               </Table>
  //             </TableContainer>
  //           </Card>
  //         )}

  //         {tabValue === 2 && (
  //           <Grid container spacing={2}>
  //             <Grid item xs={12} md={5} sx={{ width: "30%" }}>
  //               <Card sx={{ backgroundColor: "#F1FFFA" }}>
  //                 <CardContent>
  //                   <Typography variant="h6" color="#4F46BA" gutterBottom>
  //                     Talent Distribution by Role
  //                   </Typography>
  //                   {loading ? (
  //                     <List>
  //                       {Array.from(new Array(5)).map((_, i) => (
  //                         <ListItem key={i}>
  //                           <ListItemIcon>
  //                             <Skeleton variant="circular" width={24} height={24} />
  //                           </ListItemIcon>
  //                           <ListItemText
  //                             primary={<Skeleton variant="text" width="70%" />}
  //                             secondary={<Skeleton variant="text" width="50%" />}
  //                           />
  //                           <Skeleton variant="rectangular" width={100} height={8} />
  //                         </ListItem>
  //                       ))}
  //                     </List>
  //                   ) : (
  //                     <List>
  //                       {roleData.map((role, i) => (
  //                         <React.Fragment key={i}>
  //                           <ListItem>
  //                             <ListItemIcon>
  //                               <Work />
  //                             </ListItemIcon>
  //                             <ListItemText primary={role.role} secondary={`${role.count} professionals`} />
  //                             <Box
  //                               sx={{ width: 100, height: 8, bgcolor: "grey.300", borderRadius: 1, overflow: "hidden" }}
  //                             >
  //                               <Box
  //                                 sx={{
  //                                   width: `${(role.count / maxRoleCount) * 100}%`,
  //                                   height: "100%",
  //                                   bgcolor: "primary.main",
  //                                 }}
  //                               />
  //                             </Box>
  //                           </ListItem>
  //                           {i < roleData.length - 1 && <Divider variant="inset" component="li" />}
  //                         </React.Fragment>
  //                       ))}
  //                     </List>
  //                   )}
  //                 </CardContent>
  //               </Card>
  //             </Grid>

  //             <Grid item xs={12} md={4} sx={{ width: "35%" }}>
  //               <Card sx={{ backgroundColor: "#F1FFFA" }}>
  //                 <CardContent>
  //                   <Typography variant="h6" color="#4F46BA" gutterBottom>
  //                     Talent Distribution by Certification
  //                   </Typography>
  //                   {loading ? (
  //                     <List>
  //                       {Array.from(new Array(5)).map((_, i) => (
  //                         <ListItem key={i}>
  //                           <ListItemIcon>
  //                             <Skeleton variant="circular" width={24} height={24} />
  //                           </ListItemIcon>
  //                           <ListItemText
  //                             primary={<Skeleton variant="text" width="70%" />}
  //                             secondary={<Skeleton variant="text" width="50%" />}
  //                           />
  //                           <Skeleton variant="rectangular" width={100} height={8} />
  //                         </ListItem>
  //                       ))}
  //                     </List>
  //                   ) : (
  //                     <List>
  //                       {certificationData.map((cert, i) => (
  //                         <React.Fragment key={i}>
  //                           <ListItem>
  //                             <ListItemIcon>
  //                               <School />
  //                             </ListItemIcon>
  //                             <ListItemText
  //                               primary={cert.certification}
  //                               secondary={`${cert.count} certified professionals`}
  //                             />
  //                             <Box
  //                               sx={{ width: 100, height: 8, bgcolor: "grey.300", borderRadius: 1, overflow: "hidden" }}
  //                             >
  //                               <Box
  //                                 sx={{
  //                                   width: `${(cert.count / maxCertCount) * 100}%`,
  //                                   height: "100%",
  //                                   bgcolor: "secondary.main",
  //                                 }}
  //                               />
  //                             </Box>
  //                           </ListItem>
  //                           {i < certificationData.length - 1 && <Divider variant="inset" component="li" />}
  //                         </React.Fragment>
  //                       ))}
  //                     </List>
  //                   )}
  //                 </CardContent>
  //               </Card>
  //             </Grid>

  //             <Grid item xs={12} md={3} sx={{ width: "50%" }}>
  //               <Card sx={{ backgroundColor: "#F1FFFA", width: "30%" }}>
  //                 <CardContent>
  //                   <Typography variant="h6" color="#4F46BA" gutterBottom>
  //                     International Readiness Index
  //                   </Typography>
  //                   {loading ? (
  //                     <Grid container spacing={2}>
  //                       <Grid item xs={12}>
  //                         <Skeleton variant="rectangular" height={118} />
  //                       </Grid>
  //                       <Grid item xs={12}>
  //                         <Skeleton variant="rectangular" height={118} />
  //                       </Grid>
  //                       <Grid item xs={12}>
  //                         <Skeleton variant="rectangular" height={118} />
  //                       </Grid>
  //                     </Grid>
  //                   ) : (
  //                     <Grid container spacing={2}>
  //                       <Grid item xs={12}>
  //                         <Paper sx={{ p: 2, textAlign: "center" }}>
  //                           <Typography variant="h3" color="success.main">
  //                             {readinessData?.english_proficiency_percentage ?? "0"}%
  //                           </Typography>
  //                           <Typography>English Proficiency</Typography>
  //                         </Paper>
  //                       </Grid>
  //                       <Grid item xs={12}>
  //                         <Paper sx={{ p: 2, textAlign: "center" }}>
  //                           <Typography variant="h3" color="primary.main">
  //                             {readinessData?.passport_ready_percentage ?? "0"}%
  //                           </Typography>
  //                           <Typography>Passport Ready</Typography>
  //                         </Paper>
  //                       </Grid>
  //                       <Grid item xs={12}>
  //                         <Paper sx={{ p: 2, textAlign: "center" }}>
  //                           <Typography variant="h3" color="warning.main">
  //                             {readinessData?.relocation_willing_percentage ?? "0"}%
  //                           </Typography>
  //                           <Typography>Relocation Willing</Typography>
  //                         </Paper>
  //                       </Grid>
  //                     </Grid>
  //                   )}
  //                 </CardContent>
  //               </Card>
  //             </Grid>
  //           </Grid>
  //         )}
  //       </Box>
  //     </Layout>
  //   )
  // }

  // export default TalentHeatmap

// "use client"

// import React, { useState, useEffect } from "react"
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Paper,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Button,
//   Tabs,
//   Tab,
//   Chip,
//   List,
//   ListItem,
//   ListItemText,
//   ListItemIcon,
//   Divider,
//   Avatar,
//   IconButton,
//   Tooltip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
// } from "@mui/material"
// import { School, Work, FilterList, CloudDownload, ZoomIn, ZoomOut, Refresh } from "@mui/icons-material"
// import Layout from "../common/Layout"
// import apiClient from "../../context/apiClient"

// // ✅ Google Maps imports
// import { GoogleMap, HeatmapLayer, useLoadScript } from "@react-google-maps/api"

// // Map container style
// const containerStyle = {
//   width: "100%",
//   height: "500px",
// }

// // Center on India
// const indiaCenter = { lat: 22.9734, lng: 78.6569 }

// // ✅ Hardcoded state → coordinates
// const stateCoordinates = {
// AndhraPradesh: { lat: 15.9129, lng: 79.7400 },
//   ArunachalPradesh: { lat: 28.2180, lng: 94.7278 },
//   Assam: { lat: 26.2006, lng: 92.9376 },
//   Bihar: { lat: 25.0961, lng: 85.3131 },
//   Chhattisgarh: { lat: 21.2787, lng: 81.8661 },
//   Goa: { lat: 15.2993, lng: 74.1240 },
//   Gujarat: { lat: 22.2587, lng: 71.1924 },
//   Haryana: { lat: 29.0588, lng: 76.0856 },
//   HimachalPradesh: { lat: 31.1048, lng: 77.1734 },
//   Jharkhand: { lat: 23.6102, lng: 85.2799 },
//   Karnataka: { lat: 15.3173, lng: 75.7139 },
//   Kerala: { lat: 10.8505, lng: 76.2711 },
//   MadhyaPradesh: { lat: 22.9734, lng: 78.6569 },
//   Maharashtra: { lat: 19.7515, lng: 75.7139 },
//   Manipur: { lat: 24.6637, lng: 93.9063 },
//   Meghalaya: { lat: 25.4670, lng: 91.3662 },
//   Mizoram: { lat: 23.1645, lng: 92.9376 },
//   Nagaland: { lat: 26.1584, lng: 94.5624 },
//   Odisha: { lat: 20.9517, lng: 85.0985 },
//   Punjab: { lat: 31.1471, lng: 75.3412 },
//   Rajasthan: { lat: 27.0238, lng: 74.2179 },
//  Rajsthan: { lat: 27.0238, lng: 74.2179 },
//   Sikkim: { lat: 27.5330, lng: 88.5122 },
//   TamilNadu: { lat: 11.1271, lng: 78.6569 },
//   Telangana: { lat: 18.1124, lng: 79.0193 },
//   Tripura: { lat: 23.9408, lng: 91.9882 },
//   UttarPradesh: { lat: 26.8467, lng: 80.9462 },
//   Uttarakhand: { lat: 30.0668, lng: 79.0193 },
//   WestBengal: { lat: 22.9868, lng: 87.8550 },

//   // Union Territories
//   AndamanAndNicobarIslands: { lat: 11.7401, lng: 92.6586 },
//   Chandigarh: { lat: 30.7333, lng: 76.7794 },
//   DadraAndNagarHaveliAndDamanAndDiu: { lat: 20.4283, lng: 72.8397 },
//   Delhi: { lat: 28.7041, lng: 77.1025 },
//   JammuAndKashmir: { lat: 33.7782, lng: 76.5762 },
//   Ladakh: { lat: 34.1526, lng: 77.5770 },
//   Lakshadweep: { lat: 10.5667, lng: 72.6417 },
//   Puducherry: { lat: 11.9416, lng: 79.8083 }
// }

// function TalentHeatmap() {
//   const [tabValue, setTabValue] = useState(0)
//   const [roleFilter, setRoleFilter] = useState("all")
//   const [certificationFilter, setCertificationFilter] = useState("all")
//   const [zoomLevel, setZoomLevel] = useState(5)

//   const [institutionData, setInstitutionData] = useState([])
//   const [roleData, setRoleData] = useState([])
//   const [certificationData, setCertificationData] = useState([])
//   const [readinessData, setReadinessData] = useState(null)

//   const [filterOptions, setFilterOptions] = useState({ roles: [], certifications: [] })
//   const [stateHeatmapData, setStateHeatmapData] = useState([])
//   const [topStatesData, setTopStatesData] = useState([])
//   const [geoPoints, setGeoPoints] = useState([]) // ✅ For Google Heatmap

//   // Load Google Maps script
//   const { isLoaded } = useLoadScript({
//     googleMapsApiKey: "AIzaSyCJFtqpKTMxYbh3E3bnzQeDANkknX-O-gQ", // 🔑 replace with real key
//     libraries: ["visualization"], // Required for Heatmap
//   })

//   // Fetch filter options
//   useEffect(() => {
//     apiClient
//       .get("/talent-heatmap-filter-options/")
//       .then((response) => setFilterOptions(response.data))
//       .catch((error) => console.error("Error fetching filter options:", error))
//   }, [])

//   // Fetch data
//   useEffect(() => {
//     if (tabValue === 0) {
//       const params = new URLSearchParams()
//       if (roleFilter !== "all") params.append("role", roleFilter)
//       if (certificationFilter !== "all") params.append("certification", certificationFilter)
//       const queryString = params.toString()
//       const url = `/talent-heatmap/${queryString ? `?${queryString}` : ""}`

//       apiClient
//         .get(url)
//         .then((response) => {
//           setStateHeatmapData(response.data.heatmap_data)
//           setTopStatesData(response.data.top_states)

//           // ✅ Convert state + count into geo points
//           if (window.google) {
//             const points = response.data.heatmap_data.flatMap((item) => {
//               const coords = stateCoordinates[item.state]
//               if (!coords) return []
//               return Array(item.count).fill(new window.google.maps.LatLng(coords.lat, coords.lng))
//             })
//             setGeoPoints(points)
//           }
//         })
//         .catch((error) => {
//           console.error("Error fetching state-wise heatmap data:", error)
//           setStateHeatmapData([])
//           setTopStatesData([])
//           setGeoPoints([])
//         })
//     } else if (tabValue === 1) {
//       apiClient
//         .get("/talent-heatmap-institute-wise/")
//         .then((response) => setInstitutionData(response.data))
//         .catch((error) => console.error("Error fetching institution data:", error))
//     } else if (tabValue === 2) {
//       apiClient
//         .get("/talent_heatmap/RoleAndCertifications/")
//         .then((response) => {
//           setRoleData(response.data.talent_distribution_by_role)
//           setCertificationData(response.data.talent_distribution_by_certification)
//           setReadinessData(response.data.international_readiness_index)
//         })
//         .catch((error) => console.error("Error fetching role and certification data:", error))
//     }
//   }, [tabValue, roleFilter, certificationFilter])

//   // Handlers
//   const handleTabChange = (e, newValue) => setTabValue(newValue)
//   const handleRoleFilterChange = (e) => setRoleFilter(e.target.value)
//   const handleCertificationFilterChange = (e) => setCertificationFilter(e.target.value)

//   const handleZoomIn = () => setZoomLevel(Math.min(zoomLevel + 1, 12))
//   const handleZoomOut = () => setZoomLevel(Math.max(zoomLevel - 1, 3))
//   const handleResetZoom = () => setZoomLevel(5)

//   const maxRoleCount = roleData.length > 0 ? Math.max(...roleData.map((r) => r.count)) : 1
//   const maxCertCount = certificationData.length > 0 ? Math.max(...certificationData.map((c) => c.count)) : 1

//   return (
//     <Layout>
//       <Box sx={{ p: 3 }}>
//         {/* Header */}
//         <Paper sx={{ p: 2, mb: 3, backgroundColor: "background.paper", border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
//           <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
//             <Typography variant="h4" component="h1">
//               Talent Availability Heatmap
//             </Typography>
//             <Box sx={{ display: "flex", gap: 2 }}>
//               <Button variant="outlined" startIcon={<FilterList />} size="small">
//                 Advanced Filters
//               </Button>
//               <Button variant="outlined" startIcon={<CloudDownload />} size="small">
//                 Export Data
//               </Button>
//             </Box>
//           </Box>
//         </Paper>
//         {/* Tabs */}
//         <Paper sx={{ mb: 3, backgroundColor: "#F1FFFA" }}>
//           <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" centered>
//             <Tab label="State-wise Distribution" />
//             <Tab label="Institution-wise Distribution" />
//             <Tab label="Role & Certification" />
//           </Tabs>
//         </Paper>

//         {/* Tab 0 - State-wise */}
//         {tabValue === 0 && (
//           <Grid container spacing={3}>
//             {/* Map Section */}
//             <Grid item xs={12} md={8}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Box
//                     sx={{
//                       display: "flex",
//                       justifyContent: "space-between",
//                       alignItems: "center",
//                       mb: 2,
//                       width: "870px",
//                       height: "100%",
//                       backgroundColor: "#F1FFFA",
//                       borderRadius: "10px",
//                     }}
//                   >
//                     <Typography variant="h6">India AI Talent Heatmap</Typography>
//                     <Box sx={{ display: "flex", gap: 1 }}>
//                       <Tooltip title="Zoom In">
//                         <IconButton size="small" onClick={handleZoomIn}>
//                           <ZoomIn />
//                         </IconButton>
//                       </Tooltip>
//                       <Tooltip title="Zoom Out">
//                         <IconButton size="small" onClick={handleZoomOut}>
//                           <ZoomOut />
//                         </IconButton>
//                       </Tooltip>
//                       <Tooltip title="Reset View">
//                         <IconButton size="small" onClick={handleResetZoom}>
//                           <Refresh />
//                         </IconButton>
//                       </Tooltip>
//                     </Box>
//                   </Box>

//                   <Box sx={{ height: 500, borderRadius: 1, overflow: "hidden", border: "1px solid #ddd" }}>
//                     {isLoaded ? (
//                       <GoogleMap mapContainerStyle={containerStyle} center={indiaCenter} zoom={zoomLevel}>
//                         {geoPoints.length > 0 && (
//                           <HeatmapLayer data={geoPoints} options={{ radius: 40, opacity: 0.6 }} />
//                         )}
//                       </GoogleMap>
//                     ) : (
//                       <Typography sx={{ p: 2 }}>Loading Map...</Typography>
//                     )}
//                   </Box>
//                 </CardContent>
//               </Card>
//             </Grid>

//             {/* Filters + Top States */}
//             <Grid item xs={12} md={4}>
//               <Card sx={{ mb: 2, backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Filters
//                   </Typography>
//                   <FormControl fullWidth sx={{ mb: 2 }}>
//                     <InputLabel id="role-filter-label">Filter by Role</InputLabel>
//                     <Select
//                       labelId="role-filter-label"
//                       value={roleFilter}
//                       onChange={handleRoleFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Roles</MenuItem>
//                       {filterOptions.roles.map((role, i) => (
//                         <MenuItem key={i} value={role}>
//                           {role}
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                   <FormControl fullWidth>
//                     <InputLabel id="cert-filter-label">Filter by Certification</InputLabel>
//                     <Select
//                       labelId="cert-filter-label"
//                       value={certificationFilter}
//                       onChange={handleCertificationFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Certifications</MenuItem>
//                       {filterOptions.certifications.map((cert, i) => (
//                         <MenuItem key={i} value={cert}>
//                           {cert}
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                 </CardContent>
//               </Card>

//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Top States by Talent
//                   </Typography>
//                   <List dense>
//                     {topStatesData.slice(0, 10).map((s, index) => (
//                       <ListItem key={index}>
//                         <ListItemIcon>
//                           <Box sx={{ width: 16, height: 16, bgcolor: "primary.main", borderRadius: "50%" }} />
//                         </ListItemIcon>
//                         <ListItemText primary={s.state} secondary={`${s.count} professionals`} />
//                         <Chip label={`#${index + 1}`} size="small" color="primary" variant="outlined" />
//                       </ListItem>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}

//         {/* Tab 1 - Institutions */}
//         {/* {tabValue === 1 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent sx={{ p: 0 }}>
//                   <Box sx={{ p: 2 }}>
//                     <Typography variant="h6" gutterBottom>
//                       Top Institutions by AI Talent Output
//                     </Typography>
//                   </Box>
//                   <TableContainer
//                     component={Paper}
//                     sx={{
//                       width: "100%",
//                       maxHeight: "calc(100vh - 280px)",
//                       borderRadius: 0,
//                     }}
//                   >
//                     <Table stickyHeader size="small" aria-label="Top institutions table">
//                       <TableHead>
//                         <TableRow>
//                           <TableCell width={80}>Rank</TableCell>
//                           <TableCell>Institution</TableCell>
//                           <TableCell align="right" width={180}>
//                             Graduates
//                           </TableCell>
//                         </TableRow>
//                       </TableHead>
//                       <TableBody>
//                         {institutionData.map((inst, i) => (
//                           <TableRow hover key={i}>
//                             <TableCell>#{i + 1}</TableCell>
//                             <TableCell>
//                               <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                                 <Avatar sx={{ bgcolor: "primary.main", width: 28, height: 28 }}>
//                                   <School fontSize="small" />
//                                 </Avatar>
//                                 <Typography variant="body2">{inst.institution}</Typography>
//                               </Box>
//                             </TableCell>
//                             <TableCell align="right">
//                               <Chip label={`${inst.graduates} graduates`} color="secondary" size="small" />
//                             </TableCell>
//                           </TableRow>
//                         ))}
//                       </TableBody>
//                     </Table>
//                   </TableContainer>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )} */}

        
//         {tabValue === 1 && (
//           <Card sx={{ backgroundColor: "#F1FFFA", display: "flex", flexDirection: "column", height: "calc(100vh - 250px)" }}>
//             <CardContent>
//               <Typography variant="h6" gutterBottom>
//                 Top Institutions by AI Talent Output
//               </Typography>
//             </CardContent>
//             <TableContainer component={Paper} sx={{ flexGrow: 1 }}>
//               <Table stickyHeader size="small" aria-label="Top institutions table">
//                 <TableHead>
//                   <TableRow>
//                     <TableCell width={80}>Rank</TableCell>
//                     <TableCell>Institution</TableCell>
//                     <TableCell align="right" width={180}>Graduates</TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {institutionData.map((inst, i) => (
//                     <TableRow hover key={i}>
//                       <TableCell>#{i + 1}</TableCell>
//                       <TableCell>
//                         <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                           <Avatar sx={{ bgcolor: "primary.main", width: 28, height: 28 }}>
//                             <School fontSize="small" />
//                           </Avatar>
//                           <Typography variant="body2">{inst.institution}</Typography>
//                         </Box>
//                       </TableCell>
//                       <TableCell align="right">
//                         <Chip label={`${inst.graduates} graduates`} color="secondary" size="small" />
//                       </TableCell>
//                     </TableRow>
//                   ))}
//                 </TableBody>
//               </Table>
//             </TableContainer>
//           </Card>
//         )}


//         {/* Tab 2 - Role & Certification */}
//         {tabValue === 2 && (
//           <Grid container spacing={2}>
//             <Grid item xs={12} md={5} sx={{width:'30%'}}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Talent Distribution by Role
//                   </Typography>
//                   <List>
//                     {roleData.map((role, i) => (
//                       <React.Fragment key={i}>
//                         <ListItem>
//                           <ListItemIcon>
//                             <Work />
//                           </ListItemIcon>
//                           <ListItemText primary={role.role} secondary={`${role.count} professionals`} />
//                           <Box sx={{ width: 100, height: 8, bgcolor: "grey.300", borderRadius: 1, overflow: "hidden" }}>
//                             <Box
//                               sx={{
//                                 width: `${(role.count / maxRoleCount) * 100}%`,
//                                 height: "100%",
//                                 bgcolor: "primary.main",
//                               }}
//                             />
//                           </Box>
//                         </ListItem>
//                         {i < roleData.length - 1 && <Divider variant="inset" component="li" />}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={4} sx={{width: '35%'}}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Talent Distribution by Certification
//                   </Typography>
//                   <List>
//                     {certificationData.map((cert, i) => (
//                       <React.Fragment key={i}>
//                         <ListItem>
//                           <ListItemIcon>
//                             <School />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={cert.certification}
//                             secondary={`${cert.count} certified professionals`}
//                           />
//                           <Box sx={{ width: 100, height: 8, bgcolor: "grey.300", borderRadius: 1, overflow: "hidden" }}>
//                             <Box
//                               sx={{
//                                 width: `${(cert.count / maxCertCount) * 100}%`,
//                                 height: "100%",
//                                 bgcolor: "secondary.main",
//                               }}
//                             />
//                           </Box>
//                         </ListItem>
//                         {i < certificationData.length - 1 && <Divider variant="inset" component="li" />}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>

//           <Grid item xs={12} md={3} sx={{width:'50%'}}>
//               <Card sx={{ backgroundColor: "#F1FFFA", width: "30%" }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     International Readiness Index
//                   </Typography>
//                   <Grid container spacing={2}>
//                     <Grid item xs={12}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="success.main">
//                           {readinessData?.english_proficiency_percentage ?? "0"}%
//                         </Typography>
//                         <Typography>English Proficiency</Typography>
//                       </Paper>
//                     </Grid>
//                     <Grid item xs={12}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="primary.main">
//                           {readinessData?.passport_ready_percentage ?? "0"}%
//                         </Typography>
//                         <Typography>Passport Ready</Typography>
//                       </Paper>
//                     </Grid>
//                     <Grid item xs={12}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="warning.main">
//                           {readinessData?.relocation_willing_percentage ?? "0"}%
//                         </Typography>
//                         <Typography>Relocation Willing</Typography>
//                       </Paper>
//                     </Grid>
//                   </Grid>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}
//       </Box>
//     </Layout>
//   )
// }

// export default TalentHeatmap

// import React, { useState, useEffect } from "react"
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Paper,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Button,
//   Tabs,
//   Tab,
//   Chip,
//   List,
//   ListItem,
//   ListItemText,
//   ListItemIcon,
//   Divider,
//   Avatar,
//   IconButton,
//   Tooltip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,f
// } from "@mui/material"
// import { School, Work, FilterList, CloudDownload, ZoomIn, ZoomOut, Refresh } from "@mui/icons-material"
// import Layout from "../common/Layout"
// import apiClient from "../../context/apiClient"

// // ✅ Google Maps imports
// import { GoogleMap, HeatmapLayer, useLoadScript } from "@react-google-maps/api"

// // Map container style
// const containerStyle = {
//   width: "100%",
//   height: "500px",
// }

// // Center on India
// const indiaCenter = { lat: 22.9734, lng: 78.6569 }

// // ✅ Hardcoded state → coordinates
// const stateCoordinates = {
//   Maharashtra: { lat: 19.7515, lng: 75.7139 },
//   Rajsthan: { lat: 27.0238, lng: 74.2179 },
//   Bihar: { lat: 25.0961, lng: 85.3131 },
// }

// function TalentHeatmap() {
//   const [tabValue, setTabValue] = useState(0)
//   const [roleFilter, setRoleFilter] = useState("all")
//   const [certificationFilter, setCertificationFilter] = useState("all")
//   const [zoomLevel, setZoomLevel] = useState(5)

//   const [institutionData, setInstitutionData] = useState([])
//   const [roleData, setRoleData] = useState([])
//   const [certificationData, setCertificationData] = useState([])
//   const [readinessData, setReadinessData] = useState(null)

//   const [filterOptions, setFilterOptions] = useState({ roles: [], certifications: [] })
//   const [stateHeatmapData, setStateHeatmapData] = useState([])
//   const [topStatesData, setTopStatesData] = useState([])
//   const [geoPoints, setGeoPoints] = useState([]) // ✅ For Google Heatmap

//   // Load Google Maps script
//   const { isLoaded } = useLoadScript({
//     googleMapsApiKey: "AIzaSyCJFtqpKTMxYbh3E3bnzQeDANkknX-O-gQ", // 🔑 replace with real key
//     libraries: ["visualization"], // Required for Heatmap
//   })

//   // Fetch filter options
//   useEffect(() => {
//     apiClient
//       .get("/talent-heatmap-filter-options/")
//       .then((response) => setFilterOptions(response.data))
//       .catch((error) => console.error("Error fetching filter options:", error))
//   }, [])

//   // Fetch data
//   useEffect(() => {
//     if (tabValue === 0) {
//       const params = new URLSearchParams()
//       if (roleFilter !== "all") params.append("role", roleFilter)
//       if (certificationFilter !== "all") params.append("certification", certificationFilter)
//       const queryString = params.toString()
//       const url = `/talent-heatmap/${queryString ? `?${queryString}` : ""}`

//       apiClient
//         .get(url)
//         .then((response) => {
//           setStateHeatmapData(response.data.heatmap_data)
//           setTopStatesData(response.data.top_states)

//           // ✅ Convert state + count into geo points
//           if (window.google) {
//             const points = response.data.heatmap_data.flatMap((item) => {
//               const coords = stateCoordinates[item.state]
//               if (!coords) return []
//               return Array(item.count).fill(new window.google.maps.LatLng(coords.lat, coords.lng))
//             })
//             setGeoPoints(points)
//           }
//         })
//         .catch((error) => {
//           console.error("Error fetching state-wise heatmap data:", error)
//           setStateHeatmapData([])
//           setTopStatesData([])
//           setGeoPoints([])
//         })
//     } else if (tabValue === 1) {
//       apiClient
//         .get("/talent-heatmap-institute-wise/")
//         .then((response) => setInstitutionData(response.data))
//         .catch((error) => console.error("Error fetching institution data:", error))
//     } else if (tabValue === 2) {
//       apiClient
//         .get("/talent_heatmap/RoleAndCertifications/")
//         .then((response) => {
//           setRoleData(response.data.talent_distribution_by_role)
//           setCertificationData(response.data.talent_distribution_by_certification)
//           setReadinessData(response.data.international_readiness_index)
//         })
//         .catch((error) => console.error("Error fetching role and certification data:", error))
//     }
//   }, [tabValue, roleFilter, certificationFilter])

//   // Handlers
//   const handleTabChange = (e, newValue) => setTabValue(newValue)
//   const handleRoleFilterChange = (e) => setRoleFilter(e.target.value)
//   const handleCertificationFilterChange = (e) => setCertificationFilter(e.target.value)

//   const handleZoomIn = () => setZoomLevel(Math.min(zoomLevel + 1, 12))
//   const handleZoomOut = () => setZoomLevel(Math.max(zoomLevel - 1, 3))
//   const handleResetZoom = () => setZoomLevel(5)

//   const maxRoleCount = roleData.length > 0 ? Math.max(...roleData.map((r) => r.count)) : 1
//   const maxCertCount = certificationData.length > 0 ? Math.max(...certificationData.map((c) => c.count)) : 1

//   return (
//     <Layout>
//       <Box sx={{ p: 3 }}>
//         {/* Header */}
//         <Paper sx={{ p: 2, mb: 3, backgroundColor: "background.paper", border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
//           <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
//             <Typography variant="h4" component="h1">
//               Talent Availability Heatmap
//             </Typography>
//             <Box sx={{ display: "flex", gap: 2 }}>
//               <Button variant="outlined" startIcon={<FilterList />} size="small">
//                 Advanced Filters
//               </Button>
//               <Button variant="outlined" startIcon={<CloudDownload />} size="small">
//                 Export Data
//               </Button>
//             </Box>
//           </Box>
//         </Paper>
//         {/* Tabs */}
//         <Paper sx={{ mb: 3, backgroundColor: "#F1FFFA" }}>
//           <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" centered>
//             <Tab label="State-wise Distribution" />
//             <Tab label="Institution-wise Distribution" />
//             <Tab label="Role & Certification" />
//           </Tabs>
//         </Paper>

//         {/* Tab 0 - State-wise */}
//         {tabValue === 0 && (
//           <Grid container spacing={3}>
//             {/* Map Section */}
//             <Grid item xs={12} md={8}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Box
//                     sx={{
//                       display: "flex",
//                       justifyContent: "space-between",
//                       alignItems: "center",
//                       mb: 2,
//                       width: "870px",
//                       height: "100%",
//                       backgroundColor: "#F1FFFA",
//                       borderRadius: "10px",
//                     }}
//                   >
//                     <Typography variant="h6">India AI Talent Heatmap</Typography>
//                     <Box sx={{ display: "flex", gap: 1 }}>
//                       <Tooltip title="Zoom In">
//                         <IconButton size="small" onClick={handleZoomIn}>
//                           <ZoomIn />
//                         </IconButton>
//                       </Tooltip>
//                       <Tooltip title="Zoom Out">
//                         <IconButton size="small" onClick={handleZoomOut}>
//                           <ZoomOut />
//                         </IconButton>
//                       </Tooltip>
//                       <Tooltip title="Reset View">
//                         <IconButton size="small" onClick={handleResetZoom}>
//                           <Refresh />
//                         </IconButton>
//                       </Tooltip>
//                     </Box>
//                   </Box>

//                   <Box sx={{ height: 500, borderRadius: 1, overflow: "hidden", border: "1px solid #ddd" }}>
//                     {isLoaded ? (
//                       <GoogleMap mapContainerStyle={containerStyle} center={indiaCenter} zoom={zoomLevel}>
//                         {geoPoints.length > 0 && (
//                           <HeatmapLayer data={geoPoints} options={{ radius: 40, opacity: 0.6 }} />
//                         )}
//                       </GoogleMap>
//                     ) : (
//                       <Typography sx={{ p: 2 }}>Loading Map...</Typography>
//                     )}
//                   </Box>
//                 </CardContent>
//               </Card>
//             </Grid>

//             {/* Filters + Top States */}
//             <Grid item xs={12} md={4}>
//               <Card sx={{ mb: 2, backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Filters
//                   </Typography>
//                   <FormControl fullWidth sx={{ mb: 2 }}>
//                     <InputLabel id="role-filter-label">Filter by Role</InputLabel>
//                     <Select
//                       labelId="role-filter-label"
//                       value={roleFilter}
//                       onChange={handleRoleFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Roles</MenuItem>
//                       {filterOptions.roles.map((role, i) => (
//                         <MenuItem key={i} value={role}>
//                           {role}
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                   <FormControl fullWidth>
//                     <InputLabel id="cert-filter-label">Filter by Certification</InputLabel>
//                     <Select
//                       labelId="cert-filter-label"
//                       value={certificationFilter}
//                       onChange={handleCertificationFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Certifications</MenuItem>
//                       {filterOptions.certifications.map((cert, i) => (
//                         <MenuItem key={i} value={cert}>
//                           {cert}
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                 </CardContent>
//               </Card>

//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Top States by Talent
//                   </Typography>
//                   <List dense>
//                     {topStatesData.slice(0, 10).map((s, index) => (
//                       <ListItem key={index}>
//                         <ListItemIcon>
//                           <Box sx={{ width: 16, height: 16, bgcolor: "primary.main", borderRadius: "50%" }} />
//                         </ListItemIcon>
//                         <ListItemText primary={s.state} secondary={`${s.count} professionals`} />
//                         <Chip label={`#${index + 1}`} size="small" color="primary" variant="outlined" />
//                       </ListItem>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}

//         {/* Tab 1 - Institutions */}
//         {tabValue === 1 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12}>
//               <Card sx={{ backgroundColor: "#F1FFFA" , width:"100%"}}>
//                 <CardContent sx={{ p: 0 }}>
//                   <Box sx={{ p: 2, width: "100%" }}>
//                     <Typography variant="h6" gutterBottom>
//                       Top Institutions by AI Talent Output
//                     </Typography>
//                   </Box>
//                   <TableContainer
//                     component={Paper}
//                     sx={{
//                       width: "100%",
//                       maxHeight: "calc(100vh - 280px)",
//                       borderRadius: 0,
//                     }}
//                   >
//                     <Table stickyHeader size="small" aria-label="Top institutions table">
//                       <TableHead>
//                         <TableRow>
//                           <TableCell width={80}>Rank</TableCell>
//                           <TableCell>Institution</TableCell>
//                           <TableCell align="right" width={180}>
//                             Graduates
//                           </TableCell>
//                         </TableRow>
//                       </TableHead>
//                       <TableBody>
//                         {institutionData.map((inst, i) => (
//                           <TableRow hover key={i}>
//                             <TableCell>#{i + 1}</TableCell>
//                             <TableCell>
//                               <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                                 <Avatar sx={{ bgcolor: "primary.main", width: 28, height: 28 }}>
//                                   <School fontSize="small" />
//                                 </Avatar>
//                                 <Typography variant="body2">{inst.institution}</Typography>
//                               </Box>
//                             </TableCell>
//                             <TableCell align="right">
//                               <Chip label={`${inst.graduates} graduates`} color="secondary" size="small" />
//                             </TableCell>
//                           </TableRow>
//                         ))}
//                       </TableBody>
//                     </Table>
//                   </TableContainer>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}

//         {/* Tab 2 - Role & Certification */}
//         {tabValue === 2 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12} md={4}>
//               <Card sx={{ backgroundColor: "#F1FFFA" }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Talent Distribution by Role
//                   </Typography>
//                   <List>
//                     {roleData.map((role, i) => (
//                       <React.Fragment key={i}>
//                         <ListItem>
//                           <ListItemIcon>
//                             <Work />
//                           </ListItemIcon>
//                           <ListItemText primary={role.role} secondary={`${role.count} professionals`} />
//                           <Box sx={{ width: 100, height: 8, bgcolor: "grey.300", borderRadius: 1, overflow: "hidden" }}>
//                             <Box
//                               sx={{
//                                 width: `${(role.count / maxRoleCount) * 100}%`,
//                                 height: "100%",
//                                 bgcolor: "primary.main",
//                               }}
//                             />
//                           </Box>
//                         </ListItem>
//                         {i < roleData.length - 1 && <Divider variant="inset" component="li" />}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={3}>
//               <Card sx={{ backgroundColor: "#F1FFFA", width: "60%" }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Talent Distribution by Certification
//                   </Typography>
//                   <List>
//                     {certificationData.map((cert, i) => (
//                       <React.Fragment key={i}>
//                         <ListItem>
//                           <ListItemIcon>
//                             <School />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={cert.certification}
//                             secondary={`${cert.count} certified professionals`}
//                           />
//                           <Box sx={{ width: 100, height: 8, bgcolor: "grey.300", borderRadius: 1, overflow: "hidden" }}>
//                             <Box
//                               sx={{
//                                 width: `${(cert.count / maxCertCount) * 100}%`,
//                                 height: "100%",
//                                 bgcolor: "secondary.main",
//                               }}
//                             />
//                           </Box>
//                         </ListItem>
//                         {i < certificationData.length - 1 && <Divider variant="inset" component="li" />}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={3}>
//               <Card sx={{ backgroundColor: "#F1FFFA", width: "30%" }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     International Readiness Index
//                   </Typography>
//                   <Grid container spacing={2}>
//                     <Grid item xs={12}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="success.main">
//                           {readinessData?.english_proficiency_percentage ?? "0"}%
//                         </Typography>
//                         <Typography>English Proficiency</Typography>
//                       </Paper>
//                     </Grid>
//                     <Grid item xs={12}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="primary.main">
//                           {readinessData?.passport_ready_percentage ?? "0"}%
//                         </Typography>
//                         <Typography>Passport Ready</Typography>
//                       </Paper>
//                     </Grid>
//                     <Grid item xs={12}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="warning.main">
//                           {readinessData?.relocation_willing_percentage ?? "0"}%
//                         </Typography>
//                         <Typography>Relocation Willing</Typography>
//                       </Paper>
//                     </Grid>
//                   </Grid>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}
//       </Box>
//     </Layout>
//   )
// }

// export default TalentHeatmap


// import React, { useState, useEffect } from "react";
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Paper,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Button,
//   Tabs,
//   Tab,
//   Chip,
//   List,
//   ListItem,
//   ListItemText,
//   ListItemIcon,
//   Divider,
//   Avatar,
//   IconButton,
//   Tooltip,
// } from "@mui/material";
// import {
//   School,
//   Work,
//   FilterList,
//   CloudDownload,
//   ZoomIn,
//   ZoomOut,
//   Refresh,
// } from "@mui/icons-material";
// import Layout from "../common/Layout";
// import apiClient from "../../context/apiClient";

// // ✅ Google Maps imports
// import { GoogleMap, HeatmapLayer, useLoadScript } from "@react-google-maps/api";

// // Map container style
// const containerStyle = {
//   width: "100%",
//   height: "500px",
// };

// // Center on India
// const indiaCenter = { lat: 22.9734, lng: 78.6569 };

// // ✅ Hardcoded state → coordinates
// const stateCoordinates = {
//   Maharashtra: { lat: 19.7515, lng: 75.7139 },
//   Rajsthan: { lat: 27.0238, lng: 74.2179 },
//   Bihar: { lat: 25.0961, lng: 85.3131 },
// };

// function TalentHeatmap() {
//   const [tabValue, setTabValue] = useState(0);
//   const [roleFilter, setRoleFilter] = useState("all");
//   const [certificationFilter, setCertificationFilter] = useState("all");
//   const [zoomLevel, setZoomLevel] = useState(5);

//   const [institutionData, setInstitutionData] = useState([]);
//   const [roleData, setRoleData] = useState([]);
//   const [certificationData, setCertificationData] = useState([]);
//   const [readinessData, setReadinessData] = useState(null);

//   const [filterOptions, setFilterOptions] = useState({ roles: [], certifications: [] });
//   const [stateHeatmapData, setStateHeatmapData] = useState([]);
//   const [topStatesData, setTopStatesData] = useState([]);
//   const [geoPoints, setGeoPoints] = useState([]); // ✅ For Google Heatmap

//   // Load Google Maps script
//   const { isLoaded } = useLoadScript({
//     googleMapsApiKey: "AIzaSyCJFtqpKTMxYbh3E3bnzQeDANkknX-O-gQ", // 🔑 replace with real key
//     libraries: ["visualization"], // Required for Heatmap
//   });

//   // Fetch filter options
//   useEffect(() => {
//     apiClient.get("/talent-heatmap-filter-options/")
//       .then(response => setFilterOptions(response.data))
//       .catch(error => console.error("Error fetching filter options:", error));
//   }, []);

//   // Fetch data
//   useEffect(() => {
//     if (tabValue === 0) {
//       const params = new URLSearchParams();
//       if (roleFilter !== "all") params.append("role", roleFilter);
//       if (certificationFilter !== "all") params.append("certification", certificationFilter);
//       const queryString = params.toString();
//       const url = `/talent-heatmap/${queryString ? `?${queryString}` : ""}`;

//       apiClient.get(url)
//         .then(response => {
//           setStateHeatmapData(response.data.heatmap_data);
//           setTopStatesData(response.data.top_states);

//           // ✅ Convert state + count into geo points
//           if (window.google) {
//             const points = response.data.heatmap_data.flatMap(item => {
//               const coords = stateCoordinates[item.state];
//               if (!coords) return [];
//               return Array(item.count).fill(
//                 new window.google.maps.LatLng(coords.lat, coords.lng)
//               );
//             });
//             setGeoPoints(points);
//           }
//         })
//         .catch(error => {
//           console.error("Error fetching state-wise heatmap data:", error);
//           setStateHeatmapData([]);
//           setTopStatesData([]);
//           setGeoPoints([]);
//         });
//     } else if (tabValue === 1) {
//       apiClient.get("/talent-heatmap-institute-wise/")
//         .then(response => setInstitutionData(response.data))
//         .catch(error => console.error("Error fetching institution data:", error));
//     } else if (tabValue === 2) {
//       apiClient.get("/talent_heatmap/RoleAndCertifications/")
//         .then(response => {
//           setRoleData(response.data.talent_distribution_by_role);
//           setCertificationData(response.data.talent_distribution_by_certification);
//           setReadinessData(response.data.international_readiness_index);
//         })
//         .catch(error => console.error("Error fetching role and certification data:", error));
//     }
//   }, [tabValue, roleFilter, certificationFilter]);

//   // Handlers
//   const handleTabChange = (e, newValue) => setTabValue(newValue);
//   const handleRoleFilterChange = (e) => setRoleFilter(e.target.value);
//   const handleCertificationFilterChange = (e) => setCertificationFilter(e.target.value);

//   const handleZoomIn = () => setZoomLevel(Math.min(zoomLevel + 1, 12));
//   const handleZoomOut = () => setZoomLevel(Math.max(zoomLevel - 1, 3));
//   const handleResetZoom = () => setZoomLevel(5);

//   const maxRoleCount = roleData.length > 0 ? Math.max(...roleData.map(r => r.count)) : 1;
//   const maxCertCount = certificationData.length > 0 ? Math.max(...certificationData.map(c => c.count)) : 1;

//   return (
//     <Layout>
//       <Box sx={{ p: 3 }}>
//         {/* Header */}
//         <Paper sx={{p:2, mb:3, backgroundColor:"#F1FFFA"}}>
//         <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
//           <Typography variant="h4" component="h1">Talent Availability Heatmap</Typography>
//           <Box sx={{ display: "flex", gap: 2 }}>
//             <Button variant="outlined" startIcon={<FilterList />} size="small">Advanced Filters</Button>
//             <Button variant="outlined" startIcon={<CloudDownload />} size="small">Export Data</Button>
//           </Box> 
//         </Box>
// </Paper>
//         {/* Tabs */}
//         <Paper sx={{ mb: 3, backgroundColor:"#F1FFFA"}}> 
//           <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" centered>
//             <Tab label="State-wise Distribution" />
//             <Tab label="Institution-wise Distribution" />
//             <Tab label="Role & Certification" />
//           </Tabs>
//         </Paper>

//         {/* Tab 0 - State-wise */}
//         {tabValue === 0 && (
//           <Grid container spacing={3}>
//             {/* Map Section */}
//             <Grid item xs={12} md={8}>
//               <Card sx={{backgroundColor:"#F1FFFA",}}>
//                 <CardContent>
//                   <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 , width:"870px", height:"100%", backgroundColor:"#F1FFFA", borderRadius:"10px"}}>
//                     <Typography variant="h6">India AI Talent Heatmap</Typography>
//                     <Box sx={{ display: "flex", gap: 1 }}>
//                       <Tooltip title="Zoom In"><IconButton size="small" onClick={handleZoomIn}><ZoomIn /></IconButton></Tooltip>
//                       <Tooltip title="Zoom Out"><IconButton size="small" onClick={handleZoomOut}><ZoomOut /></IconButton></Tooltip>
//                       <Tooltip title="Reset View"><IconButton size="small" onClick={handleResetZoom}><Refresh /></IconButton></Tooltip>
//                     </Box>
//                   </Box>

//                   <Box sx={{ height: 500, borderRadius: 1, overflow: "hidden", border: "1px solid #ddd" }}>
//                     {isLoaded ? (
//                       <GoogleMap
//                         mapContainerStyle={containerStyle}
//                         center={indiaCenter}
//                         zoom={zoomLevel}
//                       >
//                         {geoPoints.length > 0 && (
//                           <HeatmapLayer data={geoPoints} options={{ radius: 40, opacity: 0.6 }} />
//                         )}
//                       </GoogleMap>
//                     ) : (
//                       <Typography sx={{ p: 2 }}>Loading Map...</Typography>
//                     )}
//                   </Box>
//                 </CardContent>
//               </Card>
//             </Grid>

//             {/* Filters + Top States */}
//             <Grid item xs={12} md={4}>
//               <Card sx={{ mb: 2,backgroundColor:"#F1FFFA", }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>Filters</Typography>
//                   <FormControl fullWidth sx={{ mb: 2 }}>
//                     <InputLabel id="role-filter-label">Filter by Role</InputLabel>
//                     <Select
//                       labelId="role-filter-label"
//                       value={roleFilter}
//                       onChange={handleRoleFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Roles</MenuItem>
//                       {filterOptions.roles.map((role, i) => (
//                         <MenuItem key={i} value={role}>{role}</MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                   <FormControl fullWidth>
//                     <InputLabel id="cert-filter-label">Filter by Certification</InputLabel>
//                     <Select
//                       labelId="cert-filter-label"
//                       value={certificationFilter}
//                       onChange={handleCertificationFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Certifications</MenuItem>
//                       {filterOptions.certifications.map((cert, i) => (
//                         <MenuItem key={i} value={cert}>{cert}</MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                 </CardContent>
//               </Card>

//               <Card sx={{backgroundColor:"#F1FFFA", }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>Top States by Talent</Typography>
//                   <List dense>
//                     {topStatesData.slice(0, 10).map((s, index) => (
//                       <ListItem key={index}>
//                         <ListItemIcon>
//                           <Box sx={{ width: 16, height: 16, bgcolor: "primary.main", borderRadius: "50%" }} />
//                         </ListItemIcon>
//                         <ListItemText primary={s.state} secondary={`${s.count} professionals`} />
//                         <Chip label={`#${index + 1}`} size="small" color="primary" variant="outlined" />
//                       </ListItem>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}

//         {/* Tab 1 - Institutions */}
//         {tabValue === 1 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12}>
//               <Card sx={{backgroundColor:"#F1FFFA", }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>Top Institutions by AI Talent Output</Typography>
//                   <List>
//                     {institutionData.map((inst, i) => (
//                       <React.Fragment key={i}>
//                         <ListItem>
//                           <ListItemIcon><Avatar sx={{ bgcolor: "primary.main" }}><School /></Avatar></ListItemIcon>
//                           <ListItemText primary={inst.institution} secondary={`${inst.graduates} graduates`} />
//                           <Box sx={{ display: "flex", gap: 1 }}>
//                             <Chip label={`Rank #${i + 1}`} color="primary" variant="outlined" />
//                             <Chip label={`${inst.graduates} graduates`} color="secondary" />
//                           </Box>
//                         </ListItem>
//                         {i < institutionData.length - 1 && <Divider variant="inset" component="li" />}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}

//         {/* Tab 2 - Role & Certification */}
//         {tabValue === 2 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12} md={6}>
//               <Card sx={{backgroundColor:"#F1FFFA", }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>Talent Distribution by Role</Typography>
//                   <List>
//                     {roleData.map((role, i) => (
//                       <React.Fragment key={i}>
//                         <ListItem>
//                           <ListItemIcon><Work /></ListItemIcon>
//                           <ListItemText primary={role.role} secondary={`${role.count} professionals`} />
//                           <Box sx={{ width: 100, height: 8, bgcolor: "grey.300", borderRadius: 1, overflow: "hidden" }}>
//                             <Box sx={{ width: `${(role.count / maxRoleCount) * 100}%`, height: "100%", bgcolor: "primary.main" }} />
//                           </Box>
//                         </ListItem>
//                         {i < roleData.length - 1 && <Divider variant="inset" component="li" />}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={4}>
//               <Card sx={{backgroundColor:"#F1FFFA", }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>Talent Distribution by Certification</Typography>
//                   <List>
//                     {certificationData.map((cert, i) => (
//                       <React.Fragment key={i}>
//                         <ListItem>
//                           <ListItemIcon><School /></ListItemIcon>
//                           <ListItemText primary={cert.certification} secondary={`${cert.count} certified professionals`} />
//                           <Box sx={{ width: 100, height: 8, bgcolor: "grey.300", borderRadius: 1, overflow: "hidden" }}>
//                             <Box sx={{ width: `${(cert.count / maxCertCount) * 100}%`, height: "100%", bgcolor: "secondary.main" }} />
//                           </Box>
//                         </ListItem>
//                         {i < certificationData.length - 1 && <Divider variant="inset" component="li" />}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={6}>
//               <Card sx={{backgroundColor:"#F1FFFA", width:"30%" }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>International Readiness Index</Typography>
//                   <Grid container spacing={2}>
//                     <Grid item xs={12} md={4}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="success.main">{readinessData?.english_proficiency_percentage ?? "0"}%</Typography>
//                         <Typography>English Proficiency</Typography>
//                       </Paper>
//                     </Grid>
//                     <Grid item xs={12} md={4}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="primary.main">{readinessData?.passport_ready_percentage ?? "0"}%</Typography>
//                         <Typography>Passport Ready</Typography>
//                       </Paper>
//                     </Grid>
//                     <Grid item xs={12} md={4}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="warning.main">{readinessData?.relocation_willing_percentage ?? "0"}%</Typography>
//                         <Typography>Relocation Willing</Typography>
//                       </Paper>
//                     </Grid>
//                   </Grid>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}
//       </Box>
//     </Layout>
//   );
// }

// export default TalentHeatmap;


// import React, { useState, useEffect } from "react";
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Paper,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Button,
//   Tabs,
//   Tab,
//   Chip,
//   List,
//   ListItem,
//   ListItemText,
//   ListItemIcon,
//   Divider,
//   Avatar,
//   IconButton,
//   Tooltip,
// } from "@mui/material";
// import {
//   School,
//   Work,
//   FilterList,
//   CloudDownload,
//   ZoomIn,
//   ZoomOut,
//   Refresh,
// } from "@mui/icons-material";
// import Layout from "../common/Layout";
// import apiClient from "../../context/apiClient";

// // ✅ New imports for Google Maps
// import { GoogleMap, HeatmapLayer, useLoadScript } from "@react-google-maps/api";

// // Map container style
// const containerStyle = {
//   width: "100%",
//   height: "500px",
// };

// // Center on India
// const indiaCenter = { lat: 22.9734, lng: 78.6569 };

// function TalentHeatmap() {
//   const [tabValue, setTabValue] = useState(0);
//   const [roleFilter, setRoleFilter] = useState("all");
//   const [certificationFilter, setCertificationFilter] = useState("all");
//   const [selectedState, setSelectedState] = useState(null);
//   const [zoomLevel, setZoomLevel] = useState(5);

//   const [institutionData, setInstitutionData] = useState([]);
//   const [roleData, setRoleData] = useState([]);
//   const [certificationData, setCertificationData] = useState([]);
//   const [readinessData, setReadinessData] = useState(null);

//   const [filterOptions, setFilterOptions] = useState({ roles: [], certifications: [] });
//   const [stateHeatmapData, setStateHeatmapData] = useState([]);
//   const [topStatesData, setTopStatesData] = useState([]);
//   const [geoPoints, setGeoPoints] = useState([]); // ✅ For Google Heatmap

//   // Load Google Maps script
//   const { isLoaded } = useLoadScript({
//     googleMapsApiKey: "YOUR_GOOGLE_MAPS_API_KEY", // 🔑 Replace with your key
//     libraries: ["visualization"], // Required for Heatmap
//   });

//   // Fetch filter options
//   useEffect(() => {
//     apiClient.get("/talent-heatmap-filter-options/")
//       .then(response => setFilterOptions(response.data))
//       .catch(error => console.error("Error fetching filter options:", error));
//   }, []);

//   // Fetch data
//   useEffect(() => {
//     if (tabValue === 0) {
//       const params = new URLSearchParams();
//       if (roleFilter !== "all") params.append("role", roleFilter);
//       if (certificationFilter !== "all") params.append("certification", certificationFilter);
//       const queryString = params.toString();
//       const url = `/talent-heatmap/${queryString ? `?${queryString}` : ""}`;

//       apiClient.get(url)
//         .then(response => {
//           setStateHeatmapData(response.data.heatmap_data);
//           setTopStatesData(response.data.top_states);

//           // ✅ Convert API lat/lng to Google Maps LatLng objects
//           if (window.google) {
//             const points = response.data.heatmap_data
//               .filter(d => d.lat && d.lng)
//               .map(d => new window.google.maps.LatLng(d.lat, d.lng));
//             setGeoPoints(points);
//           }
//         })
//         .catch(error => {
//           console.error("Error fetching state-wise heatmap data:", error);
//           setStateHeatmapData([]);
//           setTopStatesData([]);
//           setGeoPoints([]);
//         });
//     } else if (tabValue === 1) {
//       apiClient.get("/talent-heatmap-institute-wise/")
//         .then(response => setInstitutionData(response.data))
//         .catch(error => console.error("Error fetching institution data:", error));
//     } else if (tabValue === 2) {
//       apiClient.get("/talent_heatmap/RoleAndCertifications/")
//         .then(response => {
//           setRoleData(response.data.talent_distribution_by_role);
//           setCertificationData(response.data.talent_distribution_by_certification);
//           setReadinessData(response.data.international_readiness_index);
//         })
//         .catch(error => console.error("Error fetching role and certification data:", error));
//     }
//   }, [tabValue, roleFilter, certificationFilter]);

//   // Handlers
//   const handleTabChange = (e, newValue) => setTabValue(newValue);
//   const handleRoleFilterChange = (e) => setRoleFilter(e.target.value);
//   const handleCertificationFilterChange = (e) => setCertificationFilter(e.target.value);

//   const handleZoomIn = () => setZoomLevel(Math.min(zoomLevel + 1, 12));
//   const handleZoomOut = () => setZoomLevel(Math.max(zoomLevel - 1, 3));
//   const handleResetZoom = () => setZoomLevel(5);

//   const maxRoleCount = roleData.length > 0 ? Math.max(...roleData.map(r => r.count)) : 1;
//   const maxCertCount = certificationData.length > 0 ? Math.max(...certificationData.map(c => c.count)) : 1;

//   return (
//     <Layout>
//       <Box sx={{ p: 3 }}>
//         {/* Header */}
//         <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
//           <Typography variant="h4" component="h1">Talent Availability Heatmap</Typography>
//           <Box sx={{ display: "flex", gap: 2 }}>
//             <Button variant="outlined" startIcon={<FilterList />} size="small">Advanced Filters</Button>
//             <Button variant="outlined" startIcon={<CloudDownload />} size="small">Export Data</Button>
//           </Box>
//         </Box>

//         {/* Tabs */}
//         <Paper sx={{ mb: 3 }}>
//           <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" centered>
//             <Tab label="State-wise Distribution" />
//             <Tab label="Institution-wise Distribution" />
//             <Tab label="Role & Certification" />
//           </Tabs>
//         </Paper>

//         {/* Tab 0 - State-wise */}
//         {tabValue === 0 && (
//           <Grid container spacing={3}>
//             {/* Map Section */}
//             <Grid item xs={12} md={8}>
//               <Card>
//                 <CardContent>
//                   <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
//                     <Typography variant="h6">India AI Talent Heatmap</Typography>
//                     <Box sx={{ display: "flex", gap: 1 }}>
//                       <Tooltip title="Zoom In"><IconButton size="small" onClick={handleZoomIn}><ZoomIn /></IconButton></Tooltip>
//                       <Tooltip title="Zoom Out"><IconButton size="small" onClick={handleZoomOut}><ZoomOut /></IconButton></Tooltip>
//                       <Tooltip title="Reset View"><IconButton size="small" onClick={handleResetZoom}><Refresh /></IconButton></Tooltip>
//                     </Box>
//                   </Box>

//                   <Box sx={{ height: 500, borderRadius: 1, overflow: "hidden", border: "1px solid #ddd" }}>
//                     {isLoaded ? (
//                       <GoogleMap
//                         mapContainerStyle={containerStyle}
//                         center={indiaCenter}
//                         zoom={zoomLevel}
//                       >
//                         {geoPoints.length > 0 && (
//                           <HeatmapLayer data={geoPoints} options={{ radius: 40, opacity: 0.6 }} />
//                         )}
//                       </GoogleMap>
//                     ) : (
//                       <Typography sx={{ p: 2 }}>Loading Map...</Typography>
//                     )}
//                   </Box>
//                 </CardContent>
//               </Card>
//             </Grid>

//             {/* Filters + Top States */}
//             <Grid item xs={12} md={4}>
//               <Card sx={{ mb: 2 }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>Filters</Typography>
//                   <FormControl fullWidth sx={{ mb: 2 }}>
//                     <InputLabel id="role-filter-label">Filter by Role</InputLabel>
//                     <Select
//                       labelId="role-filter-label"
//                       value={roleFilter}
//                       onChange={handleRoleFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Roles</MenuItem>
//                       {filterOptions.roles.map((role, i) => (
//                         <MenuItem key={i} value={role}>{role}</MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                   <FormControl fullWidth>
//                     <InputLabel id="cert-filter-label">Filter by Certification</InputLabel>
//                     <Select
//                       labelId="cert-filter-label"
//                       value={certificationFilter}
//                       onChange={handleCertificationFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Certifications</MenuItem>
//                       {filterOptions.certifications.map((cert, i) => (
//                         <MenuItem key={i} value={cert}>{cert}</MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                 </CardContent>
//               </Card>

//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>Top States by Talent</Typography>
//                   <List dense>
//                     {topStatesData.slice(0, 10).map((s, index) => (
//                       <ListItem key={index}>
//                         <ListItemIcon>
//                           <Box sx={{ width: 16, height: 16, bgcolor: "primary.main", borderRadius: "50%" }} />
//                         </ListItemIcon>
//                         <ListItemText primary={s.state} secondary={`${s.count} professionals`} />
//                         <Chip label={`#${index + 1}`} size="small" color="primary" variant="outlined" />
//                       </ListItem>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}

//         {/* Tab 1 - Institutions */}
//         {tabValue === 1 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12}>
//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>Top Institutions by AI Talent Output</Typography>
//                   <List>
//                     {institutionData.map((inst, i) => (
//                       <React.Fragment key={i}>
//                         <ListItem>
//                           <ListItemIcon><Avatar sx={{ bgcolor: "primary.main" }}><School /></Avatar></ListItemIcon>
//                           <ListItemText primary={inst.institution} secondary={`${inst.graduates} graduates`} />
//                           <Box sx={{ display: "flex", gap: 1 }}>
//                             <Chip label={`Rank #${i + 1}`} color="primary" variant="outlined" />
//                             <Chip label={`${inst.graduates} graduates`} color="secondary" />
//                           </Box>
//                         </ListItem>
//                         {i < institutionData.length - 1 && <Divider variant="inset" component="li" />}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}

//         {/* Tab 2 - Role & Certification */}
//         {tabValue === 2 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12} md={6}>
//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>Talent Distribution by Role</Typography>
//                   <List>
//                     {roleData.map((role, i) => (
//                       <React.Fragment key={i}>
//                         <ListItem>
//                           <ListItemIcon><Work /></ListItemIcon>
//                           <ListItemText primary={role.role} secondary={`${role.count} professionals`} />
//                           <Box sx={{ width: 100, height: 8, bgcolor: "grey.300", borderRadius: 1, overflow: "hidden" }}>
//                             <Box sx={{ width: `${(role.count / maxRoleCount) * 100}%`, height: "100%", bgcolor: "primary.main" }} />
//                           </Box>
//                         </ListItem>
//                         {i < roleData.length - 1 && <Divider variant="inset" component="li" />}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={6}>
//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>Talent Distribution by Certification</Typography>
//                   <List>
//                     {certificationData.map((cert, i) => (
//                       <React.Fragment key={i}>
//                         <ListItem>
//                           <ListItemIcon><School /></ListItemIcon>
//                           <ListItemText primary={cert.certification} secondary={`${cert.count} certified professionals`} />
//                           <Box sx={{ width: 100, height: 8, bgcolor: "grey.300", borderRadius: 1, overflow: "hidden" }}>
//                             <Box sx={{ width: `${(cert.count / maxCertCount) * 100}%`, height: "100%", bgcolor: "secondary.main" }} />
//                           </Box>
//                         </ListItem>
//                         {i < certificationData.length - 1 && <Divider variant="inset" component="li" />}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12}>
//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>International Readiness Index</Typography>
//                   <Grid container spacing={2}>
//                     <Grid item xs={12} md={4}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="success.main">{readinessData?.english_proficiency_percentage ?? "0"}%</Typography>
//                         <Typography>English Proficiency</Typography>
//                       </Paper>
//                     </Grid>
//                     <Grid item xs={12} md={4}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="primary.main">{readinessData?.passport_ready_percentage ?? "0"}%</Typography>
//                         <Typography>Passport Ready</Typography>
//                       </Paper>
//                     </Grid>
//                     <Grid item xs={12} md={4}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="warning.main">{readinessData?.relocation_willing_percentage ?? "0"}%</Typography>
//                         <Typography>Relocation Willing</Typography>
//                       </Paper>
//                     </Grid>
//                   </Grid>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}
//       </Box>
//     </Layout>
//   );
// }

// export default TalentHeatmap;

// import React, { useState, useEffect } from "react";
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Paper,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Button,
//   Tabs,
//   Tab,
//   Chip,
//   List,
//   ListItem,
//   ListItemText,
//   ListItemIcon,
//   Divider,
//   Avatar,
//   IconButton,
//   Tooltip,
// } from "@mui/material";
// import {
//   School,
//   Work,
//   FilterList,
//   CloudDownload,
//   ZoomIn,
//   ZoomOut,
//   Refresh,
// } from "@mui/icons-material";
// import Layout from "../common/Layout";
// import apiClient from "../../context/apiClient";

// // Mock data for India states - used for static properties like color and map positioning
// const indiaStates = [
//   { id: "AP", name: "Andhra Pradesh", talent: 1250, color: "#FF5252" },
//   { id: "AR", name: "Arunachal Pradesh", talent: 180, color: "#FFA726" },
//   { id: "AS", name: "Assam", talent: 450, color: "#FFEE58" },
//   { id: "BR", name: "Bihar", talent: 780, color: "#66BB6A" },
//   { id: "CG", name: "Chhattisgarh", talent: 320, color: "#26C6DA" },
//   { id: "GA", name: "Goa", talent: 150, color: "#42A5F5" },
//   { id: "GJ", name: "Gujarat", talent: 1450, color: "#7E57C2" },
//   { id: "HR", name: "Haryana", talent: 980, color: "#EC407A" },
//   { id: "HP", name: "Himachal Pradesh", talent: 280, color: "#AB47BC" },
//   { id: "JH", name: "Jharkhand", talent: 420, color: "#26A69A" },
//   { id: "KA", name: "Karnataka", talent: 3200, color: "#EF5350" },
//   { id: "KL", name: "Kerala", talent: 1850, color: "#FFA726" },
//   { id: "MP", name: "Madhya Pradesh", talent: 950, color: "#FFEE58" },
//   { id: "MH", name: "Maharashtra", talent: 3800, color: "#66BB6A" },
//   { id: "MN", name: "Manipur", talent: 120, color: "#26C6DA" },
//   { id: "ML", name: "Meghalaya", talent: 90, color: "#42A5F5" },
//   { id: "MZ", name: "Mizoram", talent: 70, color: "#7E57C2" },
//   { id: "NL", name: "Nagaland", talent: 85, color: "#EC407A" },
//   { id: "OD", name: "Odisha", talent: 680, color: "#AB47BC" },
//   { id: "PB", name: "Punjab", talent: 1050, color: "#26A69A" },
//   { id: "RJ", name: "Rajasthan", talent: 1150, color: "#EF5350" },
//   { id: "SK", name: "Sikkim", talent: 65, color: "#FFA726" },
//   { id: "TN", name: "Tamil Nadu", talent: 2950, color: "#FFEE58" },
//   { id: "TG", name: "Telangana", talent: 2450, color: "#66BB6A" },
//   { id: "TR", name: "Tripura", talent: 110, color: "#26C6DA" },
//   { id: "UP", name: "Uttar Pradesh", talent: 2250, color: "#42A5F5" },
//   { id: "UK", name: "Uttarakhand", talent: 380, color: "#7E57C2" },
//   { id: "WB", name: "West Bengal", talent: 1750, color: "#EC407A" },
//   { id: "DL", name: "Delhi", talent: 1950, color: "#AB47BC" },
// ];

// function TalentHeatmap() {
//   const [tabValue, setTabValue] = useState(0);
//   const [roleFilter, setRoleFilter] = useState("all");
//   const [certificationFilter, setCertificationFilter] = useState("all");
//   const [selectedState, setSelectedState] = useState(null);
//   const [zoomLevel, setZoomLevel] = useState(1);
//   const [institutionData, setInstitutionData] = useState([]);
//   const [roleData, setRoleData] = useState([]);
//   const [certificationData, setCertificationData] = useState([]);
//   const [readinessData, setReadinessData] = useState(null);

//   // State for State-wise Distribution tab
//   const [filterOptions, setFilterOptions] = useState({ roles: [], certifications: [] });
//   const [stateHeatmapData, setStateHeatmapData] = useState([]);
//   const [topStatesData, setTopStatesData] = useState([]);

//   // Effect for fetching filter options once
//   useEffect(() => {
//     apiClient.get("/talent-heatmap-filter-options/")
//       .then(response => {
//         setFilterOptions(response.data);
//       })
//       .catch(error => {
//         console.error("Error fetching filter options:", error);
//       });
//   }, []);

//   // Effect for fetching data based on active tab and filters
//   useEffect(() => {
//     if (tabValue === 0) {
//       const params = new URLSearchParams();
//       if (roleFilter !== 'all') {
//         params.append('role', roleFilter);
//       }
//       if (certificationFilter !== 'all') {
//         params.append('certification', certificationFilter);
//       }
//       const queryString = params.toString();
//       const url = `/talent-heatmap/${queryString ? `?${queryString}` : ''}`;
      
//       apiClient.get(url)
//         .then(response => {
//           setStateHeatmapData(response.data.heatmap_data);
//           setTopStatesData(response.data.top_states);
//         })
//         .catch(error => {
//           console.error("Error fetching state-wise heatmap data:", error);
//           setStateHeatmapData([]);
//           setTopStatesData([]);
//         });
//     } else if (tabValue === 1) {
//       apiClient
//         .get("/talent-heatmap-institute-wise/")
//         .then((response) => {
//           setInstitutionData(response.data);
//         })
//         .catch((error) => {
//           console.error("Error fetching institution data:", error);
//         });
//     } else if (tabValue === 2) {
//       apiClient
//         .get("/talent_heatmap/RoleAndCertifications/")
//         .then((response) => {
//           setRoleData(response.data.talent_distribution_by_role);
//           setCertificationData(response.data.talent_distribution_by_certification);
//           setReadinessData(response.data.international_readiness_index);
//         })
//         .catch((error) => {
//           console.error("Error fetching role and certification data:", error);
//         });
//     }
//   }, [tabValue, roleFilter, certificationFilter]);

//   const handleTabChange = (event, newValue) => {
//     setTabValue(newValue);
//   };

//   const handleRoleFilterChange = (event) => {
//     setRoleFilter(event.target.value);
//   };

//   const handleCertificationFilterChange = (event) => {
//     setCertificationFilter(event.target.value);
//   };

//   const handleStateClick = (state) => {
//     setSelectedState(state);
//   };

//   const handleZoomIn = () => {
//     setZoomLevel(Math.min(zoomLevel + 0.2, 2));
//   };

//   const handleZoomOut = () => {
//     setZoomLevel(Math.max(zoomLevel - 0.2, 0.6));
//   };

//   const handleResetZoom = () => {
//     setZoomLevel(1);
//     setSelectedState(null);
//   };

//   // Combine static state data with dynamic API data for map rendering
//   const heatmapDataMap = new Map(stateHeatmapData.map(item => [item.state, item.count]));
//   const displayStates = indiaStates.map(state => ({
//     ...state,
//     talent: heatmapDataMap.get(state.name) || 0,
//   }));
  
//   const maxTalent = displayStates.length > 0 ? Math.max(1, ...displayStates.map(state => state.talent)) : 1;

//   const getColorIntensity = (talent) => {
//     if (maxTalent === 0) return 0.2;
//     const intensity = Math.min(0.2 + (talent / maxTalent) * 0.8, 1);
//     return intensity;
//   };

//   const maxRoleCount = roleData.length > 0 ? Math.max(...roleData.map((r) => r.count)) : 1;
//   const maxCertCount = certificationData.length > 0 ? Math.max(...certificationData.map((c) => c.count)) : 1;

//   return (
//     <Layout>
//       <Box sx={{ p: 3 }}>
//         <Box
//           sx={{
//             display: "flex",
//             justifyContent: "space-between",
//             alignItems: "center",
//             mb: 3,
//           }}
//         >
//           <Typography variant="h4" component="h1" gutterBottom>
//             Talent Availability Heatmap
//           </Typography>
//           <Box sx={{ display: "flex", gap: 2 }}>
//             <Button variant="outlined" startIcon={<FilterList />} size="small">
//               Advanced Filters
//             </Button>
//             <Button
//               variant="outlined"
//               startIcon={<CloudDownload />}
//               size="small"
//             >
//               Export Data
//             </Button>
//           </Box>
//         </Box>

//         <Paper sx={{ mb: 3 }}>
//           <Tabs
//             value={tabValue}
//             onChange={handleTabChange}
//             indicatorColor="primary"
//             textColor="primary"
//             centered
//           >
//             <Tab label="State-wise Distribution" />
//             <Tab label="Institution-wise Distribution" />
//             <Tab label="Role & Certification" />
//           </Tabs>
//         </Paper>

//         {tabValue === 0 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12} md={8}>
//               <Card>
//                 <CardContent>
//                   <Box
//                     sx={{
//                       display: "flex",
//                       justifyContent: "space-between",
//                       alignItems: "center",
//                       mb: 2,
//                     }}
//                   >
//                     <Typography variant="h6">
//                       India AI Talent Heatmap
//                     </Typography>
//                     <Box sx={{ display: "flex", gap: 1 }}>
//                       <Tooltip title="Zoom In">
//                         <IconButton size="small" onClick={handleZoomIn}>
//                           <ZoomIn />
//                         </IconButton>
//                       </Tooltip>
//                       <Tooltip title="Zoom Out">
//                         <IconButton size="small" onClick={handleZoomOut}>
//                           <ZoomOut />
//                         </IconButton>
//                       </Tooltip>
//                       <Tooltip title="Reset View">
//                         <IconButton size="small" onClick={handleResetZoom}>
//                           <Refresh />
//                         </IconButton>
//                       </Tooltip>
//                     </Box>
//                   </Box>

//                   <Box
//                     sx={{
//                       height: 500,
//                       bgcolor: "#f5f5f5",
//                       borderRadius: 1,
//                       position: "relative",
//                       overflow: "hidden",
//                       border: "1px solid #ddd",
//                     }}
//                   >
//                     <Box
//                       sx={{
//                         position: "absolute",
//                         top: "50%",
//                         left: "50%",
//                         transform: `translate(-50%, -50%) scale(${zoomLevel})`,
//                         width: "80%",
//                         height: "80%",
//                         transition: "transform 0.3s ease",
//                       }}
//                     >
//                       <Box
//                         component="img"
//                         src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c3/India_map_blank.svg/800px-India_map_blank.svg.png"
//                         sx={{
//                           width: "100%",
//                           height: "100%",
//                           objectFit: "contain",
//                           opacity: 0.7,
//                         }}
//                       />
//                       {displayStates.map((state) => (
//                         state.talent > 0 && (
//                           <Tooltip
//                             key={state.id}
//                             title={`${state.name}: ${state.talent} AI Professionals`}
//                           >
//                             <Box
//                               sx={{
//                                 position: "absolute",
//                                 width: 20,
//                                 height: 20,
//                                 borderRadius: "50%",
//                                 bgcolor: state.color,
//                                 opacity: getColorIntensity(state.talent),
//                                 border:
//                                   selectedState?.id === state.id
//                                     ? "2px solid #000"
//                                     : "none",
//                                 cursor: "pointer",
//                                 top: `${Math.random() * 70 + 10}%`,
//                                 left: `${Math.random() * 70 + 10}%`,
//                                 transform: "translate(-50%, -50%)",
//                                 transition: "all 0.2s ease",
//                                 "&:hover": {
//                                   transform: "translate(-50%, -50%) scale(1.2)",
//                                   boxShadow: "0 0 0 2px rgba(0,0,0,0.2)",
//                                 },
//                               }}
//                               onClick={() => handleStateClick(state)}
//                             />
//                           </Tooltip>
//                         )
//                       ))}
//                     </Box>

//                     <Box
//                       sx={{
//                         position: "absolute",
//                         bottom: 16,
//                         left: 16,
//                         bgcolor: "rgba(255,255,255,0.9)",
//                         p: 2,
//                         borderRadius: 1,
//                         minWidth: 200,
//                       }}
//                     >
//                       <Typography variant="subtitle2" gutterBottom>
//                         Legend
//                       </Typography>
//                       <Box
//                         sx={{ display: "flex", alignItems: "center", mb: 1 }}
//                       >
//                         <Box
//                           sx={{
//                             width: 16,
//                             height: 16,
//                             bgcolor: "#FF5252",
//                             opacity: 0.3,
//                             mr: 1,
//                             borderRadius: "50%",
//                           }}
//                         />
//                         <Typography variant="caption">Low</Typography>
//                       </Box>
//                       <Box
//                         sx={{ display: "flex", alignItems: "center", mb: 1 }}
//                       >
//                         <Box
//                           sx={{
//                             width: 16,
//                             height: 16,
//                             bgcolor: "#FF5252",
//                             opacity: 0.6,
//                             mr: 1,
//                             borderRadius: "50%",
//                           }}
//                         />
//                         <Typography variant="caption">
//                           Medium
//                         </Typography>
//                       </Box>
//                       <Box sx={{ display: "flex", alignItems: "center" }}>
//                         <Box
//                           sx={{
//                             width: 16,
//                             height: 16,
//                             bgcolor: "#FF5252",
//                             opacity: 1,
//                             mr: 1,
//                             borderRadius: "50%",
//                           }}
//                         />
//                         <Typography variant="caption">High</Typography>
//                       </Box>
//                     </Box>
//                   </Box>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={4}>
//               <Card sx={{ mb: 2 }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Filters
//                   </Typography>
//                   <FormControl fullWidth sx={{ mb: 2 }}>
//                     <InputLabel id="role-filter-label">
//                       Filter by Role
//                     </InputLabel>
//                     <Select
//                       labelId="role-filter-label"
//                       value={roleFilter}
//                       label="Filter by Role"
//                       onChange={handleRoleFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Roles</MenuItem>
//                       {filterOptions.roles.map((role, index) => (
//                         <MenuItem key={index} value={role}>{role}</MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>

//                   <FormControl fullWidth>
//                     <InputLabel id="cert-filter-label">
//                       Filter by Certification
//                     </InputLabel>
//                     <Select
//                       labelId="cert-filter-label"
//                       value={certificationFilter}
//                       label="Filter by Certification"
//                       onChange={handleCertificationFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Certifications</MenuItem>
//                       {filterOptions.certifications.map((cert, index) => (
//                         <MenuItem key={index} value={cert}>{cert}</MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                 </CardContent>
//               </Card>

//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Top States by Talent
//                   </Typography>
//                   <List dense>
//                     {topStatesData.slice(0, 10).map((stateData, index) => {
//                       const fullState = displayStates.find(s => s.name === stateData.state);
//                       if (!fullState) return null;

//                       return (
//                         <ListItem
//                           key={fullState.id}
//                           button
//                           onClick={() => handleStateClick(fullState)}
//                           selected={selectedState?.id === fullState.id}
//                         >
//                           <ListItemIcon>
//                             <Box
//                               sx={{
//                                 width: 16,
//                                 height: 16,
//                                 bgcolor: fullState.color,
//                                 opacity: getColorIntensity(stateData.count),
//                                 borderRadius: "50%",
//                               }}
//                             />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={stateData.state}
//                             secondary={`${stateData.count} professionals`}
//                           />
//                           <Chip
//                             label={`#${index + 1}`}
//                             size="small"
//                             color="primary"
//                             variant="outlined"
//                           />
//                         </ListItem>
//                       )
//                     })}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>

//             {selectedState && (
//               <Grid item xs={12}>
//                 <Card>
//                   <CardContent>
//                     <Typography variant="h6" gutterBottom>
//                       {selectedState.name} - Detailed Information
//                     </Typography>
//                     <Grid container spacing={2}>
//                       <Grid item xs={12} md={3}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h4" color="primary">
//                             {selectedState.talent}
//                           </Typography>
//                           <Typography variant="subtitle2">
//                             Total AI Professionals
//                           </Typography>
//                         </Paper>
//                       </Grid>
//                       <Grid item xs={12} md={3}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h4" color="secondary">
//                             {Math.floor(selectedState.talent * 0.65)}
//                           </Typography>
//                           <Typography variant="subtitle2">
//                             NAISG Certified
//                           </Typography>
//                         </Paper>
//                       </Grid>
//                       <Grid item xs={12} md={3}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h4" color="success.main">
//                             {Math.floor(selectedState.talent * 0.32)}
//                           </Typography>
//                           <Typography variant="subtitle2">
//                             International Ready
//                           </Typography>
//                         </Paper>
//                       </Grid>
//                       <Grid item xs={12} md={3}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h4" color="warning.main">
//                             {Math.floor(selectedState.talent * 0.18)}
//                           </Typography>
//                           <Typography variant="subtitle2">
//                             Currently Available
//                           </Typography>
//                         </Paper>
//                       </Grid>
//                     </Grid>
//                   </CardContent>
//                 </Card>
//               </Grid>
//             )}
//           </Grid>
//         )}

//         {tabValue === 1 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12}>
//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Top Institutions by AI Talent Output
//                   </Typography>
//                   <List>
//                     {institutionData.map((institution, index) => (
//                       <React.Fragment key={index}>
//                         <ListItem>
//                           <ListItemIcon>
//                             <Avatar sx={{ bgcolor: "primary.main" }}>
//                               <School />
//                             </Avatar>
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={institution.institution}
//                             secondary={`${institution.graduates} graduates`}
//                           />
//                           <Box
//                             sx={{
//                               display: "flex",
//                               alignItems: "center",
//                               gap: 1,
//                             }}
//                           >
//                             <Chip
//                               label={`Rank #${index + 1}`}
//                               color="primary"
//                               variant="outlined"
//                             />
//                             <Chip
//                               label={`${institution.graduates} graduates`}
//                               color="secondary"
//                             />
//                           </Box>
//                         </ListItem>
//                         {index < institutionData.length - 1 && (
//                           <Divider variant="inset" component="li" />
//                         )}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}

//         {tabValue === 2 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12} md={6}>
//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Talent Distribution by Role
//                   </Typography>
//                   <List>
//                     {roleData.map((role, index) => (
//                       <React.Fragment key={index}>
//                         <ListItem>
//                           <ListItemIcon>
//                             <Work />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={role.role}
//                             secondary={`${role.count} professionals`}
//                           />
//                           <Box
//                             sx={{
//                               width: 100,
//                               height: 8,
//                               bgcolor: "grey.300",
//                               borderRadius: 1,
//                               overflow: "hidden",
//                             }}
//                           >
//                             <Box
//                               sx={{
//                                 width: `${(role.count / maxRoleCount) * 100}%`,
//                                 height: "100%",
//                                 bgcolor: "primary.main",
//                               }}
//                             />
//                           </Box>
//                         </ListItem>
//                         {index < roleData.length - 1 && (
//                           <Divider variant="inset" component="li" />
//                         )}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={6}>
//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Talent Distribution by Certification
//                   </Typography>
//                   <List>
//                     {certificationData.map((cert, index) => (
//                       <React.Fragment key={index}>
//                         <ListItem>
//                           <ListItemIcon>
//                             <School />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={cert.certification}
//                             secondary={`${cert.count} certified professionals`}
//                           />
//                           <Box
//                             sx={{
//                               width: 100,
//                               height: 8,
//                               bgcolor: "grey.300",
//                               borderRadius: 1,
//                               overflow: "hidden",
//                             }}
//                           >
//                             <Box
//                               sx={{
//                                 width: `${(cert.count / maxCertCount) * 100}%`,
//                                 height: "100%",
//                                 bgcolor: "secondary.main",
//                               }}
//                             />
//                           </Box>
//                         </ListItem>
//                         {index < certificationData.length - 1 && (
//                           <Divider variant="inset" component="li" />
//                         )}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12}>
//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     International Readiness Index
//                   </Typography>
//                   <Grid container spacing={2}>
//                     <Grid item xs={12} md={4}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="success.main">
//                           {readinessData?.english_proficiency_percentage ?? "0"}
//                           %
//                         </Typography>
//                         <Typography variant="subtitle1">
//                           English Proficiency
//                         </Typography>
//                         <Typography variant="body2" color="textSecondary">
//                           Professionals with advanced English skills
//                         </Typography>
//                       </Paper>
//                     </Grid>

//                     <Grid item xs={12} md={4}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="primary.main">
//                           {readinessData?.passport_ready_percentage ?? "0"}%
//                         </Typography>
//                         <Typography variant="subtitle1">
//                           Passport Ready
//                         </Typography>
//                         <Typography variant="body2" color="textSecondary">
//                           Professionals with valid passports
//                         </Typography>
//                       </Paper>
//                     </Grid>

//                     <Grid item xs={12} md={4}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="warning.main">
//                           {readinessData?.relocation_willing_percentage ?? "0"}%
//                         </Typography>
//                         <Typography variant="subtitle1">
//                           Relocation Willing
//                         </Typography>
//                         <Typography variant="body2" color="textSecondary">
//                           Professionals open to international relocation
//                         </Typography>
//                       </Paper>
//                     </Grid>
//                   </Grid>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}
//       </Box>
//     </Layout>
//   );
// }

// export default TalentHeatmap;
// import React, { useState, useEffect } from "react";
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Paper,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Button,
//   Tabs,
//   Tab,
//   Chip,
//   List,
//   ListItem,
//   ListItemText,
//   ListItemIcon,
//   Divider,
//   Avatar,
//   IconButton,
//   Tooltip,
// } from "@mui/material";
// import {
//   School,
//   Work,
//   FilterList,
//   CloudDownload,
//   ZoomIn,
//   ZoomOut,
//   Refresh,
// } from "@mui/icons-material";
// import Layout from "../common/Layout";
// import apiClient from "../../context/apiClient";

// // Mock data for India states
// const indiaStates = [
//   { id: "AP", name: "Andhra Pradesh", talent: 1250, color: "#FF5252" },
//   { id: "AR", name: "Arunachal Pradesh", talent: 180, color: "#FFA726" },
//   { id: "AS", name: "Assam", talent: 450, color: "#FFEE58" },
//   { id: "BR", name: "Bihar", talent: 780, color: "#66BB6A" },
//   { id: "CG", name: "Chhattisgarh", talent: 320, color: "#26C6DA" },
//   { id: "GA", name: "Goa", talent: 150, color: "#42A5F5" },
//   { id: "GJ", name: "Gujarat", talent: 1450, color: "#7E57C2" },
//   { id: "HR", name: "Haryana", talent: 980, color: "#EC407A" },
//   { id: "HP", name: "Himachal Pradesh", talent: 280, color: "#AB47BC" },
//   { id: "JH", name: "Jharkhand", talent: 420, color: "#26A69A" },
//   { id: "KA", name: "Karnataka", talent: 3200, color: "#EF5350" },
//   { id: "KL", name: "Kerala", talent: 1850, color: "#FFA726" },
//   { id: "MP", name: "Madhya Pradesh", talent: 950, color: "#FFEE58" },
//   { id: "MH", name: "Maharashtra", talent: 3800, color: "#66BB6A" },
//   { id: "MN", name: "Manipur", talent: 120, color: "#26C6DA" },
//   { id: "ML", name: "Meghalaya", talent: 90, color: "#42A5F5" },
//   { id: "MZ", name: "Mizoram", talent: 70, color: "#7E57C2" },
//   { id: "NL", name: "Nagaland", talent: 85, color: "#EC407A" },
//   { id: "OD", name: "Odisha", talent: 680, color: "#AB47BC" },
//   { id: "PB", name: "Punjab", talent: 1050, color: "#26A69A" },
//   { id: "RJ", name: "Rajasthan", talent: 1150, color: "#EF5350" },
//   { id: "SK", name: "Sikkim", talent: 65, color: "#FFA726" },
//   { id: "TN", name: "Tamil Nadu", talent: 2950, color: "#FFEE58" },
//   { id: "TG", name: "Telangana", talent: 2450, color: "#66BB6A" },
//   { id: "TR", name: "Tripura", talent: 110, color: "#26C6DA" },
//   { id: "UP", name: "Uttar Pradesh", talent: 2250, color: "#42A5F5" },
//   { id: "UK", name: "Uttarakhand", talent: 380, color: "#7E57C2" },
//   { id: "WB", name: "West Bengal", talent: 1750, color: "#EC407A" },
//   { id: "DL", name: "Delhi", talent: 1950, color: "#AB47BC" },
// ];

// // Mock data for talent by role
// const talentByRole = [
//   { role: "AI Engineer", count: 5200 },
//   { role: "ML Specialist", count: 4800 },
//   { role: "Data Scientist", count: 6500 },
//   { role: "NLP Engineer", count: 3200 },
//   { role: "Computer Vision Specialist", count: 2800 },
//   { role: "AI Research Scientist", count: 1900 },
// ];

// // Mock data for talent by certification
// const talentByCertification = [
//   { certification: "NAISG Certified", count: 12500 },
//   { certification: "Coursera - Machine Learning", count: 9800 },
//   { certification: "Coursera - Deep Learning", count: 7500 },
//   { certification: "NSDC AI Certification", count: 6200 },
//   { certification: "Microsoft AI Certification", count: 5400 },
//   { certification: "Google TensorFlow Certification", count: 4800 },
// ];

// function TalentHeatmap() {
//   const [tabValue, setTabValue] = useState(0);
//   const [roleFilter, setRoleFilter] = useState("all");
//   const [certificationFilter, setCertificationFilter] = useState("all");
//   const [selectedState, setSelectedState] = useState(null);
//   const [zoomLevel, setZoomLevel] = useState(1);
//   const [institutionData, setInstitutionData] = useState([]);

//   useEffect(() => {
//     if (tabValue === 1) {
//       apiClient
//         .get("/talent-heatmap-institute-wise/")
//         .then((response) => {
//           setInstitutionData(response.data);
//         })
//         .catch((error) => {
//           console.error("Error fetching institution data:", error);
//         });
//     }
//   }, [tabValue]);

//   const handleTabChange = (event, newValue) => {
//     setTabValue(newValue);
//   };

//   const handleRoleFilterChange = (event) => {
//     setRoleFilter(event.target.value);
//   };

//   const handleCertificationFilterChange = (event) => {
//     setCertificationFilter(event.target.value);
//   };

//   const handleStateClick = (state) => {
//     setSelectedState(state);
//   };

//   const handleZoomIn = () => {
//     setZoomLevel(Math.min(zoomLevel + 0.2, 2));
//   };

//   const handleZoomOut = () => {
//     setZoomLevel(Math.max(zoomLevel - 0.2, 0.6));
//   };

//   const handleResetZoom = () => {
//     setZoomLevel(1);
//     setSelectedState(null);
//   };

//   const getColorIntensity = (talent) => {
//     const maxTalent = Math.max(...indiaStates.map((state) => state.talent));
//     const intensity = Math.min(0.2 + (talent / maxTalent) * 0.8, 1);
//     return intensity;
//   };

//   return (
//     <Layout>
//       <Box sx={{ p: 3 }}>
//         <Box
//           sx={{
//             display: "flex",
//             justifyContent: "space-between",
//             alignItems: "center",
//             mb: 3,
//           }}
//         >
//           <Typography variant="h4" component="h1" gutterBottom>
//             Talent Availability Heatmap
//           </Typography>
//           <Box sx={{ display: "flex", gap: 2 }}>
//             <Button variant="outlined" startIcon={<FilterList />} size="small">
//               Advanced Filters
//             </Button>
//             <Button
//               variant="outlined"
//               startIcon={<CloudDownload />}
//               size="small"
//             >
//               Export Data
//             </Button>
//           </Box>
//         </Box>

//         <Paper sx={{ mb: 3 }}>
//           <Tabs
//             value={tabValue}
//             onChange={handleTabChange}
//             indicatorColor="primary"
//             textColor="primary"
//             centered
//           >
//             <Tab label="State-wise Distribution" />
//             <Tab label="Institution-wise Distribution" />
//             <Tab label="Role & Certification" />
//           </Tabs>
//         </Paper>

//         {tabValue === 0 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12} md={8}>
//               <Card>
//                 <CardContent>
//                   <Box
//                     sx={{
//                       display: "flex",
//                       justifyContent: "space-between",
//                       alignItems: "center",
//                       mb: 2,
//                     }}
//                   >
//                     <Typography variant="h6">
//                       India AI Talent Heatmap
//                     </Typography>
//                     <Box sx={{ display: "flex", gap: 1 }}>
//                       <Tooltip title="Zoom In">
//                         <IconButton size="small" onClick={handleZoomIn}>
//                           <ZoomIn />
//                         </IconButton>
//                       </Tooltip>
//                       <Tooltip title="Zoom Out">
//                         <IconButton size="small" onClick={handleZoomOut}>
//                           <ZoomOut />
//                         </IconButton>
//                       </Tooltip>
//                       <Tooltip title="Reset View">
//                         <IconButton size="small" onClick={handleResetZoom}>
//                           <Refresh />
//                         </IconButton>
//                       </Tooltip>
//                     </Box>
//                   </Box>

//                   <Box
//                     sx={{
//                       height: 500,
//                       bgcolor: "#f5f5f5",
//                       borderRadius: 1,
//                       position: "relative",
//                       overflow: "hidden",
//                       border: "1px solid #ddd",
//                     }}
//                   >
//                     <Box
//                       sx={{
//                         position: "absolute",
//                         top: "50%",
//                         left: "50%",
//                         transform: `translate(-50%, -50%) scale(${zoomLevel})`,
//                         width: "80%",
//                         height: "80%",
//                         transition: "transform 0.3s ease",
//                       }}
//                     >
//                       <Box
//                         component="img"
//                         src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c3/India_map_blank.svg/800px-India_map_blank.svg.png"
//                         sx={{
//                           width: "100%",
//                           height: "100%",
//                           objectFit: "contain",
//                           opacity: 0.7,
//                         }}
//                       />
//                       {indiaStates.map((state) => (
//                         <Tooltip
//                           key={state.id}
//                           title={`${state.name}: ${state.talent} AI Professionals`}
//                         >
//                           <Box
//                             sx={{
//                               position: "absolute",
//                               width: 20,
//                               height: 20,
//                               borderRadius: "50%",
//                               bgcolor: state.color,
//                               opacity: getColorIntensity(state.talent),
//                               border:
//                                 selectedState?.id === state.id
//                                   ? "2px solid #000"
//                                   : "none",
//                               cursor: "pointer",
//                               top: `${Math.random() * 70 + 10}%`,
//                               left: `${Math.random() * 70 + 10}%`,
//                               transform: "translate(-50%, -50%)",
//                               transition: "all 0.2s ease",
//                               "&:hover": {
//                                 transform: "translate(-50%, -50%) scale(1.2)",
//                                 boxShadow: "0 0 0 2px rgba(0,0,0,0.2)",
//                               },
//                             }}
//                             onClick={() => handleStateClick(state)}
//                           />
//                         </Tooltip>
//                       ))}
//                     </Box>

//                     <Box
//                       sx={{
//                         position: "absolute",
//                         bottom: 16,
//                         left: 16,
//                         bgcolor: "rgba(255,255,255,0.9)",
//                         p: 2,
//                         borderRadius: 1,
//                         minWidth: 200,
//                       }}
//                     >
//                       <Typography variant="subtitle2" gutterBottom>
//                         Legend
//                       </Typography>
//                       <Box
//                         sx={{ display: "flex", alignItems: "center", mb: 1 }}
//                       >
//                         <Box
//                           sx={{
//                             width: 16,
//                             height: 16,
//                             bgcolor: "#FF5252",
//                             opacity: 0.3,
//                             mr: 1,
//                             borderRadius: "50%",
//                           }}
//                         />
//                         <Typography variant="caption">Low (0-500)</Typography>
//                       </Box>
//                       <Box
//                         sx={{ display: "flex", alignItems: "center", mb: 1 }}
//                       >
//                         <Box
//                           sx={{
//                             width: 16,
//                             height: 16,
//                             bgcolor: "#FF5252",
//                             opacity: 0.6,
//                             mr: 1,
//                             borderRadius: "50%",
//                           }}
//                         />
//                         <Typography variant="caption">
//                           Medium (500-1500)
//                         </Typography>
//                       </Box>
//                       <Box sx={{ display: "flex", alignItems: "center" }}>
//                         <Box
//                           sx={{
//                             width: 16,
//                             height: 16,
//                             bgcolor: "#FF5252",
//                             opacity: 1,
//                             mr: 1,
//                             borderRadius: "50%",
//                           }}
//                         />
//                         <Typography variant="caption">High (1500+)</Typography>
//                       </Box>
//                     </Box>
//                   </Box>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={4}>
//               <Card sx={{ mb: 2 }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Filters
//                   </Typography>
//                   <FormControl fullWidth sx={{ mb: 2 }}>
//                     <InputLabel id="role-filter-label">
//                       Filter by Role
//                     </InputLabel>
//                     <Select
//                       labelId="role-filter-label"
//                       value={roleFilter}
//                       label="Filter by Role"
//                       onChange={handleRoleFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Roles</MenuItem>
//                       <MenuItem value="ai">AI Engineer</MenuItem>
//                       <MenuItem value="ml">ML Specialist</MenuItem>
//                       <MenuItem value="ds">Data Scientist</MenuItem>
//                       <MenuItem value="nlp">NLP Engineer</MenuItem>
//                       <MenuItem value="cv">Computer Vision</MenuItem>
//                     </Select>
//                   </FormControl>

//                   <FormControl fullWidth>
//                     <InputLabel id="cert-filter-label">
//                       Filter by Certification
//                     </InputLabel>
//                     <Select
//                       labelId="cert-filter-label"
//                       value={certificationFilter}
//                       label="Filter by Certification"
//                       onChange={handleCertificationFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Certifications</MenuItem>
//                       <MenuItem value="naisg">NAISG Certified</MenuItem>
//                       <MenuItem value="coursera">Coursera</MenuItem>
//                       <MenuItem value="nsdc">NSDC</MenuItem>
//                       <MenuItem value="microsoft">Microsoft</MenuItem>
//                       <MenuItem value="google">Google</MenuItem>
//                     </Select>
//                   </FormControl>
//                 </CardContent>
//               </Card>

//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Top States by Talent
//                   </Typography>
//                   <List dense>
//                     {indiaStates
//                       .sort((a, b) => b.talent - a.talent)
//                       .slice(0, 10)
//                       .map((state, index) => (
//                         <ListItem
//                           key={state.id}
//                           button
//                           onClick={() => handleStateClick(state)}
//                           selected={selectedState?.id === state.id}
//                         >
//                           <ListItemIcon>
//                             <Box
//                               sx={{
//                                 width: 16,
//                                 height: 16,
//                                 bgcolor: state.color,
//                                 opacity: getColorIntensity(state.talent),
//                                 borderRadius: "50%",
//                               }}
//                             />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={state.name}
//                             secondary={`${state.talent} professionals`}
//                           />
//                           <Chip
//                             label={`#${index + 1}`}
//                             size="small"
//                             color="primary"
//                             variant="outlined"
//                           />
//                         </ListItem>
//                       ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>

//             {selectedState && (
//               <Grid item xs={12}>
//                 <Card>
//                   <CardContent>
//                     <Typography variant="h6" gutterBottom>
//                       {selectedState.name} - Detailed Information
//                     </Typography>
//                     <Grid container spacing={2}>
//                       <Grid item xs={12} md={3}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h4" color="primary">
//                             {selectedState.talent}
//                           </Typography>
//                           <Typography variant="subtitle2">
//                             Total AI Professionals
//                           </Typography>
//                         </Paper>
//                       </Grid>
//                       <Grid item xs={12} md={3}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h4" color="secondary">
//                             {Math.floor(selectedState.talent * 0.65)}
//                           </Typography>
//                           <Typography variant="subtitle2">
//                             NAISG Certified
//                           </Typography>
//                         </Paper>
//                       </Grid>
//                       <Grid item xs={12} md={3}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h4" color="success.main">
//                             {Math.floor(selectedState.talent * 0.32)}
//                           </Typography>
//                           <Typography variant="subtitle2">
//                             International Ready
//                           </Typography>
//                         </Paper>
//                       </Grid>
//                       <Grid item xs={12} md={3}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h4" color="warning.main">
//                             {Math.floor(selectedState.talent * 0.18)}
//                           </Typography>
//                           <Typography variant="subtitle2">
//                             Currently Available
//                           </Typography>
//                         </Paper>
//                       </Grid>
//                     </Grid>
//                   </CardContent>
//                 </Card>
//               </Grid>
//             )}
//           </Grid>
//         )}

//         {tabValue === 1 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12}>
//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Top Institutions by AI Talent Output
//                   </Typography>
//                   <List>
//                     {institutionData.map((institution, index) => (
//                       <React.Fragment key={index}>
//                         <ListItem>
//                           <ListItemIcon>
//                             <Avatar sx={{ bgcolor: "primary.main" }}>
//                               <School />
//                             </Avatar>
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={institution.institution}
//                             secondary={`${institution.graduates} graduates`}
//                           />
//                           <Box
//                             sx={{
//                               display: "flex",
//                               alignItems: "center",
//                               gap: 1,
//                             }}
//                           >
//                             <Chip
//                               label={`Rank #${index + 1}`}
//                               color="primary"
//                               variant="outlined"
//                             />
//                             <Chip
//                               label={`${institution.graduates} graduates`}
//                               color="secondary"
//                             />
//                           </Box>
//                         </ListItem>
//                         {index < institutionData.length - 1 && (
//                           <Divider variant="inset" component="li" />
//                         )}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}

//         {tabValue === 2 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12} md={6}>
//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Talent Distribution by Role
//                   </Typography>
//                   <List>
//                     {talentByRole.map((role, index) => (
//                       <React.Fragment key={index}>
//                         <ListItem>
//                           <ListItemIcon>
//                             <Work />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={role.role}
//                             secondary={`${role.count} professionals`}
//                           />
//                           <Box
//                             sx={{
//                               width: 100,
//                               height: 8,
//                               bgcolor: "grey.300",
//                               borderRadius: 1,
//                               overflow: "hidden",
//                             }}
//                           >
//                             <Box
//                               sx={{
//                                 width: `${
//                                   (role.count /
//                                     Math.max(
//                                       ...talentByRole.map((r) => r.count)
//                                     )) *
//                                   100
//                                 }%`,
//                                 height: "100%",
//                                 bgcolor: "primary.main",
//                               }}
//                             />
//                           </Box>
//                         </ListItem>
//                         {index < talentByRole.length - 1 && (
//                           <Divider variant="inset" component="li" />
//                         )}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={6}>
//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Talent Distribution by Certification
//                   </Typography>
//                   <List>
//                     {talentByCertification.map((cert, index) => (
//                       <React.Fragment key={index}>
//                         <ListItem>
//                           <ListItemIcon>
//                             <School />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={cert.certification}
//                             secondary={`${cert.count} certified professionals`}
//                           />
//                           <Box
//                             sx={{
//                               width: 100,
//                               height: 8,
//                               bgcolor: "grey.300",
//                               borderRadius: 1,
//                               overflow: "hidden",
//                             }}
//                           >
//                             <Box
//                               sx={{
//                                 width: `${
//                                   (cert.count /
//                                     Math.max(
//                                       ...talentByCertification.map(
//                                         (c) => c.count
//                                       )
//                                     )) *
//                                   100
//                                 }%`,
//                                 height: "100%",
//                                 bgcolor: "secondary.main",
//                               }}
//                             />
//                           </Box>
//                         </ListItem>
//                         {index < talentByCertification.length - 1 && (
//                           <Divider variant="inset" component="li" />
//                         )}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12}>
//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     International Readiness Index
//                   </Typography>
//                   <Grid container spacing={2}>
//                     <Grid item xs={12} md={4}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="success.main">
//                           78%
//                         </Typography>
//                         <Typography variant="subtitle1">
//                           English Proficiency
//                         </Typography>
//                         <Typography variant="body2" color="textSecondary">
//                           Professionals with advanced English skills
//                         </Typography>
//                       </Paper>
//                     </Grid>

//                     <Grid item xs={12} md={4}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="primary.main">
//                           65%
//                         </Typography>
//                         <Typography variant="subtitle1">
//                           Passport Ready
//                         </Typography>
//                         <Typography variant="body2" color="textSecondary">
//                           Professionals with valid passports
//                         </Typography>
//                       </Paper>
//                     </Grid>

//                     <Grid item xs={12} md={4}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="warning.main">
//                           42%
//                         </Typography>
//                         <Typography variant="subtitle1">
//                           Relocation Willing
//                         </Typography>
//                         <Typography variant="body2" color="textSecondary">
//                           Professionals open to international relocation
//                         </Typography>
//                       </Paper>
//                     </Grid>
//                   </Grid>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}
//       </Box>
//     </Layout>
//   );
// }

// export default TalentHeatmap;
// // import { useState, useEffect, useCallback } from "react";
// // import apiClient from "../../context/apiClient";
// // import {
// //   Grid,
// //   Card,
// //   CardContent,
// //   Container,
// //   Typography,
// //   Button,
// //   Box,
// //   Table,
// //   TableBody,
// //   TableCell,
// //   TableContainer,
// //   TableHead,
// //   TableRow,
// //   Avatar,
// //   IconButton,
// //   Dialog,
// //   DialogTitle,
// //   DialogContent,
// //   DialogActions,
// //   TextField,
// //   FormControl,
// //   InputLabel,
// //   Select,
// //   MenuItem,
// //   Chip,
// //   CircularProgress,
// //   Stack,
// //   Paper,
// //   useTheme,
// //   useMediaQuery,
// // } from "@mui/material";
// // import {
// //   Add,
// //   Edit,
// //   Delete,
// //   AdminPanelSettings,
// //   Search,
// //   WorkOutline,
// //   PeopleAlt,
// //   ChatBubbleOutline,
// //   PendingActions,
// //   TaskAlt,
// //   Visibility,
// //   Phone,
// //   Email,
// // } from "@mui/icons-material";
// // import Layout from "../common/Layout";
// // import { useNotification } from "../../context/NotificationContext";

// // const UserManagement = () => {
// //   const { showNotification } = useNotification();
// //   const theme = useTheme();
// //   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

// //   // State
// //   const [users, setUsers] = useState([]);
// //   const [loading, setLoading] = useState(true);
// //   const [openDialog, setOpenDialog] = useState(false);
// //   const [deleteConfirm, setDeleteConfirm] = useState({
// //     open: false,
// //     user: null,
// //   });
// //   const [selectedUser, setSelectedUser] = useState(null);
// //   const [searchTerm, setSearchTerm] = useState("");
// //   const [newUser, setNewUser] = useState({
// //     username: "",
// //     email: "",
// //     password: "",
// //     user_role: "talent", // Default role
// //     phone_number: "",
// //   });

// //   // Utility Functions
// //   const getRoleColor = (role) => {
// //     switch (role) {
// //       case "talent":
// //         return "#4caf50";
// //       case "employer":
// //         return "#ff9800";
// //       case "admin":
// //         return "#f44336";
// //       default:
// //         return "#2196f3";
// //     }
// //   };

// //   const formatDate = (isoString) => {
// //     if (!isoString) return "N/A";
// //     const date = new Date(isoString);
// //     return date.toLocaleDateString("en-GB"); // Format as DD/MM/YYYY
// //   };

// //   // API Calls
// //   const fetchUsers = useCallback(async () => {
// //     setLoading(true);
// //     try {
// //       const response = await apiClient.get("/admin/users/");
// //       const transformedUsers = response.data.map((user) => ({
// //         id: user.id,
// //         name: user.username,
// //         email: user.email,
// //         phone_number: user.phone_number || "N/A",
// //         role: user.user_role.toLowerCase(),
// //         status: user.is_active ? "active" : "pending",
// //         joinDate: formatDate(user.date_joined),
// //         lastLogin: formatDate(user.last_login),
// //       }));
// //       setUsers(transformedUsers);
// //     } catch (error) {
// //       console.error("Failed to fetch users:", error);
// //       const errorMessage =
// //         error.response?.data?.detail ||
// //         "An error occurred while fetching users.";
// //       showNotification(errorMessage, "error");
// //     } finally {
// //       setLoading(false);
// //     }
// //   }, [showNotification]);

// //   useEffect(() => {
// //     fetchUsers();
// //   }, [fetchUsers]);

// //   // Event Handlers
// //   const handleAddUser = () => {
// //     setSelectedUser(null);
// //     setNewUser({
// //       username: "",
// //       email: "",
// //       password: "",
// //       user_role: "talent",
// //       phone_number: "",
// //     });
// //     setOpenDialog(true);
// //   };

// //   const handleEditUser = (user) => {
// //     setSelectedUser(user);
// //     setNewUser({
// //       username: user.name,
// //       email: user.email,
// //       user_role: user.role,
// //       phone_number: user.phone_number,
// //       password: "", // Don't pre-fill password
// //     });
// //     setOpenDialog(true);
// //   };

// //   const handleDeleteClick = (user) => {
// //     setDeleteConfirm({ open: true, user });
// //   };

// //   const handleCloseDeleteConfirm = () => {
// //     setDeleteConfirm({ open: false, user: null });
// //   };

// //   const handleDeleteConfirm = async () => {
// //     if (!deleteConfirm.user) return;
// //     try {
// //       await apiClient.delete(`/admin/users/${deleteConfirm.user.id}/`);
// //       showNotification(
// //         `User "${deleteConfirm.user.name}" deleted successfully!`,
// //         "success"
// //       );
// //       handleCloseDeleteConfirm();
// //       fetchUsers(); // Refresh user list
// //     } catch (error) {
// //       console.error("Failed to delete user:", error);
// //       const errorMessage =
// //         error.response?.data?.detail || "Failed to delete user.";
// //       showNotification(errorMessage, "error");
// //     }
// //   };

// //   const handleSaveUser = async () => {
// //     try {
// //       if (selectedUser) {
// //         // Update existing user
// //         const payload = {
// //           username: newUser.username,
// //           email: newUser.email,
// //           user_role: newUser.user_role.toUpperCase(),
// //           phone_number: newUser.phone_number,
// //         };
// //         await apiClient.patch(`/admin/users/${selectedUser.id}/`, payload);
// //         showNotification("User updated successfully!", "success");
// //       } else {
// //         // Create new user
// //         if (!newUser.password) {
// //           showNotification("Password is required for new users.", "warning");
// //           return;
// //         }
// //         const payload = {
// //           username: newUser.username,
// //           email: newUser.email,
// //           password: newUser.password,
// //           user_role: newUser.user_role.toUpperCase(),
// //           phone_number: newUser.phone_number,
// //         };
// //         await apiClient.post("/admin/users/", payload);
// //         showNotification("User created successfully!", "success");
// //       }
// //       setOpenDialog(false);
// //       fetchUsers(); // Refresh user list
// //     } catch (error) {
// //       console.error("Failed to save user:", error);
// //       // Handle nested error messages from Django Rest Framework
// //       const errors = error.response?.data;
// //       let errorMessage = "An error occurred while saving the user.";
// //       if (typeof errors === "object" && errors !== null) {
// //         errorMessage = Object.entries(errors)
// //           .map(
// //             ([key, value]) =>
// //               `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
// //           )
// //           .join(" | ");
// //       } else if (errors) {
// //         errorMessage = errors.toString();
// //       }
// //       showNotification(errorMessage, "error");
// //     }
// //   };

// //   // Derived State
// //   const filteredUsers = users.filter(
// //     (user) =>
// //       user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
// //       user.email.toLowerCase().includes(searchTerm.toLowerCase())
// //   );

// //   const userStats = {
// //     total: users.length,
// //     talent: users.filter((u) => u.role === "talent").length,
// //     employers: users.filter((u) => u.role === "employer").length,
// //     admins: users.filter((u) => u.role === "admin").length,
// //     active: users.filter((u) => u.status === "active").length,
// //     pending: users.filter((u) => u.status === "pending").length,
// //   };

// //   const statCards = [
// //     {
// //       title: "Total Users",
// //       value: userStats.total,
// //       icon: <WorkOutline />,
// //       bgColor: theme.palette.grey[100],
// //       color: theme.palette.grey[600],
// //     },
// //     {
// //       title: "AI Talents",
// //       value: userStats.talent,
// //       icon: <PeopleAlt />,
// //       bgColor: "rgba(59, 130, 246, 0.1)",
// //       color: "#3B82F6",
// //     },
// //     {
// //       title: "Employers",
// //       value: userStats.employers,
// //       icon: <ChatBubbleOutline />,
// //       bgColor: "rgba(239, 68, 68, 0.1)",
// //       color: "#EF4444",
// //     },
// //     {
// //       title: "Admins",
// //       value: userStats.admins,
// //       icon: <AdminPanelSettings />,
// //       bgColor: theme.palette.grey[100],
// //       color: theme.palette.grey[600],
// //     },
// //     {
// //       title: "Active",
// //       value: userStats.active,
// //       icon: <TaskAlt />,
// //       bgColor: "rgba(16, 185, 129, 0.1)",
// //       color: "#10B981",
// //     },
// //     {
// //       title: "Pending",
// //       value: userStats.pending,
// //       icon: <PendingActions />,
// //       bgColor: "rgba(245, 158, 11, 0.1)",
// //       color: "#F59E0B",
// //     },
// //   ];

// //   // Render
// //   return (
// //     <Layout title="Admin Dashboard">
// //       <Container
// //         maxWidth="xl"
// //         sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}
// //       >
// //         <Box
// //           sx={{
// //             display: "flex",
// //             flexDirection: { xs: "column", md: "row" },
// //             justifyContent: "space-between",
// //             alignItems: { xs: "flex-start", md: "center" },
// //             gap: 2,
// //             mb: 3,
// //           }}
// //         >
// //           <Box>
// //             <Typography
// //               variant="h4"
// //               sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}
// //             >
// //               User Management
// //             </Typography>
// //             <Typography variant="body2" color="text.secondary">
// //               Manage all users across the GATEP Platform
// //             </Typography>
// //           </Box>
// //           <Button
// //             variant="contained"
// //             startIcon={<Add />}
// //             onClick={handleAddUser}
// //             sx={{
// //               borderRadius: "12px",
// //               textTransform: "none",
// //               fontWeight: 600,
// //               px: 3,
// //               py: 1.5,
// //               width: { xs: "100%", md: "auto" },
// //               boxShadow: "none",
// //             }}
// //           >
// //             Add New User
// //           </Button>
// //         </Box>

// //         <Paper
// //           elevation={0}
// //           sx={{
// //             p: 2,
// //             mb: 3,
// //             borderRadius: "16px",
// //             border: "1px solid",
// //             borderColor: "divider",
// //           }}
// //         >
// //           <Stack
// //             direction={{ xs: "column", md: "row" }}
// //             spacing={2}
// //             alignItems="center"
// //           >
// //             <TextField
// //               fullWidth
// //               placeholder="Search by name or email..."
// //               value={searchTerm}
// //               onChange={(e) => setSearchTerm(e.target.value)}
// //               InputProps={{
// //                 startAdornment: (
// //                   <Search sx={{ color: "text.disabled", mr: 1 }} />
// //                 ),
// //                 sx: { borderRadius: "12px" },
// //               }}
// //             />
// //           </Stack>
// //         </Paper>

// //         <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
// //           {statCards.map((stat) => (
// //             <Grid item xs={6} sm={4} md={2} key={stat.title}>
// //               <Card
// //                 elevation={0}
// //                 sx={{
// //                   borderRadius: "16px",
// //                   height: "100%",
// //                   border: "1px solid",
// //                   borderColor: "divider",
// //                 }}
// //               >
// //                 <CardContent sx={{ textAlign: "center", p: { xs: 2, md: 3 } }}>
// //                   <Box
// //                     sx={{
// //                       width: { xs: 48, md: 64 },
// //                       height: { xs: 48, md: 64 },
// //                       borderRadius: "12px",
// //                       bgcolor: stat.bgColor,
// //                       color: stat.color,
// //                       display: "flex",
// //                       alignItems: "center",
// //                       justifyContent: "center",
// //                       mx: "auto",
// //                       mb: 2,
// //                       "& .MuiSvgIcon-root": { fontSize: { xs: 32, md: 40 } },
// //                     }}
// //                   >
// //                     {stat.icon}
// //                   </Box>
// //                   <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
// //                     {stat.value}
// //                   </Typography>
// //                   <Typography variant="body2" color="text.secondary">
// //                     {stat.title}
// //                   </Typography>
// //                 </CardContent>
// //               </Card>
// //             </Grid>
// //           ))}
// //         </Grid>

// //         <Paper
// //           elevation={0}
// //           sx={{
// //             borderRadius: "16px",
// //             overflow: "hidden",
// //             border: "1px solid",
// //             borderColor: "divider",
// //           }}
// //         >
// //           {loading ? (
// //             <Box sx={{ display: "flex", justifyContent: "center", p: 10 }}>
// //               <CircularProgress />
// //             </Box>
// //           ) : (
// //             <TableContainer>
// //               <Table sx={{ minWidth: 800 }}>
// //                 <TableHead sx={{ bgcolor: "action.hover" }}>
// //                   <TableRow>
// //                     {[
// //                       "User",
// //                       "Role",
// //                       "Status",
// //                       "Join Date",
// //                       "Last Login",
// //                       "Actions",
// //                     ].map((headCell) => (
// //                       <TableCell
// //                         key={headCell}
// //                         sx={{
// //                           fontWeight: 600,
// //                           color: "text.secondary",
// //                           borderBottomColor: "divider",
// //                         }}
// //                       >
// //                         {headCell}
// //                       </TableCell>
// //                     ))}
// //                   </TableRow>
// //                 </TableHead>
// //                 <TableBody>
// //                   {filteredUsers.map((user) => (
// //                     <TableRow
// //                       key={user.id}
// //                       sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
// //                     >
// //                       <TableCell sx={{ borderBottomColor: "divider" }}>
// //                         <Box
// //                           sx={{ display: "flex", alignItems: "center", gap: 2 }}
// //                         >
// //                           <Avatar
// //                             sx={{
// //                               width: 40,
// //                               height: 40,
// //                               bgcolor: getRoleColor(user.role),
// //                               color: "white",
// //                             }}
// //                           >
// //                             {user.name.charAt(0).toUpperCase()}
// //                           </Avatar>
// //                           <Box>
// //                             <Typography variant="body2" fontWeight={600}>
// //                               {user.name}
// //                             </Typography>
// //                             <Stack
// //                               direction="row"
// //                               spacing={0.5}
// //                               alignItems="center"
// //                               mt={0.5}
// //                               sx={{ color: "text.secondary" }}
// //                             >
// //                               <Email sx={{ fontSize: 14 }} />
// //                               <Typography variant="caption">
// //                                 {user.email}
// //                               </Typography>
// //                             </Stack>
// //                             <Stack
// //                               direction="row"
// //                               spacing={0.5}
// //                               alignItems="center"
// //                               mt={0.5}
// //                               sx={{ color: "text.secondary" }}
// //                             >
// //                               <Phone sx={{ fontSize: 14 }} />
// //                               <Typography variant="caption">
// //                                 {user.phone_number}
// //                               </Typography>
// //                             </Stack>
// //                           </Box>
// //                         </Box>
// //                       </TableCell>
// //                       <TableCell sx={{ borderBottomColor: "divider" }}>
// //                         <Chip
// //                           label={user.role}
// //                           sx={{
// //                             bgcolor: `${getRoleColor(user.role)}20`,
// //                             color: getRoleColor(user.role),
// //                             fontWeight: 600,
// //                             borderRadius: "6px",
// //                             textTransform: "capitalize",
// //                           }}
// //                         />
// //                       </TableCell>
// //                       <TableCell sx={{ borderBottomColor: "divider" }}>
// //                         <Chip
// //                           label={user.status}
// //                           sx={{
// //                             bgcolor:
// //                               user.status === "active"
// //                                 ? "#10B98120"
// //                                 : "#F59E0B20",
// //                             color:
// //                               user.status === "active" ? "#10B981" : "#F59E0B",
// //                             fontWeight: 600,
// //                             borderRadius: "6px",
// //                             textTransform: "capitalize",
// //                           }}
// //                         />
// //                       </TableCell>
// //                       <TableCell
// //                         sx={{
// //                           color: "text.secondary",
// //                           borderBottomColor: "divider",
// //                         }}
// //                       >
// //                         {user.joinDate}
// //                       </TableCell>
// //                       <TableCell
// //                         sx={{
// //                           color: "text.secondary",
// //                           borderBottomColor: "divider",
// //                         }}
// //                       >
// //                         {user.lastLogin}
// //                       </TableCell>
// //                       <TableCell sx={{ borderBottomColor: "divider" }}>
// //                         <Stack direction="row" spacing={0}>
// //                           <IconButton
// //                             size="small"
// //                             onClick={() => handleEditUser(user)}
// //                           >
// //                             <Edit fontSize="small" />
// //                           </IconButton>
// //                           <IconButton size="small">
// //                             <Visibility fontSize="small" />
// //                           </IconButton>
// //                           <IconButton
// //                             size="small"
// //                             onClick={() => handleDeleteClick(user)}
// //                           >
// //                             <Delete fontSize="small" />
// //                           </IconButton>
// //                         </Stack>
// //                       </TableCell>
// //                     </TableRow>
// //                   ))}
// //                 </TableBody>
// //               </Table>
// //             </TableContainer>
// //           )}
// //         </Paper>

// //         {/* Add/Edit User Dialog */}
// //         <Dialog
// //           open={openDialog}
// //           onClose={() => setOpenDialog(false)}
// //           maxWidth="sm"
// //           fullWidth
// //           fullScreen={isMobile}
// //         >
// //           <DialogTitle>
// //             {selectedUser ? "Edit User" : "Add New User"}
// //           </DialogTitle>
// //           <DialogContent>
// //             <Grid container spacing={2} sx={{ mt: 1 }}>
// //               <Grid item xs={12}>
// //                 <TextField
// //                   fullWidth
// //                   label="Username"
// //                   value={newUser.username}
// //                   onChange={(e) =>
// //                     setNewUser((p) => ({ ...p, username: e.target.value }))
// //                   }
// //                 />
// //               </Grid>
// //               <Grid item xs={12}>
// //                 <TextField
// //                   fullWidth
// //                   label="Email Address"
// //                   type="email"
// //                   value={newUser.email}
// //                   onChange={(e) =>
// //                     setNewUser((p) => ({ ...p, email: e.target.value }))
// //                   }
// //                 />
// //               </Grid>
// //               <Grid item xs={12}>
// //                 <TextField
// //                   fullWidth
// //                   label="Phone Number"
// //                   value={newUser.phone_number}
// //                   onChange={(e) =>
// //                     setNewUser((p) => ({ ...p, phone_number: e.target.value }))
// //                   }
// //                 />
// //               </Grid>
// //               {!selectedUser && (
// //                 <Grid item xs={12}>
// //                   <TextField
// //                     fullWidth
// //                     label="Password"
// //                     type="password"
// //                     helperText="Required for new users"
// //                     value={newUser.password}
// //                     onChange={(e) =>
// //                       setNewUser((p) => ({ ...p, password: e.target.value }))
// //                     }
// //                   />
// //                 </Grid>
// //               )}
// //               <Grid item xs={12}>
// //                 <FormControl fullWidth>
// //                   <InputLabel>Role</InputLabel>
// //                   <Select
// //                     value={newUser.user_role}
// //                     onChange={(e) =>
// //                       setNewUser((p) => ({ ...p, user_role: e.target.value }))
// //                     }
// //                     label="Role"
// //                   >
// //                     <MenuItem value="talent">Talent</MenuItem>
// //                     <MenuItem value="employer">Employer</MenuItem>
// //                     <MenuItem value="admin">Admin</MenuItem>
// //                   </Select>
// //                 </FormControl>
// //               </Grid>
// //             </Grid>
// //           </DialogContent>
// //           <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
// //             <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
// //             <Button variant="contained" onClick={handleSaveUser}>
// //               {selectedUser ? "Update User" : "Create User"}
// //             </Button>
// //           </DialogActions>
// //         </Dialog>

// //         {/* Delete Confirmation Dialog */}
// //         <Dialog
// //           open={deleteConfirm.open}
// //           onClose={handleCloseDeleteConfirm}
// //           maxWidth="xs"
// //           fullWidth
// //         >
// //           <DialogTitle>Confirm Deletion</DialogTitle>
// //           <DialogContent>
// //             <Typography>
// //               Are you sure you want to delete the user "
// //               {deleteConfirm.user?.name}"? This action cannot be undone.
// //             </Typography>
// //           </DialogContent>
// //           <DialogActions>
// //             <Button onClick={handleCloseDeleteConfirm}>Cancel</Button>
// //             <Button
// //               variant="contained"
// //               color="error"
// //               onClick={handleDeleteConfirm}
// //             >
// //               Delete
// //             </Button>
// //           </DialogActions>
// //         </Dialog>
// //       </Container>
// //     </Layout>
// //   );
// // };

// // export default UserManagement;

// import React, { useState } from "react";
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Paper,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Button,
//   Tabs,
//   Tab,
//   Chip,
//   List,
//   ListItem,
//   ListItemText,
//   ListItemIcon,
//   Divider,
//   Avatar,
//   IconButton,
//   Tooltip,
// } from "@mui/material";
// import {
//   School,
//   Work,
//   FilterList,
//   CloudDownload,
//   ZoomIn,
//   ZoomOut,
//   Refresh,
// } from "@mui/icons-material";
// import Layout from "../common/Layout";

// // Mock data for India states
// const indiaStates = [
//   { id: "AP", name: "Andhra Pradesh", talent: 1250, color: "#FF5252" },
//   { id: "AR", name: "Arunachal Pradesh", talent: 180, color: "#FFA726" },
//   { id: "AS", name: "Assam", talent: 450, color: "#FFEE58" },
//   { id: "BR", name: "Bihar", talent: 780, color: "#66BB6A" },
//   { id: "CG", name: "Chhattisgarh", talent: 320, color: "#26C6DA" },
//   { id: "GA", name: "Goa", talent: 150, color: "#42A5F5" },
//   { id: "GJ", name: "Gujarat", talent: 1450, color: "#7E57C2" },
//   { id: "HR", name: "Haryana", talent: 980, color: "#EC407A" },
//   { id: "HP", name: "Himachal Pradesh", talent: 280, color: "#AB47BC" },
//   { id: "JH", name: "Jharkhand", talent: 420, color: "#26A69A" },
//   { id: "KA", name: "Karnataka", talent: 3200, color: "#EF5350" },
//   { id: "KL", name: "Kerala", talent: 1850, color: "#FFA726" },
//   { id: "MP", name: "Madhya Pradesh", talent: 950, color: "#FFEE58" },
//   { id: "MH", name: "Maharashtra", talent: 3800, color: "#66BB6A" },
//   { id: "MN", name: "Manipur", talent: 120, color: "#26C6DA" },
//   { id: "ML", name: "Meghalaya", talent: 90, color: "#42A5F5" },
//   { id: "MZ", name: "Mizoram", talent: 70, color: "#7E57C2" },
//   { id: "NL", name: "Nagaland", talent: 85, color: "#EC407A" },
//   { id: "OD", name: "Odisha", talent: 680, color: "#AB47BC" },
//   { id: "PB", name: "Punjab", talent: 1050, color: "#26A69A" },
//   { id: "RJ", name: "Rajasthan", talent: 1150, color: "#EF5350" },
//   { id: "SK", name: "Sikkim", talent: 65, color: "#FFA726" },
//   { id: "TN", name: "Tamil Nadu", talent: 2950, color: "#FFEE58" },
//   { id: "TG", name: "Telangana", talent: 2450, color: "#66BB6A" },
//   { id: "TR", name: "Tripura", talent: 110, color: "#26C6DA" },
//   { id: "UP", name: "Uttar Pradesh", talent: 2250, color: "#42A5F5" },
//   { id: "UK", name: "Uttarakhand", talent: 380, color: "#7E57C2" },
//   { id: "WB", name: "West Bengal", talent: 1750, color: "#EC407A" },
//   { id: "DL", name: "Delhi", talent: 1950, color: "#AB47BC" },
// ];

// // Mock data for top institutions
// const topInstitutions = [
//   {
//     id: 1,
//     name: "Indian Institute of Technology, Bombay",
//     talent: 450,
//     state: "Maharashtra",
//   },
//   {
//     id: 2,
//     name: "Indian Institute of Technology, Delhi",
//     talent: 420,
//     state: "Delhi",
//   },
//   {
//     id: 3,
//     name: "Indian Institute of Science, Bangalore",
//     talent: 380,
//     state: "Karnataka",
//   },
//   {
//     id: 4,
//     name: "Indian Institute of Technology, Madras",
//     talent: 350,
//     state: "Tamil Nadu",
//   },
//   {
//     id: 5,
//     name: "International Institute of Information Technology, Hyderabad",
//     talent: 320,
//     state: "Telangana",
//   },
//   {
//     id: 6,
//     name: "Birla Institute of Technology and Science, Pilani",
//     talent: 290,
//     state: "Rajasthan",
//   },
//   {
//     id: 7,
//     name: "National Institute of Technology, Tiruchirappalli",
//     talent: 260,
//     state: "Tamil Nadu",
//   },
//   {
//     id: 8,
//     name: "Indian Institute of Technology, Kanpur",
//     talent: 240,
//     state: "Uttar Pradesh",
//   },
//   {
//     id: 9,
//     name: "Indian Institute of Technology, Kharagpur",
//     talent: 230,
//     state: "West Bengal",
//   },
//   {
//     id: 10,
//     name: "Indian Statistical Institute, Kolkata",
//     talent: 210,
//     state: "West Bengal",
//   },
// ];

// // Mock data for talent by role
// const talentByRole = [
//   { role: "AI Engineer", count: 5200 },
//   { role: "ML Specialist", count: 4800 },
//   { role: "Data Scientist", count: 6500 },
//   { role: "NLP Engineer", count: 3200 },
//   { role: "Computer Vision Specialist", count: 2800 },
//   { role: "AI Research Scientist", count: 1900 },
// ];

// // Mock data for talent by certification
// const talentByCertification = [
//   { certification: "NAISG Certified", count: 12500 },
//   { certification: "Coursera - Machine Learning", count: 9800 },
//   { certification: "Coursera - Deep Learning", count: 7500 },
//   { certification: "NSDC AI Certification", count: 6200 },
//   { certification: "Microsoft AI Certification", count: 5400 },
//   { certification: "Google TensorFlow Certification", count: 4800 },
// ];

// function TalentHeatmap() {
//   const [tabValue, setTabValue] = useState(0);
//   const [roleFilter, setRoleFilter] = useState("all");
//   const [certificationFilter, setCertificationFilter] = useState("all");
//   const [selectedState, setSelectedState] = useState(null);
//   const [zoomLevel, setZoomLevel] = useState(1);

//   const handleTabChange = (event, newValue) => {
//     setTabValue(newValue);
//   };

//   const handleRoleFilterChange = (event) => {
//     setRoleFilter(event.target.value);
//   };

//   const handleCertificationFilterChange = (event) => {
//     setCertificationFilter(event.target.value);
//   };

//   const handleStateClick = (state) => {
//     setSelectedState(state);
//   };

//   const handleZoomIn = () => {
//     setZoomLevel(Math.min(zoomLevel + 0.2, 2));
//   };

//   const handleZoomOut = () => {
//     setZoomLevel(Math.max(zoomLevel - 0.2, 0.6));
//   };

//   const handleResetZoom = () => {
//     setZoomLevel(1);
//     setSelectedState(null);
//   };

//   // Get color intensity based on talent count
//   const getColorIntensity = (talent) => {
//     const maxTalent = Math.max(...indiaStates.map((state) => state.talent));
//     const intensity = Math.min(0.2 + (talent / maxTalent) * 0.8, 1);
//     return intensity;
//   };

//   return (
//     <Layout>
//       <Box sx={{ p: 3 }}>
//         <Box
//           sx={{
//             display: "flex",
//             justifyContent: "space-between",
//             alignItems: "center",
//             mb: 3,
//           }}
//         >
//           <Typography variant="h4" component="h1" gutterBottom>
//             Talent Availability Heatmap
//           </Typography>
//           <Box sx={{ display: "flex", gap: 2 }}>
//             <Button variant="outlined" startIcon={<FilterList />} size="small">
//               Advanced Filters
//             </Button>
//             <Button
//               variant="outlined"
//               startIcon={<CloudDownload />}
//               size="small"
//             >
//               Export Data
//             </Button>
//           </Box>
//         </Box>

//         <Paper sx={{ mb: 3 }}>
//           <Tabs
//             value={tabValue}
//             onChange={handleTabChange}
//             indicatorColor="primary"
//             textColor="primary"
//             centered
//           >
//             <Tab label="State-wise Distribution" />
//             <Tab label="Institution-wise Distribution" />
//             <Tab label="Role & Certification" />
//           </Tabs>
//         </Paper>

//         {/* State-wise Distribution Tab */}
//         {tabValue === 0 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12} md={8}>
//               <Card>
//                 <CardContent>
//                   <Box
//                     sx={{
//                       display: "flex",
//                       justifyContent: "space-between",
//                       alignItems: "center",
//                       mb: 2,
//                     }}
//                   >
//                     <Typography variant="h6">
//                       India AI Talent Heatmap
//                     </Typography>
//                     <Box sx={{ display: "flex", gap: 1 }}>
//                       <Tooltip title="Zoom In">
//                         <IconButton size="small" onClick={handleZoomIn}>
//                           <ZoomIn />
//                         </IconButton>
//                       </Tooltip>
//                       <Tooltip title="Zoom Out">
//                         <IconButton size="small" onClick={handleZoomOut}>
//                           <ZoomOut />
//                         </IconButton>
//                       </Tooltip>
//                       <Tooltip title="Reset View">
//                         <IconButton size="small" onClick={handleResetZoom}>
//                           <Refresh />
//                         </IconButton>
//                       </Tooltip>
//                     </Box>
//                   </Box>

//                   <Box
//                     sx={{
//                       height: 500,
//                       bgcolor: "#f5f5f5",
//                       borderRadius: 1,
//                       position: "relative",
//                       overflow: "hidden",
//                       border: "1px solid #ddd",
//                     }}
//                   >
//                     {/* This is a placeholder for the actual map visualization */}
//                     <Box
//                       sx={{
//                         position: "absolute",
//                         top: "50%",
//                         left: "50%",
//                         transform: `translate(-50%, -50%) scale(${zoomLevel})`,
//                         width: "80%",
//                         height: "80%",
//                         transition: "transform 0.3s ease",
//                       }}
//                     >
//                       <Box
//                         component="img"
//                         src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c3/India_map_blank.svg/800px-India_map_blank.svg.png"
//                         sx={{
//                           width: "100%",
//                           height: "100%",
//                           objectFit: "contain",
//                           opacity: 0.7,
//                         }}
//                       />

//                       {/* Simulate state markers */}
//                       {indiaStates.map((state) => (
//                         <Tooltip
//                           key={state.id}
//                           title={`${state.name}: ${state.talent} AI Professionals`}
//                         >
//                           <Box
//                             sx={{
//                               position: "absolute",
//                               width: 20,
//                               height: 20,
//                               borderRadius: "50%",
//                               bgcolor: state.color,
//                               opacity: getColorIntensity(state.talent),
//                               border:
//                                 selectedState?.id === state.id
//                                   ? "2px solid #000"
//                                   : "none",
//                               cursor: "pointer",
//                               // Random positions for demonstration
//                               top: `${Math.random() * 70 + 10}%`,
//                               left: `${Math.random() * 70 + 10}%`,
//                               transform: "translate(-50%, -50%)",
//                               transition: "all 0.2s ease",
//                               "&:hover": {
//                                 transform: "translate(-50%, -50%) scale(1.2)",
//                                 boxShadow: "0 0 0 2px rgba(0,0,0,0.2)",
//                               },
//                             }}
//                             onClick={() => handleStateClick(state)}
//                           />
//                         </Tooltip>
//                       ))}
//                     </Box>

//                     <Box
//                       sx={{
//                         position: "absolute",
//                         bottom: 16,
//                         left: 16,
//                         bgcolor: "rgba(255,255,255,0.9)",
//                         p: 2,
//                         borderRadius: 1,
//                         minWidth: 200,
//                       }}
//                     >
//                       <Typography variant="subtitle2" gutterBottom>
//                         Legend
//                       </Typography>
//                       <Box
//                         sx={{ display: "flex", alignItems: "center", mb: 1 }}
//                       >
//                         <Box
//                           sx={{
//                             width: 16,
//                             height: 16,
//                             bgcolor: "#FF5252",
//                             opacity: 0.3,
//                             mr: 1,
//                             borderRadius: "50%",
//                           }}
//                         />
//                         <Typography variant="caption">Low (0-500)</Typography>
//                       </Box>
//                       <Box
//                         sx={{ display: "flex", alignItems: "center", mb: 1 }}
//                       >
//                         <Box
//                           sx={{
//                             width: 16,
//                             height: 16,
//                             bgcolor: "#FF5252",
//                             opacity: 0.6,
//                             mr: 1,
//                             borderRadius: "50%",
//                           }}
//                         />
//                         <Typography variant="caption">
//                           Medium (500-1500)
//                         </Typography>
//                       </Box>
//                       <Box sx={{ display: "flex", alignItems: "center" }}>
//                         <Box
//                           sx={{
//                             width: 16,
//                             height: 16,
//                             bgcolor: "#FF5252",
//                             opacity: 1,
//                             mr: 1,
//                             borderRadius: "50%",
//                           }}
//                         />
//                         <Typography variant="caption">High (1500+)</Typography>
//                       </Box>
//                     </Box>
//                   </Box>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={4}>
//               <Card sx={{ mb: 2 }}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Filters
//                   </Typography>
//                   <FormControl fullWidth sx={{ mb: 2 }}>
//                     <InputLabel id="role-filter-label">
//                       Filter by Role
//                     </InputLabel>
//                     <Select
//                       labelId="role-filter-label"
//                       value={roleFilter}
//                       label="Filter by Role"
//                       onChange={handleRoleFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Roles</MenuItem>
//                       <MenuItem value="ai">AI Engineer</MenuItem>
//                       <MenuItem value="ml">ML Specialist</MenuItem>
//                       <MenuItem value="ds">Data Scientist</MenuItem>
//                       <MenuItem value="nlp">NLP Engineer</MenuItem>
//                       <MenuItem value="cv">Computer Vision</MenuItem>
//                     </Select>
//                   </FormControl>

//                   <FormControl fullWidth>
//                     <InputLabel id="cert-filter-label">
//                       Filter by Certification
//                     </InputLabel>
//                     <Select
//                       labelId="cert-filter-label"
//                       value={certificationFilter}
//                       label="Filter by Certification"
//                       onChange={handleCertificationFilterChange}
//                       size="small"
//                     >
//                       <MenuItem value="all">All Certifications</MenuItem>
//                       <MenuItem value="naisg">NAISG Certified</MenuItem>
//                       <MenuItem value="coursera">Coursera</MenuItem>
//                       <MenuItem value="nsdc">NSDC</MenuItem>
//                       <MenuItem value="microsoft">Microsoft</MenuItem>
//                       <MenuItem value="google">Google</MenuItem>
//                     </Select>
//                   </FormControl>
//                 </CardContent>
//               </Card>

//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Top States by Talent
//                   </Typography>
//                   <List dense>
//                     {indiaStates
//                       .sort((a, b) => b.talent - a.talent)
//                       .slice(0, 10)
//                       .map((state, index) => (
//                         <ListItem
//                           key={state.id}
//                           button
//                           onClick={() => handleStateClick(state)}
//                           selected={selectedState?.id === state.id}
//                         >
//                           <ListItemIcon>
//                             <Box
//                               sx={{
//                                 width: 16,
//                                 height: 16,
//                                 bgcolor: state.color,
//                                 opacity: getColorIntensity(state.talent),
//                                 borderRadius: "50%",
//                               }}
//                             />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={state.name}
//                             secondary={`${state.talent} professionals`}
//                           />
//                           <Chip
//                             label={`#${index + 1}`}
//                             size="small"
//                             color="primary"
//                             variant="outlined"
//                           />
//                         </ListItem>
//                       ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>

//             {selectedState && (
//               <Grid item xs={12}>
//                 <Card>
//                   <CardContent>
//                     <Typography variant="h6" gutterBottom>
//                       {selectedState.name} - Detailed Information
//                     </Typography>
//                     <Grid container spacing={2}>
//                       <Grid item xs={12} md={3}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h4" color="primary">
//                             {selectedState.talent}
//                           </Typography>
//                           <Typography variant="subtitle2">
//                             Total AI Professionals
//                           </Typography>
//                         </Paper>
//                       </Grid>
//                       <Grid item xs={12} md={3}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h4" color="secondary">
//                             {Math.floor(selectedState.talent * 0.65)}
//                           </Typography>
//                           <Typography variant="subtitle2">
//                             NAISG Certified
//                           </Typography>
//                         </Paper>
//                       </Grid>
//                       <Grid item xs={12} md={3}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h4" color="success.main">
//                             {Math.floor(selectedState.talent * 0.32)}
//                           </Typography>
//                           <Typography variant="subtitle2">
//                             International Ready
//                           </Typography>
//                         </Paper>
//                       </Grid>
//                       <Grid item xs={12} md={3}>
//                         <Paper sx={{ p: 2, textAlign: "center" }}>
//                           <Typography variant="h4" color="warning.main">
//                             {Math.floor(selectedState.talent * 0.18)}
//                           </Typography>
//                           <Typography variant="subtitle2">
//                             Currently Available
//                           </Typography>
//                         </Paper>
//                       </Grid>
//                     </Grid>
//                   </CardContent>
//                 </Card>
//               </Grid>
//             )}
//           </Grid>
//         )}

//         {/* Institution-wise Distribution Tab */}
//         {tabValue === 1 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12}>
//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Top Institutions by AI Talent Output
//                   </Typography>
//                   <List>
//                     {topInstitutions.map((institution, index) => (
//                       <React.Fragment key={institution.id}>
//                         <ListItem>
//                           <ListItemIcon>
//                             <Avatar sx={{ bgcolor: "primary.main" }}>
//                               <School />
//                             </Avatar>
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={institution.name}
//                             secondary={`${institution.state} • ${institution.talent} AI professionals`}
//                           />
//                           <Box
//                             sx={{
//                               display: "flex",
//                               alignItems: "center",
//                               gap: 1,
//                             }}
//                           >
//                             <Chip
//                               label={`Rank #${index + 1}`}
//                               color="primary"
//                               variant="outlined"
//                             />
//                             <Chip
//                               label={`${institution.talent} graduates`}
//                               color="secondary"
//                             />
//                           </Box>
//                         </ListItem>
//                         {index < topInstitutions.length - 1 && (
//                           <Divider variant="inset" component="li" />
//                         )}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}

//         {/* Role & Certification Tab */}
//         {tabValue === 2 && (
//           <Grid container spacing={3}>
//             <Grid item xs={12} md={6}>
//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Talent Distribution by Role
//                   </Typography>
//                   <List>
//                     {talentByRole.map((role, index) => (
//                       <React.Fragment key={index}>
//                         <ListItem>
//                           <ListItemIcon>
//                             <Work />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={role.role}
//                             secondary={`${role.count} professionals`}
//                           />
//                           <Box
//                             sx={{
//                               width: 100,
//                               height: 8,
//                               bgcolor: "grey.300",
//                               borderRadius: 1,
//                               overflow: "hidden",
//                             }}
//                           >
//                             <Box
//                               sx={{
//                                 width: `${
//                                   (role.count /
//                                     Math.max(
//                                       ...talentByRole.map((r) => r.count)
//                                     )) *
//                                   100
//                                 }%`,
//                                 height: "100%",
//                                 bgcolor: "primary.main",
//                               }}
//                             />
//                           </Box>
//                         </ListItem>
//                         {index < talentByRole.length - 1 && (
//                           <Divider variant="inset" component="li" />
//                         )}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={6}>
//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     Talent Distribution by Certification
//                   </Typography>
//                   <List>
//                     {talentByCertification.map((cert, index) => (
//                       <React.Fragment key={index}>
//                         <ListItem>
//                           <ListItemIcon>
//                             <School />
//                           </ListItemIcon>
//                           <ListItemText
//                             primary={cert.certification}
//                             secondary={`${cert.count} certified professionals`}
//                           />
//                           <Box
//                             sx={{
//                               width: 100,
//                               height: 8,
//                               bgcolor: "grey.300",
//                               borderRadius: 1,
//                               overflow: "hidden",
//                             }}
//                           >
//                             <Box
//                               sx={{
//                                 width: `${
//                                   (cert.count /
//                                     Math.max(
//                                       ...talentByCertification.map(
//                                         (c) => c.count
//                                       )
//                                     )) *
//                                   100
//                                 }%`,
//                                 height: "100%",
//                                 bgcolor: "secondary.main",
//                               }}
//                             />
//                           </Box>
//                         </ListItem>
//                         {index < talentByCertification.length - 1 && (
//                           <Divider variant="inset" component="li" />
//                         )}
//                       </React.Fragment>
//                     ))}
//                   </List>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12}>
//               <Card>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom>
//                     International Readiness Index
//                   </Typography>
//                   <Grid container spacing={2}>
//                     <Grid item xs={12} md={4}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="success.main">
//                           78%
//                         </Typography>
//                         <Typography variant="subtitle1">
//                           English Proficiency
//                         </Typography>
//                         <Typography variant="body2" color="textSecondary">
//                           Professionals with advanced English skills
//                         </Typography>
//                       </Paper>
//                     </Grid>

//                     <Grid item xs={12} md={4}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="primary.main">
//                           65%
//                         </Typography>
//                         <Typography variant="subtitle1">
//                           Passport Ready
//                         </Typography>
//                         <Typography variant="body2" color="textSecondary">
//                           Professionals with valid passports
//                         </Typography>
//                       </Paper>
//                     </Grid>

//                     <Grid item xs={12} md={4}>
//                       <Paper sx={{ p: 2, textAlign: "center" }}>
//                         <Typography variant="h3" color="warning.main">
//                           42%
//                         </Typography>
//                         <Typography variant="subtitle1">
//                           Relocation Willing
//                         </Typography>
//                         <Typography variant="body2" color="textSecondary">
//                           Professionals open to international relocation
//                         </Typography>
//                       </Paper>
//                     </Grid>
//                   </Grid>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         )}
//       </Box>
//     </Layout>
//   );
// }

// export default TalentHeatmap;
